/**
 * Editor canvas: React Flow surface that renders concepts as nodes and
 * relations as edges, both derived from the WorkspaceStore.
 *
 * Drop a type from the palette → addConceptOnCanvas (optimistic + API call).
 * Drag a node → moveConceptOnCanvas (debounced patchDiagram view_state).
 * Connect two handles → connectConcepts (matrix-validated server-side).
 *
 * Default relation type for connect is `archimate.Serving`. Future iteration
 * (M2.4) will add a small picker for relation type when source+target are
 * compatible with multiple matrix-allowed relations.
 */
import type { BilingualText, Concept, Relation, ViewElementState } from "@ae/domain";
import {
  formatMultiplicityLabel,
  MULTIPLICITY_SOURCE_KEY,
  MULTIPLICITY_TARGET_KEY,
  SHARP_NESTING_RELATION,
  sharpNestVerdict,
} from "@ae/domain";
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  ConnectionMode,
  Controls,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type OnConnect,
  type OnReconnect,
  type OnSelectionChangeFunc,
  ReactFlow,
  type ReactFlowInstance,
  reconnectEdge,
  useReactFlow,
  useUpdateNodeInternals,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  alwaysAllowedRelationTypeIds,
  autoApplyStereotypeFor,
  getRegistry,
  isElementTypeAllowedOnViewKind,
  resolveBilingual,
  resolveConceptName,
  statusIndicatorFor,
  subStereotypesForBase,
  validRelationTypesBetween,
} from "../../registry";
import { CANVAS_CURSOR_CELL_SIZE, useKeymap } from "../../state/keymap";
import { useSession } from "../../state/session";
import { type GroupEncloseItem, useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { colorForType } from "../layerColors";
import { readDroppedTypeName } from "../palette/dragTypeName";
import { handleIdToAngle, nearestBorderAngle } from "./angleUtils";
import { CanvasContextMenu, type CanvasMenuStand } from "./CanvasContextMenu";
import { CanvasGridLayout } from "./CanvasGridLayout";
import { CanvasToolbar } from "./CanvasToolbar";
import { ChangeArchimateTypeDialog } from "./ChangeArchimateTypeDialog";
import { ConceptContextMenu } from "./ConceptContextMenu";
import { ConceptNode } from "./ConceptNode";
import { CURSOR_CELL_NODE_ID, CursorCellNode } from "./CursorCellNode";
import { getC4Stereotype, isC4ViewKind } from "./c4Stereotype";
import { isSameConceptClone } from "./conceptIdentity";
import { EdgeMarkers } from "./EdgeMarkers";
import { applyRelationStyle } from "./edgeStyles";
import {
  edgeLabelFocusId,
  type Focusable,
  focusablesInCell,
  parseEdgeLabelFocusId,
  snapToCell,
} from "./focusNavigation";
import { GapDiffCanvas } from "./GapDiffCanvas";
import { GlossaryCanvas } from "./GlossaryCanvas";
import { HeatMapCanvasV2 } from "./HeatMapCanvasV2";
import { ListBoxNode } from "./ListBoxNode";
import { OverlayRulesPanel } from "./OverlayRulesPanel";
import { readOverlayRules } from "./overlayConfig";
import { makeOverlayEvaluator } from "./overlayEval";
import { PlateauDiffCanvas } from "./PlateauDiffCanvas";
import { passendeFont } from "./passendeFont";
import { RelationContextMenu } from "./RelationContextMenu";
import { RelationHeatMapCanvas } from "./RelationHeatMapCanvas";
import { RibbonPortal } from "./RibbonSlot";
import { RoadmapCanvas } from "./RoadmapCanvas";
import { RoadmapMatrixCanvas } from "./RoadmapMatrixCanvas";
import { RoutedEdge } from "./RoutedEdge";
import { findDuplicateRelations, isSameRelationIdentity } from "./relationIdentity";
import { relationsRepresentedByOldNesting } from "./renestRelations";
import { SimplifyPanel } from "./SimplifyPanel";
import { SubStereotypeChoiceModal } from "./SubStereotypeChoiceModal";
import { SuperNode } from "./SuperNode";
import { SwimlaneCanvas } from "./SwimlaneCanvas";

/**
 * Order of preference when picking a relation type for a fresh connection:
 * try a meaningful default first, then fall back so a line is always created.
 * The user can change the type via the attribute panel afterwards.
 */
const RELATION_TYPE_PREFERENCE = [
  "archimate.Serving",
  "archimate.Access",
  "archimate.Realization",
  "archimate.Triggering",
  "archimate.Flow",
  "archimate.Composition",
  "archimate.Aggregation",
  "archimate.Assignment",
  "archimate.Specialization",
  "archimate.Association",
];

const nodeTypes = {
  concept: ConceptNode,
  listbox: ListBoxNode,
  "cursor-cell": CursorCellNode,
  supernode: SuperNode,
};
const edgeTypes = { routed: RoutedEdge };

/**
 * Past de view-kind's `auto_apply_stereotype`-regel toe op een concept
 * dat zojuist op de view geplaatst is. Patcht het concept via
 * `patchConcept` wanneer (a) de view-kind een regel heeft, (b) het
 * concept's typeId in `applies_to` staat én (c) het stereotype nog
 * niet op het concept zit. Idempotent en silent — UI-flow wordt niet
 * geblokkeerd als de patch faalt.
 */
async function maybeApplyViewKindStereotype(
  workspaceId: string,
  branchId: string,
  concept: { id: string; typeId: string; stereotypes?: string[] },
  viewKindId: string,
): Promise<void> {
  const stereotypeId = autoApplyStereotypeFor(viewKindId, concept.typeId);
  if (!stereotypeId) return;
  const current = concept.stereotypes ?? [];
  if (current.includes(stereotypeId)) return;
  try {
    await useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
      stereotypes: [...current, stereotypeId],
    });
  } catch (err) {
    console.warn("[auto-apply-stereotype] patch failed", concept.id, err);
  }
}

/**
 * Resolve een React-Flow edge naar z'n onderliggende domain-relation-id.
 *
 * Multi-instance-refactor (2026-05-26): edge.id is per-instance (UUID voor
 * multi-placements, relation-id voor canonical). De domain-relation-id
 * leeft in `edge.data.relationId`. Voor canonical/legacy entries zijn die
 * twee gelijk; voor multi-instance entries niet.
 *
 * Gebruik in elke handler die `store.getRelation`, `selectRelation`, of
 * vergelijkbare entity-lookups doet. Handlers die per-edge state lezen
 * (waypoints, hidden, label-offsets) blijven `edge.id` gebruiken want
 * dat is de viewState-entry-key.
 */
function edgeRelationId(edge: { id: string; data?: unknown }): string {
  const data = edge.data as { relationId?: unknown } | undefined;
  if (data && typeof data.relationId === "string") return data.relationId;
  return edge.id;
}

// Module-vlag: is de Canvas al eens gemount in deze page-lifetime? Bij een hard
// refresh herlaadt de JS → vlag = false → eerste mount toont METEEN (geen blank
// bij page-load). Een latere remount via key={branchId} (branch-wissel) heeft
// de vlag op true → dán pas verbergen-tot-fit (anti-flikker). Survives remounts,
// reset bij page-reload.
let canvasEverMounted = false;

/**
 * Viewport (pan + zoom) per diagram, persisted in localStorage. Zonder dit
 * valt elke (re)mount terug op de `fitView`-prop, die bij een cold page-load
 * soms fit op een NOG NIET volledig gehydrateerde node-set (hydrate-guard
 * hierboven, bug A in M4-acceptance.md) — dat geeft een te ver ingezoomde
 * viewport die daarna nooit meer gecorrigeerd wordt (fitView draait alleen
 * bij mount). Door de laatst-bekende viewport te onthouden en die bij mount
 * terug te zetten, is de gebruiker niet afhankelijk van fitView's timing.
 */
function viewportStorageKey(workspaceId: string, diagramId: string): string {
  return `ae.viewport:${workspaceId}:${diagramId}`;
}
function readPersistedViewport(workspaceId: string, diagramId: string): Viewport | null {
  try {
    const raw = window.localStorage.getItem(viewportStorageKey(workspaceId, diagramId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Viewport>;
    if (
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      typeof parsed.zoom === "number"
    ) {
      return { x: parsed.x, y: parsed.y, zoom: parsed.zoom };
    }
  } catch {
    // Corrupte of ontoegankelijke storage — negeren, fitView blijft de fallback.
  }
  return null;
}
function writePersistedViewport(workspaceId: string, diagramId: string, viewport: Viewport): void {
  try {
    window.localStorage.setItem(
      viewportStorageKey(workspaceId, diagramId),
      JSON.stringify(viewport),
    );
  } catch {
    // Storage vol/ontoegankelijk — niet kritiek, viewport wordt dan niet onthouden.
  }
}

/**
 * Forceert React-Flow om de node-internals (handle-posities) te her-meten.
 * Moet een DESCENDANT van <ReactFlow> zijn zodat de hooks werken (in Canvas
 * zelf werken ze niet).
 *
 * Twee triggers:
 * 1. `nonce` bumpt na een user-undo/redo — een store-gedreven positie-revert
 *    her-tekent de edge-aanhechting anders pas bij de volgende render
 *    (endpoint-lag).
 * 2. `nodeIdsKey` (sorted-joined node-ids) verandert wanneer de NODE-SET
 *    wijzigt (diagram-switch, vereenvoudigde-kopie, paste, nieuw concept op
 *    canvas). Root cause van de "edges verdwijnen soms uit de render, data
 *    blijft correct, hersteld pas willekeurig later"-bug (gediagnosticeerd
 *    2026-07-10/11): xyflow's `getEdgePosition` (`@xyflow/system`) geeft
 *    `null` terug — en dus rendert `EdgeWrapper` de edge helemaal niet —
 *    zodra `isNodeInitialized(node)` false is, wat NIET hetzelfde is als
 *    "node bestaat in de lookup" (bewezen via instrumentatie: source/target
 *    zaten al correct in xyflow's node-lookup terwijl de edge toch niet
 *    rendert). `isNodeInitialized` eist ook `node.internals.handleBounds`
 *    (of `node.handles`) — die wordt pas gevuld nadat de `<Handle>`-DOM-
 *    elementen gemount zijn EN xyflow's eigen ResizeObserver ze gemeten
 *    heeft. Bij een net toegevoegde/vervangen node kan een edge er al naar
 *    wijzen VOORDAT die meting heeft plaatsgevonden, en niets in xyflow
 *    triggert daarna vanzelf een retry voor precies dat node/edge-paar — het
 *    blijft potentieel permanent `null` tot iets anders toevallig
 *    `updateNodeInternals` aanroept (bv. deze undo/redo-tak, of een
 *    toevallige her-render die de handle-bounds wél goed meet). Door dit
 *    proactief te doen zodra de node-id-set wijzigt, sluiten we het race-
 *    window: de `<Handle>`-elementen zijn dan al gemount (effect draait na
 *    DOM-commit), dus `updateNodeInternals` meet ze meteen correct.
 *
 * (Eerdere, VERWORPEN pogingen — `rf.setEdges(edges)` sync/rAF/setInterval
 * om xyflow's edges-store te "forceren" te syncen — losten niets op: bewezen
 * dat xyflow's edges-array + node-lookup + edge.hidden-vlag altijd al
 * correct waren op het moment van falen. Het probleem zat dus niet in de
 * edges-sync maar in de hierboven beschreven handle-measurement-race.)
 */
/** Sleutels voor de niveau-namen in de melding; de index ís het niveau. */
const NIVEAU_SLEUTEL = ["sharp.levelArea", "sharp.levelBusiness", "sharp.levelSub"] as const;

/**
 * Nesten tussen Sharp's procesniveaus: leg zwijgend een aggregatie aan.
 *
 * Tussen procesgebied (0), bedrijfsproces (1) en subproces (2) is het antwoord
 * op "welk relatietype?" altijd hetzelfde, dus de vraag is daar alleen
 * wrijving. Geen compositie: een subproces bestaat op zichzelf en kan in meer
 * dan één proces voorkomen. Zie `sharpProcessLevels.ts`.
 *
 * Retourneert `true` wanneer 'ie de nesting heeft afgehandeld — dan hoort de
 * aanroeper z'n eigen relatietype-vraag over te slaan.
 *
 * **Twee aanroepers, en dat is de valkuil**: nesten kan via een sleep op het
 * canvas én via een drop uit het palet in een container. De eerste versie zat
 * alleen in het sleep-pad, waardoor een vers uit het palet gesleept subproces
 * alsnog de vraag kreeg.
 */
function sharpNiveauNesting(
  workspaceId: string,
  branchId: string,
  childConcept: Concept,
  parentConcept: Concept,
  childConceptId: string,
  parentConceptId: string,
  t: ReturnType<typeof useTranslation>["t"],
): boolean {
  const oordeel = sharpNestVerdict(childConcept.stereotypes, parentConcept.stereotypes);
  if (oordeel.soort === "niet_van_toepassing") return false;
  void (async () => {
    const relation = await useWorkspaceData
      .getState()
      .connectConcepts(
        workspaceId,
        branchId,
        childConceptId,
        parentConceptId,
        SHARP_NESTING_RELATION,
      );
    if (relation) {
      useWorkspaceData.getState().hideRelationOnDiagram(workspaceId, branchId, relation.id);
    }
    // Omhoog nesten — een procesgebied binnen een subproces — is een
    // niveaufout. We weigeren 'm niet (de taal staat de relatie toe, en de
    // matrix is de harde grens, niet de methode) maar we zeggen het wel,
    // anders staat er een omkering in het model die niemand opmerkt.
    if (oordeel.soort === "aggregeer_met_waarschuwing") {
      useWorkspaceData.setState({
        lastError: t("sharp.nestLevelInverted", {
          child: t(NIVEAU_SLEUTEL[oordeel.kindNiveau] ?? "sharp.levelSub"),
          parent: t(NIVEAU_SLEUTEL[oordeel.ouderNiveau] ?? "sharp.levelSub"),
        }),
      });
    }
  })();
  return true;
}

function EdgeGeometryRefresher({ nodeIdsKey }: { nodeIdsKey: string }) {
  const updateNodeInternals = useUpdateNodeInternals();
  const rf = useReactFlow();
  const nonce = useWorkspaceData((s) => s.canvasGeometryNonce);
  useEffect(() => {
    if (nonce === 0) return;
    for (const n of rf.getNodes()) updateNodeInternals(n.id);
  }, [nonce, updateNodeInternals, rf]);
  useEffect(() => {
    if (!nodeIdsKey) return;
    for (const n of rf.getNodes()) updateNodeInternals(n.id);
  }, [nodeIdsKey, updateNodeInternals, rf]);
  return null;
}

export function Canvas() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Individual selectors — never return new object literals from a Zustand
  // selector or React's external-store hook ends up in an infinite loop.
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken (leesrecht op het workpackage): selecteren, pannen en het
  // attributenpaneel openen mag; slepen, verbinden, neerzetten en de
  // contextmenu's niet.
  const readOnly = useSession((s) => s.readOnly);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const lastError = useWorkspaceData((s) => s.lastError);
  // Edges are not mirrored in local state, so we drive their `selected` flag
  // from the UI store. This also lets React Flow's Delete-key handler see the
  // currently-selected edge as part of its internal selection set.
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectedInstanceId = useUi((s) => s.selectedInstanceId);
  // Multi-select-sets uit external panels (bv. GridPane Shift/Cmd-klik).
  // Canvas spiegelt deze naar `node.selected`/`edge.selected` zodat
  // alle gemarkeerde objecten visueel oplichten, niet alleen de
  // single-select-anchor.
  const selectedConceptIds = useUi((s) => s.selectedConceptIds);
  // Multi-instance-refactor (2026-05-26): entry-keys voor visuele
  // edge-highlight, los van selectedRelationIds (relation-ids voor
  // AttributePanel/Toolbar bulk-actions, niet in deze component gelezen).
  const selectedEdgeIds = useUi((s) => s.selectedEdgeIds);
  // "Spring naar"-requests vanuit GridPane ("Toon op canvas"-context-
  // menu). Veranderende nonce triggert het focus-useEffect verderop dat
  // ReactFlow centreert.
  const viewportFocusRequest = useUi((s) => s.viewportFocusRequest);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Detect "store nog niet gehydrateerd" door te checken of er überhaupt
  // concepts in de store zitten. Tijdens initial page-load arriveert de
  // diagram (met een gevulde viewState) soms vóór de concepts. Dan zou
  // de pre-pass elke entry als orphan zien (`store.getConcept(...)` is
  // null), `directTotalCounts` blijft leeg, `hasChildren` voor containers
  // wordt false, en de chevron + auto-nest-relaties verdwijnen tot een
  // drag-event de re-eval triggert. Met deze flag skippen we de pre-pass
  // volledig totdat er concepts in de store zitten — de useMemo retriggert
  // automatisch via `revision` zodra hydrate verder gaat. Bug A in
  // M4-acceptance.md.
  const conceptsCount = useWorkspaceData((s) => s.store.listConcepts().length);

  // Derive the canonical node + edge view from the store. Recomputed when the
  // store revision bumps, the active diagram changes, or the UI language flips.
  const derived = useMemo(() => {
    void revision; // included to retrigger memo when the store mutates
    void conceptsCount; // retrigger memo wanneer concepts arriveren tijdens hydrate
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    const diagram = activeDiagramId ? store.getDiagram(activeDiagramId) : null;
    const viewState = diagram?.viewState ?? {};
    // Deterministische kleur/dim-overlay (conditional formatting). De regelset
    // leeft op de diagram-viewState; de evaluator is een no-op zolang de
    // overlay uit staat, dus geen overhead op normale diagrammen.
    // Visuele parent→child-edges uit de viewState-nesting (containerId) —
    // vult de overlay-child-detectie aan waar model-`parent_id` ontbreekt
    // (bv. geïmporteerde Attribuut/Identifier-rijen).
    const overlayChildEdges: [string, string][] = [];
    for (const [k, v] of Object.entries(viewState)) {
      if (!v.containerId) continue;
      const parentVe = viewState[v.containerId];
      if (!parentVe) continue;
      overlayChildEdges.push([parentVe.ref ?? v.containerId, v.ref ?? k]);
    }
    const overlayEvaluate = makeOverlayEvaluator(
      readOverlayRules(viewState),
      store.listConcepts(),
      store.listRelations(),
      overlayChildEdges,
    );
    // M8 §5: op een viewpoint met een `label_resolver` (Sharp/C4/BMC)
    // tonen concepten hun per-view alt-label (`altLabels[resolver]`) i.p.v.
    // de gewone naam — mits gezet. Anders vallen we terug op de reguliere
    // naam-resolutie (die o.a. de Sharp-glossary-afleiding meepakt).
    const viewResolver = diagram?.viewKind
      ? reg.getViewKind(diagram.viewKind)?.label_resolver
      : undefined;
    const conceptDisplayLabel = (c: Concept): string => {
      // Het tekenobject "Diagram-beschrijving" is een titelblok: het toont de
      // beschrijving van het diagram waar 'ie op ligt, en **valt terug op de
      // naam** zodra die beschrijving leeg is. Bij élke render opnieuw
      // gelezen, dus het volgt wat je aan het diagram verandert.
      //
      // Tot 2026-09-04 werd de beschrijving één keer bij het neerzetten
      // overgeschreven naar de naam van het object. Stond er op dat moment
      // nog geen beschrijving — het gewone geval, want je zet 't vakje neer
      // vóór je 'm schrijft — dan bleef 't vakje voorgoed leeg, en een later
      // ingevulde beschrijving kwam er nooit meer in. Vandaar de levende
      // koppeling; de terugval op de naam zorgt dat 't vakje nooit leeg is,
      // want een diagram heeft altijd een naam en lang niet altijd een
      // beschrijving.
      if (c.typeId === "drawing.DiagramDescriptionBox") {
        const kies = (t: BilingualText | undefined) => (t?.[lang] ?? t?.nl ?? t?.en ?? "").trim();
        const tekst = kies(diagram?.description) || kies(diagram?.name);
        if (tekst.length > 0) return tekst;
      }
      if (viewResolver) {
        // Per-taal: alleen als het alt-label in de getoonde taal gevuld is;
        // anders de gewone naam (leeg alt-veld → gewone naam, niet "(zonder naam)").
        const alt = c.altLabels[viewResolver]?.[lang];
        if (alt && alt.trim().length > 0) return alt;
      }
      return resolveConceptName(c, (id) => store.getConcept(id), lang);
    };
    // Hydrate-guard (bug A): wanneer er een diagram met viewState-entries
    // is maar er nog geen concepts in de store zitten, draaien we te
    // vroeg — alle entries zouden ten onrechte als orphan worden
    // weggefilterd. Skip de hele derived-berekening totdat de eerste
    // concepts geladen zijn; de memo retriggert vanzelf zodra dat
    // gebeurt (via `revision` of `conceptsCount`).
    const hasViewStateEntries = Object.keys(viewState).length > 0;
    const storeIsHydrated = conceptsCount > 0;
    if (hasViewStateEntries && !storeIsHydrated) {
      return {
        nodes: [] as Node[],
        edges: [] as Edge[],
        instanceToConcept: new Map<string, string>(),
        hiddenTotalOnDiagram: 0,
      };
    }

    // Each viewState entry with a position (and not hidden) is one node on
    // the canvas. The entry's KEY is the node id — it might equal the
    // underlying concept id (canonical placement) or be a fresh UUID for
    // an additional instance, in which case `entry.conceptRef` points at
    // the concept. Iterating viewState entries (rather than concepts)
    // means a single concept can render multiple times on the same diagram.
    const computedNodes: Node[] = [];
    // Map node-instance-id → underlying concept id, used by edges below
    // (and by selection, drag handlers) to bridge the two id spaces.
    const instanceToConcept = new Map<string, string>();
    // First instance per concept id, for default edge endpoint resolution.
    const firstInstancePerConcept = new Map<string, string>();
    // Pre-pass: bepaal welke containers `collapsedAsContainer:true` zijn,
    // en hoeveel directe + transitive descendants ze hebben. De
    // collapse-state wordt nu via z-stacking afgehandeld — de container
    // schildert bovenop zijn descendants (en de binnen-segmenten van
    // edges) i.p.v. dat de descendants `hidden:true` krijgen. Voordeel:
    // edges blijven semantisch verbonden met descendants, geen re-anchor;
    // browser regelt visuele clipping puur via paint-order.
    //
    // - directTotalCounts: hoeveel directe kinderen heeft elke container?
    //   Bepaalt `hasChildren` (chevron-zichtbaarheid).
    // - transitiveDescendantCounts: hoeveel descendants (op elk niveau)
    //   liggen er onder elke container? Voor een collapsed container is
    //   dit de badge-count linksboven (= "verbergt zoveel achter zich").
    // - hiddenTotalOnDiagram: aantal concept-instances dat momenteel
    //   visueel obscured wordt door minstens één collapsed ancestor.
    //   Voedt de canvas-rechtsboven totaal-teller.
    const directTotalCounts = new Map<string, number>();
    const transitiveDescendantCounts = new Map<string, number>();
    // viewState-keys waarvan de `containerId` als stale wordt gedetecteerd
    // (kind groter dan parent → inverted nesting, mag geen child-rendering
    // krijgen). Wordt verderop gelezen door de node-build sectie.
    const invertedNestKeys = new Set<string>();
    let hiddenTotalOnDiagram = 0;
    for (const [key, ve] of Object.entries(viewState)) {
      // Skip orphan viewState-entries (concept hard-weg vóór purgeItems-
      // viewState-cleanup beschikbaar was, of relations zonder eigen
      // concept). Tellen we ze mee dan zien containers een
      // hiddenChildCount voor objecten die niet bestaan, en triggert de
      // chevron op ghost-children. Concept-instances hebben een position;
      // relations niet — dus de position-check filtert beide gevallen
      // (geen position OF concept hard-weg).
      if (ve.position === undefined) continue;
      const veConceptId = ve.ref ?? key;
      if (!store.getConcept(veConceptId)) continue;
      void key;

      if (ve.containerId) {
        // Self-healing: detecteer stale containerId waarbij het kind
        // visueel grotere bounds heeft dan z'n claimed parent. Dat kan
        // ontstaan zijn door een eerdere drag-misstap waarbij de
        // (oude) nest-detector alleen op center-containment checkte.
        // Een groter object hoort nooit child van een kleiner object
        // te zijn — we negeren de relatie hier zodat 't kind niet
        // meetelt in de parent's child-count en de ghost-chevron
        // verdwijnt. We markeren ook de entry in `invertedNestKeys`
        // zodat de node-build downstream `parentId` overslaat en de
        // position absoluut behandelt (anders krijg je een onverwacht
        // verplaatst kind doordat de stale `position` relatief was
        // tov de oude "parent").
        const parentVe = viewState[ve.containerId];
        const childArea = (ve.width ?? 140) * (ve.height ?? 60);
        const parentArea = (parentVe?.width ?? 140) * (parentVe?.height ?? 60);
        if (parentVe && childArea > parentArea) {
          invertedNestKeys.add(key);
          continue;
        }
        directTotalCounts.set(ve.containerId, (directTotalCounts.get(ve.containerId) ?? 0) + 1);
        // Walk omhoog langs containerId-chain en increment de
        // transitive-counter voor élke voorouder. Tegelijk detecteren of
        // er ergens in de chain een collapsed container zit — zo ja, deze
        // descendant is visueel obscured.
        let cur: string | undefined = ve.containerId;
        const seen = new Set<string>();
        let obscuredByCollapse = false;
        while (cur !== undefined && !seen.has(cur)) {
          seen.add(cur);
          transitiveDescendantCounts.set(cur, (transitiveDescendantCounts.get(cur) ?? 0) + 1);
          if (viewState[cur]?.collapsedAsContainer === true) {
            obscuredByCollapse = true;
          }
          cur = viewState[cur]?.containerId;
        }
        if (obscuredByCollapse) hiddenTotalOnDiagram++;
      }
    }
    // Sharp Detailed Concept compartment-render — pre-pass.
    // Op een sharp.detailed-concept view tonen we sharp.Attribute en
    // sharp.Identifier als compacte regels in de parent-Concept-box i.p.v.
    // als losse vakjes. We verzamelen hier per parent-instance de child-
    // entries (gesorteerd op sort_order) zodat ConceptNode ze kan
    // renderen in een compartment.
    const isDetailedConceptView = diagram?.viewKind === "sharp.detailed-concept";
    interface CompartmentChild {
      instanceId: string;
      conceptId: string;
      typeId: "sharp.Attribute" | "sharp.Identifier";
      label: string;
      isPrimary: boolean;
      sortOrder: number;
    }
    const compartmentChildrenByParent = new Map<string, CompartmentChild[]>();
    const childrenInCompartment = new Set<string>();
    if (isDetailedConceptView) {
      for (const [k, v] of Object.entries(viewState)) {
        if (v.position === undefined) continue;
        if (v.hidden === true) continue;
        if (!v.containerId) continue;
        const parentVe = viewState[v.containerId];
        if (!parentVe || parentVe.position === undefined || parentVe.hidden === true) continue;
        const childConceptId = v.ref ?? k;
        const child = store.getConcept(childConceptId);
        if (!child || child.tombstoneInBranch) continue;
        if (child.typeId !== "sharp.Attribute" && child.typeId !== "sharp.Identifier") continue;
        const parentConceptId = parentVe.ref ?? v.containerId;
        const parent = store.getConcept(parentConceptId);
        // Subtype-bewust: ook subtypes van BusinessObject hosten Sharp-
        // Attribuut/Identifier-compartimenten.
        if (!parent || !reg.effectiveTypeIdsFor(parent.typeId).has("archimate.BusinessObject"))
          continue;
        const sortOrder =
          typeof child.attributes.sort_order === "number"
            ? child.attributes.sort_order
            : Number.POSITIVE_INFINITY;
        const arr = compartmentChildrenByParent.get(v.containerId) ?? [];
        arr.push({
          instanceId: k,
          conceptId: childConceptId,
          typeId: child.typeId,
          label: conceptDisplayLabel(child),
          isPrimary: child.typeId === "sharp.Identifier" && child.attributes.is_primary === true,
          sortOrder,
        });
        compartmentChildrenByParent.set(v.containerId, arr);
        childrenInCompartment.add(k);
      }
      // Sorteer per parent: identifiers eerst (op sort_order, dan primary
      // eerst binnen identifiers), daarna attributen (op sort_order).
      // Items zonder sort_order (Number.POSITIVE_INFINITY) komen achteraan
      // op naam.
      for (const [, arr] of compartmentChildrenByParent) {
        arr.sort((a, b) => {
          // Type: identifiers eerst.
          if (a.typeId !== b.typeId) {
            return a.typeId === "sharp.Identifier" ? -1 : 1;
          }
          // Sort_order asc.
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
          // Tie-break op naam.
          return a.label.localeCompare(b.label);
        });
      }
    }

    // Set van instance-ids die door minstens één andere node als
    // containerId gerefereerd worden. Wordt gebruikt om aan ConceptNode
    // door te geven dat dit een visuele container is — die rendert dan
    // de naam midden-boven i.p.v. centraal.
    const containerInstanceIds = new Set<string>();
    for (const ve2 of Object.values(viewState)) {
      if (ve2.containerId) containerInstanceIds.add(ve2.containerId);
    }

    for (const [key, ve] of Object.entries(viewState)) {
      if (ve.position === undefined) continue;
      if (ve.hidden === true) continue;
      // Synthetische super-node (Fase 2 vereenvoudiging) — geen onderliggend
      // concept, dus VÓÓR de getConcept-guard afhandelen. Rendert als eigen
      // node-type; members zijn hidden + containerId→deze entry zodat hun
      // edges via het bestaande re-anchor-mechanisme op dit blok landen.
      if (ve.type === "supernode") {
        computedNodes.push({
          id: key,
          type: "supernode",
          position: ve.position,
          data: {
            layer: ve.supernode?.layer,
            label: ve.supernode?.label,
            typeId: ve.supernode?.typeId,
            memberCount: ve.supernode?.memberConceptIds.length ?? 0,
          },
          style: { width: ve.width ?? 260, height: ve.height ?? 120 },
        });
        continue;
      }
      // Op detailed-concept view rendert een sharp.Attribute /
      // sharp.Identifier compact als regel in de parent — niet als
      // standalone box. Skip de standalone-render hier.
      if (childrenInCompartment.has(key)) continue;
      const conceptId = ve.ref ?? key;
      const concept = store.getConcept(conceptId);
      if (!concept || concept.tombstoneInBranch) continue;
      const elementType = reg.getElementType(concept.typeId);
      const label = conceptDisplayLabel(concept);
      const typeLabel = elementType ? resolveBilingual(elementType.label, lang) : concept.typeId;
      const layer = elementType?.layer ?? "composite";
      // C4 convention: small "[Software System]" / "[Container]"
      // subtitle below the name on C4-flavoured views, plus the
      // concept's own description as a second line under that.
      // Computed here (rather than inside ConceptNode) so ConceptNode
      // stays a pure presentational component and we can compute it
      // once per node instead of per-render.
      const onC4View = !!diagram && isC4ViewKind(diagram.viewKind);
      const onBmcView = !!diagram?.viewKind?.startsWith("bmc.");
      // Drawing-primitieven (rechthoek / lijn) zijn niet-semantisch.
      // Op een C4-view zou `getC4Stereotype` "[Software System]" en
      // `resolveBilingual(description, lang)` "(unnamed)" terug kunnen
      // geven — beide ongewenst onder een vrije rechthoek. Dus
      // expliciet stereotype + description leeg laten voor drawing.*.
      const isDrawingTypeForRender = concept.typeId.startsWith("drawing.");
      // Stereotype-subtitle — pre-formatted incl. notation-brackets zodat
      // ConceptNode 'm letterlijk kan renderen. C4-conventie is `[…]`,
      // ArchiMate/Sharp/BMC-conventie is guillemet `«…»`.
      let stereotype: string | null =
        onC4View && !isDrawingTypeForRender
          ? (() => {
              const s = getC4Stereotype(
                concept.typeId,
                concept.attributes,
                lang,
                concept.stereotypes,
              );
              return s ? `[${s}]` : null;
            })()
          : null;
      // BMC-view: toon het meest relevante stereotype als subtitle
      // onder de naam. Voorkeur:
      //   1. bmc.*-stereotype (BMC-flavored — primair)
      //   2. c4.*-stereotype (cross-laag bridging — C4 op BMC view)
      //   3. sharp.*-stereotype (cross-laag bridging — Sharp op BMC view)
      // Labels in de packs zijn al guillemet-gewrapt; defense-in-depth
      // wrapt alsnog wanneer 't ontbreekt.
      let bmcViewStereoId: string | undefined;
      if (!stereotype && onBmcView && !isDrawingTypeForRender) {
        const stereoIds = concept.stereotypes ?? [];
        for (const prefix of ["bmc.", "c4.", "sharp."]) {
          const id = stereoIds.find((s) => s.startsWith(prefix));
          if (id) {
            bmcViewStereoId = id;
            break;
          }
        }
        if (bmcViewStereoId) {
          const st = reg.getStereotype(bmcViewStereoId);
          if (st) {
            const raw = resolveBilingual(st.label, lang);
            stereotype = raw.startsWith("«") && raw.endsWith("»") ? raw : `«${raw}»`;
          }
        }
      }
      if (
        !stereotype &&
        !isDrawingTypeForRender &&
        concept.stereotypes?.includes("sharp.ConceptConstraint")
      ) {
        stereotype = "«Concept Constraint»";
      }
      const rawDescription =
        onC4View && !isDrawingTypeForRender && concept.description
          ? resolveBilingual(concept.description, lang)
          : "";
      // resolveBilingual valt terug op "(unnamed)" wanneer alle taal-
      // slots leeg zijn — die placeholder hoort nooit als
      // diagram-tekst zichtbaar te zijn, dus filteren we 'm hier ook
      // uit (defense-in-depth t.o.v. de drawing-check hierboven).
      let description = rawDescription && rawDescription !== "(unnamed)" ? rawDescription : null;
      // «Concept Constraint» — toon de rule_statement (de business-
      // regel) onder de naam in het object. Dat is meteen leesbaar op
      // de canvas i.p.v. dat de modelleur 't AttributePanel moet openen.
      // Hergebruikt de bestaande `description`-slot in ConceptNode want
      // de render-stijl past al (kleinere font, word-wrap).
      if (
        !description &&
        !isDrawingTypeForRender &&
        concept.stereotypes?.includes("sharp.ConceptConstraint")
      ) {
        const rule = concept.attributes?.rule_statement;
        const ruleText =
          rule && typeof rule === "object" && !Array.isArray(rule)
            ? resolveBilingual(rule as { nl?: string; en?: string }, lang)
            : typeof rule === "string"
              ? rule
              : "";
        if (ruleText && ruleText !== "(unnamed)") description = ruleText;
      }
      const directTotal = directTotalCounts.get(key) ?? 0;
      // Chevron/collapse bij élke echte omvatting — genestte nodes
      // (concept-in-concept, C4-container, grouping) én, op detailed-concept-
      // views, de Attribuut/Identifier-compartiment-regels: die worden bij
      // collapse gevouwen (ConceptNode verbergt ze, badge toont het aantal).
      // Eerder werd de chevron blanket op alle sharp.* views verborgen,
      // waardoor genestte containers (bv. een C4-Archief) niet inklapbaar
      // waren én compartiment-concepten niet te vouwen.
      const transitiveDescendants = transitiveDescendantCounts.get(key) ?? 0;
      const hasChildren = directTotal > 0;
      // Collapse-state komt nu rechtstreeks van de container's eigen
      // viewState-flag (gezet/gewist door toggleCollapseContainer). De
      // descendants blijven gewoon visible in viewState; alleen z-index
      // verandert (zie hieronder). Badge-count = transitive descendants
      // wanneer collapsed (= "verberg zoveel achter mij"), anders 0.
      const isCollapsed = ve.collapsedAsContainer === true;
      const hiddenChildCount = isCollapsed ? transitiveDescendants : 0;
      // drawing.ListBox krijgt een eigen node-type "listbox" met
      // gespecialiseerde rendering (titel + sorteerbare lijst + drop).
      // Voor alle andere concept-types blijft het standaard "concept"-
      // type dat ConceptNode rendert.
      const isListBox = concept.typeId === "drawing.ListBox";
      if (isListBox) {
        const listboxData = {
          conceptId,
          // De Naam van het concept zelf wordt als lijst-titel
          // getoond — duidt voor de modelleur direct welk lijstvak
          // 't is. Geen apart `title`-attribuut nodig.
          title: concept.name,
          items: ve.listboxItems ?? [],
          fillColor: ve.nodeStyle?.fillColor,
          strokeColor: ve.nodeStyle?.strokeColor,
          fontColor: ve.nodeStyle?.fontColor,
        };
        const listboxNode: Node = {
          id: key,
          type: "listbox",
          position: ve.position,
          data: listboxData,
          style: {
            width: ve.width ?? 240,
            height: ve.height ?? 160,
          },
          ...(ve.containerId && viewState[ve.containerId]?.position
            ? { parentId: ve.containerId, extent: "parent" as const }
            : {}),
        };
        computedNodes.push(listboxNode);
        continue;
      }
      const node: Node = {
        id: key,
        type: "concept",
        position: ve.position,
        data: {
          label,
          layer,
          typeLabel,
          typeId: concept.typeId,
          conceptId,
          aspect: elementType?.aspect ?? "other",
          nodeStyle: ve.nodeStyle,
          stereotype,
          description,
          hiddenChildCount,
          hasChildren,
          isCollapsed,
          /** True wanneer minstens één andere node deze instance als
           *  containerId heeft = visueel-genest binnen deze box. ConceptNode
           *  toont de naam dan midden-boven i.p.v. centraal. */
          isContainer: containerInstanceIds.has(key),
          /** Box-grootte voor referentie (compartments, etc.). */
          width: ve.width,
          height: ve.height,
          /** De tekengrootte waarbij de naam in dít vakje past. Per object,
           *  niet per diagram — zie `passendeFont` hierboven. Een expliciete
           *  keuze via de werkbalk wint hier altijd van. */
          diagramFontSize: concept.typeId.startsWith("drawing.")
            ? undefined
            : passendeFont(conceptDisplayLabel(concept), ve.width),
          stereotypes: concept.stereotypes,
          attributes: concept.attributes,
          labelOffset: ve.labelOffset,
          // Status-bolletje linksboven op concepten waarvan een
          // stereotype op deze view-kind een `status_indicator`-regel
          // heeft en het bijbehorende attribuut een gemapte waarde
          // bevat. `null` op view-kinds zonder
          // `show_stereotype_status_indicators: true`.
          statusIndicator: statusIndicatorFor(diagram?.viewKind ?? "", {
            stereotypes: concept.stereotypes,
            attributes: concept.attributes,
          }),
          // Op standaard ArchiMate-views (`archimate.*`) tonen we
          // concepten in hun ArchiMate-laag-kleur, ongeacht
          // C4/Sharp-stereotypes die ze dragen. Stereotype-gestuurde
          // kleuring blijft alleen actief op de c4.*/sharp.*-views.
          //
          // BMC-views (`bmc.*`) zijn gemengd: BMC-getypeerde concepten
          // gebruiken de ArchiMate-laag-kleur (matcht palette: geel/
          // paars/perzik). Concepten op een BMC-view met een C4- of
          // Sharp-stereotype (cross-laag bridging) gebruiken juist
          // hún stereotype-pack-kleur — een c4.Container blijft
          // C4-blauw, een sharp.Concept blijft Sharp-peach. Beslist
          // per-concept op basis van het actieve BMC-view-stereotype
          // (`bmcViewStereoId` hierboven): bmc.* of geen stereotype
          // → archimate-base-colors aan; c4.* of sharp.* → uit zodat
          // colorForConcept de stereotype-token route neemt.
          useArchimateBaseColors: (() => {
            if (diagram?.viewKind?.startsWith("archimate.") ?? false) return true;
            if (!onBmcView) return false;
            if (!bmcViewStereoId) return true;
            return bmcViewStereoId.startsWith("bmc.");
          })(),
          // BMC-views renderen alle elementen als rechthoek ongeacht
          // het ArchiMate-aspect — geen Value-octagon, geen
          // BusinessProcess-pijl-vorm, geen Capability-zeshoek. Zo
          // matcht het uiterlijk de "tekst-in-blok"-conventie van het
          // Osterwalder-canvas en de andere BMC-views. ConceptNode
          // honoreert dit via `shapeStyle(aspect, forceRectangle)`.
          forceRectangle: onBmcView,
          // Sharp Detailed Concept compartment-children — alleen op
          // detailed-concept view en alleen voor parents die als
          // Concept fungeren. Wanneer leeg/undefined rendert ConceptNode
          // de standaard layout zonder compartment.
          compartmentChildren: compartmentChildrenByParent.get(key) ?? null,
          // Deterministische overlay-effect (kleur/dim) uit de actieve
          // regelset — `null` wanneer de overlay uit staat of geen regel
          // dit concept matcht (→ ConceptNode rendert standaardkleur).
          overlayEffect: overlayEvaluate({
            id: conceptId,
            typeId: concept.typeId,
            layer,
            stereotypes: concept.stereotypes,
            attributes: concept.attributes,
            name: concept.name,
          }),
        } satisfies Record<string, unknown>,
      };
      // Detecteer of dit element een descendant is van een collapsed
      // container (ergens hogerop in de containerId-chain). Bepaalt
      // zowel de zIndex (zie strategie hieronder) als of we de
      // parentId loskoppelen — RF's `calculateChildXYZ` forceert hard
      // `child.z = parent.z + 1`, wat onze collapse-via-zIndex
      // ontoereikend maakt voor parentId-genest kinderen. Door
      // parentId weg te halen en position naar absolute coords om
      // te rekenen, vervalt die regel.
      let obscuredByCollapse = false;
      {
        let cur: string | undefined = ve.containerId;
        const seen = new Set<string>();
        while (cur !== undefined && !seen.has(cur)) {
          seen.add(cur);
          if (viewState[cur]?.collapsedAsContainer === true) {
            obscuredByCollapse = true;
            break;
          }
          cur = viewState[cur]?.containerId;
        }
      }

      // Z-index strategie (van hoog naar laag prioriteit):
      //   1. Obscured-by-collapse descendants → -1000 (ver achter).
      //      Hierdoor kan een collapsed container met user-zOrder
      //      bv. -5 nog steeds z'n eigen descendants verbergen, terwijl
      //      de container zelf relatief vrij gestapeld kan worden t.o.v.
      //      andere objecten op het canvas. Geldt ook voor nested
      //      collapsed-binnen-collapsed: inner collapsed verzakt mee
      //      naar -1000 want outer dekt 'm sowieso af.
      //   2. Collapsed containers (niet zelf obscured) → user-zOrder
      //      indien gezet, anders default 1001 (boven alles). De
      //      gebruiker kan z'n collapsed container dus 'naar achteren'
      //      duwen om 'm achter andere objecten te leggen — descendants
      //      blijven verborgen omdat die op -1000 zitten.
      //   3. User-set zOrder via toolbar — wint van type-defaults.
      //   4. Drawing-primitieven ZONDER container → -10: helemaal achter,
      //      zodat de concepten binnen zo'n kaderrechthoek met de muis
      //      selecteerbaar blijven. **Zit de tekening zelf in een container,
      //      dan geldt dat niet**: React Flow deelt z-index uit op
      //      nestdiepte (top-level 0, kind 1, kleinkind 2), dus -10 zet 'm
      //      achter z'n eigen ouder — die 'm dekkend overschildert. Het
      //      resultaat was een rechthoek die je wel aanmaakt maar nooit
      //      ziet: "ik mag geen tekening op dit diagram plaatsen" (gemeld
      //      2026-09-02). Een geneste tekening laat z'n z-index dus met
      //      rust; dekt 'ie een buurman af, dan duwt de z-order-knop in de
      //      werkbalk 'm naar achteren (regel 3 wint hiervan).
      //   5. Anders: default zIndex (auto-managed door React Flow).
      // We zetten zowel `node.zIndex` (waarop RF baseert wat 'ie in z'n
      // internalNode bewaart) ALS `node.style.zIndex` (die door RF's
      // NodeWrapper als laatste in de inline-style wordt gespread, dus
      // override't `internals.z`). De style-override is cruciaal omdat
      // RF's `adoptUserNodes` `checkEquality` op userNode-referentie doet
      // en in sommige render-cycles dezelfde referentie ziet (bv. tijdens
      // selection-mutations), waardoor `internals.z` op de oude waarde
      // blijft hangen ook al hebben wij `node.zIndex` bijgewerkt. Met de
      // expliciete `node.style.zIndex` werkt z-order altijd door naar de
      // DOM — onafhankelijk van RF's interne caching.
      let appliedZ: number | undefined;
      if (obscuredByCollapse) {
        appliedZ = -1000;
      } else if (isCollapsed) {
        // Dichtgeklapte container — bewust bóven de edges (10000)
        // zodat 'ie alles erbinnen visueel afdekt, ook lijnen die
        // anders door 'm heen zouden lopen. Symmetrisch met
        // `obscuredByCollapse`-logica voor descendants.
        appliedZ = ve.zOrder ?? 10000;
      } else if (ve.zOrder !== undefined) {
        appliedZ = ve.zOrder;
      } else if (isDrawingTypeForRender && ve.containerId === undefined) {
        appliedZ = -10;
      }
      if (appliedZ !== undefined) {
        node.zIndex = appliedZ;
        node.style = { ...(node.style ?? {}), zIndex: appliedZ };
      }
      // Apply explicit width/height from the resize handle if set.
      if (ve.width !== undefined) node.width = ve.width;
      if (ve.height !== undefined) node.height = ve.height;
      // Visual containment: when `containerId` points to another node on
      // dit diagram, React Flow rendert deze child binnen z'n parent en
      // de `position` wordt geïnterpreteerd als relatief tot de parent.
      // We slaan `extent: "parent"` bewust over zodat de gebruiker een
      // child uit de container kan slepen — onnest-detectie in
      // onNodesChange pikt op wanneer het centrum van de child de
      // parent verlaat. Bij obscured-by-collapse breken we de
      // parent-child relatie los (zie zIndex-comment hierboven).
      if (obscuredByCollapse) {
        // Bereken absolute position door alle parent-positions in de
        // chain bij elkaar op te tellen. Stop bij eerste parent zonder
        // position (zou raar zijn maar guard ertegen).
        let absX = ve.position.x;
        let absY = ve.position.y;
        let cur: string | undefined = ve.containerId;
        const seen = new Set<string>();
        while (cur !== undefined && !seen.has(cur)) {
          seen.add(cur);
          const parentVe = viewState[cur];
          if (!parentVe?.position) break;
          absX += parentVe.position.x;
          absY += parentVe.position.y;
          cur = parentVe.containerId;
        }
        node.position = { x: absX, y: absY };
        // Opzettelijk geen parentId — dat is de hele truc.
      } else if (
        ve.containerId &&
        viewState[ve.containerId]?.position &&
        !invertedNestKeys.has(key)
      ) {
        node.parentId = ve.containerId;
      } else if (invertedNestKeys.has(key)) {
        // Stale containerId (kind groter dan parent). Behandel als
        // unnested: bewaar absoluut waar `ve.position` nu staat. De
        // viewState-data blijft corrupt tot een drag-actie of expliciete
        // cleanup 'm fixt — maar visueel klopt de positie weer.
      }
      computedNodes.push(node);
      instanceToConcept.set(key, conceptId);
      // Prefer the canonical entry (key === conceptId) as "first instance".
      const existing = firstInstancePerConcept.get(conceptId);
      if (!existing || key === conceptId) {
        firstInstancePerConcept.set(conceptId, key);
      }
    }
    // React Flow renders nodes in array order; children must come AFTER
    // hun parent zodat ze gelaagd worden. Diepe-nesting (3+ niveaus)
    // vereist depth-based sortering: een grandchild moet ná z'n parent
    // komen, niet alleen ná z'n root. Een puur binaire heeft-parent-
    // sort (0 vs 1) plaatst grandchild en parent in dezelfde bucket
    // met willekeurige onderlinge volgorde — RF dropt dan de
    // grandchild stilzwijgend wanneer-ie vóór z'n parent staat.
    const nodeById = new Map(computedNodes.map((n) => [n.id, n]));
    const depthCache = new Map<string, number>();
    const computeDepth = (id: string): number => {
      const cached = depthCache.get(id);
      if (cached !== undefined) return cached;
      const node = nodeById.get(id);
      if (!node?.parentId) {
        depthCache.set(id, 0);
        return 0;
      }
      // Cycle-guard: tijdens berekening tijdelijk depth=0 invoeren
      // zodat een onverwachte cycle niet oneindig recursed.
      depthCache.set(id, 0);
      const d = computeDepth(node.parentId) + 1;
      depthCache.set(id, d);
      return d;
    };
    computedNodes.sort((a, b) => computeDepth(a.id) - computeDepth(b.id));

    // Per-diagram-scoping: a relation is shown on diagram D only when
    // some viewState-entry refers to it (presence = "placed here"). Multi-
    // instance (2026-05-26): per relation can multiple entries existeren —
    // canonical (entry-key = relation.id) of multi (entry-key = UUID met
    // ref = relation.id en type = "relation"). Legacy relations that
    // pre-date this convention have no viewState entry on any diagram —
    // those keep the old "show wherever both endpoints are" behaviour
    // so existing data doesn't vanish.
    const placedRelationIds = new Set<string>();
    const allRelations = store.listRelations();
    for (const d of store.listDiagrams()) {
      for (const [k, ve] of Object.entries(d.viewState)) {
        // Object-instances hebben een position; relation-instances niet.
        if (ve.position !== undefined) continue;
        if (ve.type === "object") continue;
        const rid = ve.ref ?? k;
        if (store.getRelation(rid)) placedRelationIds.add(rid);
      }
    }

    // Voor edge re-anchor naar nearest-visible-ancestor (collapse-feature):
    // alle instances per concept verzamelen — ook de hidden — zodat we
    // kunnen oplossen welke instance op dit diagram als anchor dient
    // wanneer alle directe instances ingeklapt zijn.
    const allInstancesPerConcept = new Map<string, string[]>();
    for (const [key, ve] of Object.entries(viewState)) {
      if (ve.position === undefined) continue;
      const conceptId = ve.ref ?? key;
      const arr = allInstancesPerConcept.get(conceptId);
      if (arr) arr.push(key);
      else allInstancesPerConcept.set(conceptId, [key]);
    }

    /**
     * Loop omhoog langs containerId-chain tot we een instance vinden die
     * niet `hidden: true` is. Dat is de anchor waarop een edge naar een
     * (mogelijk) ingeklapt endpoint wordt geprojecteerd. Returnt null
     * wanneer er geen zichtbare voorouder bestaat (dan kan de edge niet
     * gerenderd worden).
     */
    const findVisibleAncestor = (instanceId: string | undefined): string | null => {
      if (!instanceId) return null;
      let cur: string | undefined = instanceId;
      const seen = new Set<string>();
      while (cur !== undefined && !seen.has(cur)) {
        seen.add(cur);
        const ve: import("@ae/domain").ViewElementState | undefined = viewState[cur];
        if (!ve) return null;
        if (ve.hidden !== true) return cur;
        cur = ve.containerId;
      }
      return null;
    };

    /**
     * Bovenop `findVisibleAncestor` (hidden-skip): wanneer het resultaat
     * zélf een descendant is van een DICHTGEKLAPTE container, is die
     * descendant niet zichtbaar — collapse zet 'm op zIndex -1000, ver
     * achter de collapsed container die 'm afdekt. De zichtbare anchor is
     * dan de BUITENSTE collapsed ancestor (bij geneste collapse de
     * outermost, want die dekt alles eronder af). Zo re-anchoren edges
     * naar een ingeklapt endpoint naar de container-rand i.p.v. naar de
     * verborgen descendant door de container heen te steken. Edges tussen
     * twee descendants ónder dezelfde collapsed container krijgen dan beide
     * dezelfde anchor → de same-anchor-`continue` in de edge-loop slaat ze
     * over (interne lijnen verdwijnen mee met de collapse). Onafhankelijk
     * van de container's user-zOrder, dus 'm naar achteren duwen blijft
     * mogelijk zonder dat interne lijnen erdoorheen poken.
     */
    const resolveVisibleAnchor = (instanceId: string | undefined): string | null => {
      const base = findVisibleAncestor(instanceId);
      if (!base) return null;
      let outermostCollapsed: string | null = null;
      let cur: string | undefined = viewState[base]?.containerId;
      const seen = new Set<string>();
      while (cur !== undefined && !seen.has(cur)) {
        seen.add(cur);
        if (viewState[cur]?.collapsedAsContainer === true) outermostCollapsed = cur;
        cur = viewState[cur]?.containerId;
      }
      return outermostCollapsed ?? base;
    };

    /**
     * Geef een zichtbare anchor-instance terug voor een endpoint van een
     * relatie. Volgorde: prefer een instance die zelf zichtbaar is;
     * zo niet, val terug op een ingeklapte instance en walk omhoog. Eerste
     * resultaat wint — bij meerdere instances is dat doorgaans de
     * canonieke (key === conceptId).
     *
     * `reAnchored` is true wanneer de gevonden anchor een ander id is dan
     * de input — d.w.z. de oorspronkelijke instance was hidden óf zat onder
     * een collapsed container, en we zijn één of meer container-niveaus
     * omhoog gegaan. RoutedEdge gebruikt deze flag om het attachment-punt op
     * de container-rand te berekenen via de hoek naar het andere endpoint,
     * in plaats van de default React-Flow-handle te kiezen.
     */
    const resolveEndpointAnchor = (
      preferredInstanceId: string | undefined,
      conceptId: string,
    ): { anchor: string | null; reAnchored: boolean } => {
      if (preferredInstanceId) {
        const direct = resolveVisibleAnchor(preferredInstanceId);
        if (direct) return { anchor: direct, reAnchored: direct !== preferredInstanceId };
      }
      // Try first-visible-instance (was 't bestaande pad)
      const first = firstInstancePerConcept.get(conceptId);
      if (first) {
        const anchor = resolveVisibleAnchor(first);
        if (anchor) return { anchor, reAnchored: anchor !== first };
      }
      // Alle instances zijn hidden — pak de eerste-met-zichtbare-ancestor
      const all = allInstancesPerConcept.get(conceptId) ?? [];
      for (const inst of all) {
        const anchor = resolveVisibleAnchor(inst);
        if (anchor) return { anchor, reAnchored: anchor !== inst };
      }
      return { anchor: null, reAnchored: false };
    };

    /**
     * Bij re-anchor: bereken de hoek vanuit het anchor-center (de zichtbare
     * container) naar het ORIGINELE endpoint center (de hidden instance).
     * Resultaat is bedoeld voor `pointFromAngle` op de container — zo
     * landt de lijn op de container-rand op het punt waar de denkbeeldige
     * lijn van container-center naar de hidden endpoint de rand kruist.
     *
     * Werkt door de `containerId`-chain van origin omhoog te wandelen tot
     * we de ancestor (anchor) bereiken, en relatieve positions te
     * accumuleren — viewState-positions zijn relatief tot de directe
     * parent, dus we tellen op om de origin's positie in ancestor-local
     * coords te krijgen. Returnt undefined wanneer geen `containerId`-pad
     * naar de ancestor bestaat (bv. origin is geen descendant van de
     * gevonden anchor — kan niet gebeuren bij collapse, wel mogelijk bij
     * andere re-anchor-paden).
     *
     * Default node-grootte 140×60 matcht de fallback in RoutedEdge zodat
     * angle-berekening en attachment-rendering uit dezelfde geometrie
     * uitgaan.
     */
    const computeReAnchorAngle = (
      originInstanceId: string,
      ancestorInstanceId: string,
    ): number | undefined => {
      let accX = 0;
      let accY = 0;
      let cur: string | undefined = originInstanceId;
      const seen = new Set<string>();
      while (cur !== undefined && cur !== ancestorInstanceId && !seen.has(cur)) {
        seen.add(cur);
        const ve: import("@ae/domain").ViewElementState | undefined = viewState[cur];
        if (!ve?.position) return undefined;
        accX += ve.position.x;
        accY += ve.position.y;
        cur = ve.containerId;
      }
      if (cur !== ancestorInstanceId) return undefined;
      const originVe = viewState[originInstanceId];
      if (!originVe) return undefined;
      const originW = originVe.width ?? 140;
      const originH = originVe.height ?? 60;
      const ancestorVe = viewState[ancestorInstanceId];
      if (!ancestorVe) return undefined;
      const ancestorW = ancestorVe.width ?? 140;
      const ancestorH = ancestorVe.height ?? 60;
      // Origin's center in ancestor's local coords
      const ox = accX + originW / 2;
      const oy = accY + originH / 2;
      // Ancestor's center in its own local coords
      const ax = ancestorW / 2;
      const ay = ancestorH / 2;
      const dx = ox - ax;
      const dy = oy - ay;
      if (dx === 0 && dy === 0) return undefined;
      return ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
    };

    // Auto-hide containment-relaties tussen visueel-geneste paren: een
    // viewState-entry met `containerId` is een child binnen een parent,
    // dus de Composition/Aggregation/Realization-relatie tussen die
    // twee concepten is visueel redundant (de nesting communiceert al
    // dat het kind binnen de container hoort). Per-diagram berekend op
    // basis van containerId-pad zonder viewState-write — `hidden` blijft
    // ongebruikt zodat user-hides via Delete-on-edge nog onderscheiden
    // kunnen worden.
    const autoHiddenContainmentRelationIds = new Set<string>();
    {
      // parent-concept-id per child-instance-id
      const parentByInstance = new Map<string, string>();
      for (const [instanceId, ve] of Object.entries(viewState)) {
        if (!ve.containerId) continue;
        const parentVe = viewState[ve.containerId];
        if (!parentVe) continue;
        const parentConceptId = parentVe.ref ?? ve.containerId;
        parentByInstance.set(instanceId, parentConceptId);
      }
      if (parentByInstance.size > 0) {
        for (const r of allRelations) {
          if (r.tombstoneInBranch) continue;
          // Tekenlijnen niet. Deze onderdrukking bestaat omdat de omhulling de
          // relatie al uitdrukt — een compositie hoef je niet én als lijn én
          // als kader te tonen. Een `drawing.Line` drukt niets uit: 'ie ís de
          // aanwijslijn die de modelleur tekende. Verdwijnt 'ie zodra 't vlak
          // in de groep ligt waar de lijn heen wijst, dan is precies dat werk
          // weg (gemeld 2026-09-02: "schuif ik 'm eruit, dan wordt 'ie wel
          // zichtbaar"). Let op: dit is een AFGELEIDE onderdrukking, niet de
          // `hidden`-vlag — in de viewState is niets te zien.
          if (r.typeId.startsWith("drawing.")) continue;
          for (const [childInstance, parentConceptId] of parentByInstance) {
            const childVe = viewState[childInstance];
            const childConceptId = childVe?.ref ?? childInstance;
            if (
              (r.sourceConceptId === childConceptId && r.targetConceptId === parentConceptId) ||
              (r.sourceConceptId === parentConceptId && r.targetConceptId === childConceptId)
            ) {
              autoHiddenContainmentRelationIds.add(r.id);
              break;
            }
          }
        }
      }
    }

    // Multi-instance edge-derivation (2026-05-26): bouw per relation een
    // lijst van viewState-entries op dit diagram. Elke entry → één edge.
    // Voor relations zonder enige entry op dit diagram (legacy of geplaatst
    // elders) emitten we een virtual entry zodat legacy data zichtbaar
    // blijft.
    type RelInstance = { entryKey: string; entry: ViewElementState };
    const relationInstancesOnDiagram = new Map<string, RelInstance[]>();
    for (const [key, ve] of Object.entries(viewState)) {
      if (ve.position !== undefined) continue;
      if (ve.type === "object") continue;
      const relId = ve.ref ?? key;
      if (!store.getRelation(relId)) continue;
      const list = relationInstancesOnDiagram.get(relId);
      if (list) list.push({ entryKey: key, entry: ve });
      else relationInstancesOnDiagram.set(relId, [{ entryKey: key, entry: ve }]);
    }

    // Build edges by iterating per relation × its instance-entries on
    // this diagram. For canonical/legacy (no entry on this diagram) we
    // still emit one virtual entry so legacy data renders. For multi-
    // instance entries we emit one edge per entry, keyed by entry-key
    // (UUID) so React Flow can render them all distinctly. The
    // `data.relationId` field threads the underlying relation-id through
    // for downstream handlers (selection/deletion/label-edit/etc.) so
    // they can look up the domain relation regardless of whether the
    // edge id is canonical (= relation.id) or multi-instance (= UUID).
    const computedEdges: Edge[] = [];
    for (const r of allRelations) {
      if (r.tombstoneInBranch) continue;
      if (autoHiddenContainmentRelationIds.has(r.id)) continue;
      const instances = relationInstancesOnDiagram.get(r.id);
      const isLegacy = !placedRelationIds.has(r.id);
      // Bepaal welke entries we te renderen hebben:
      //  - Multi-instance / canonical-met-entry → één edge per entry
      //  - Legacy zonder entries op ENIG diagram → één virtual entry
      //  - Op andere diagrammen geplaatst → niet hier
      let entriesToRender: RelInstance[];
      if (instances && instances.length > 0) {
        entriesToRender = instances;
      } else if (isLegacy) {
        entriesToRender = [{ entryKey: r.id, entry: {} as ViewElementState }];
      } else {
        continue;
      }

      for (const { entryKey, entry: handleState } of entriesToRender) {
        if (handleState?.hidden === true) continue;
        const sourceResolved = resolveEndpointAnchor(
          handleState?.sourceInstanceId,
          r.sourceConceptId,
        );
        const targetResolved = resolveEndpointAnchor(
          handleState?.targetInstanceId,
          r.targetConceptId,
        );
        if (!sourceResolved.anchor || !targetResolved.anchor) continue;
        const isGenuineSelfLoop = r.sourceConceptId === r.targetConceptId;
        if (sourceResolved.anchor === targetResolved.anchor && !isGenuineSelfLoop) {
          continue;
        }
        // Inline `r` and `handleState` worden gebruikt door de body
        // hieronder. We bouwen één edge en pushen 'm; geen .map omdat we
        // per relation 0..N edges willen kunnen produceren.
        computedEdges.push(
          buildOneEdgeFromRelation(r, entryKey, handleState, sourceResolved, targetResolved),
        );
      }
    }

    // Builder-helper: één edge bouwen voor (relation, instance-entry).
    // Pure function — al z'n inputs zijn doorgegeven; sluit alleen om
    // closure-state (viewState, lang, diagram, store, reg, colorForType,
    // computeReAnchorAngle, etc.) die onveranderd is sinds we boven 'm
    // gebouwd hebben. Behalve `id` en `data.relationId` is de body
    // identiek aan de pre-multi-instance map((r) => …).
    function buildOneEdgeFromRelation(
      r: Relation,
      entryKey: string,
      handleState: ViewElementState | undefined,
      sourceResolved: { anchor: string | null; reAnchored: boolean },
      targetResolved: { anchor: string | null; reAnchored: boolean },
    ): Edge {
      // M8 §5: op een resolver-viewpoint een gezet relatie-alt-label i.p.v.
      // het gewone label tonen — per taal (leeg alt in de getoonde taal →
      // gewone label).
      const relAlt = viewResolver ? r.altLabels[viewResolver]?.[lang] : undefined;
      const effectiveLabel = relAlt && relAlt.trim().length > 0 ? { [lang]: relAlt } : r.label;
      const hasUserLabel = Object.values(effectiveLabel).some((v) => v.trim().length > 0);
      const userLabel = hasUserLabel ? resolveBilingual(effectiveLabel, lang).trim() : "";
      // Only show a label when the user has explicitly entered one.
      // The relation type is already implied by the line style/arrow.
      let edgeLabel: string | undefined = userLabel || undefined;
      let influenceWeight = false;
      if (r.typeId === "archimate.Influence") {
        const value = typeof r.attributes.value === "string" ? r.attributes.value.trim() : "";
        if (value) {
          edgeLabel = userLabel ? `${value} ${userLabel}` : value;
          influenceWeight = !userLabel;
        }
      }
      // Sharp schrijft de multipliciteit als één label midden op de pijl, in
      // de vorm `bron:doel` — `1:1` tussen subprocessen, `1:M` en `M:1` tussen
      // processen (Workflow Modeling, figuur 3.12). Niet als kraaienpoot: die
      // hoort bij het Sharp-contract van «Concept Association» en staat al in
      // `edgeStyles.ts`.
      //
      // Overal tonen waar de relatie zichtbaar is, niet alleen op een
      // werkstroomview — zelfde afweging als bij de CRUD-badge hieronder: wie
      // de waarde invult, wil 'm zien staan.
      const multipliciteit = formatMultiplicityLabel(
        r.attributes[MULTIPLICITY_SOURCE_KEY],
        r.attributes[MULTIPLICITY_TARGET_KEY],
      );
      if (multipliciteit) {
        edgeLabel = userLabel ? `${multipliciteit} ${userLabel}` : multipliciteit;
      }
      // «Access CRUD»-stereotype op een archimate.Access-relatie
      // toont z'n crud_mode (Create/Read/Update/Delete/Read-Write)
      // als gekleurd badge op de lijn — overal waar de relatie
      // zichtbaar is. Sinds 2026-05-10 niet meer gegated op
      // sharp.data-process-connection: ook op ArchiMate-views
      // (bv. Application Cooperation) is de CRUD-render zinvol
      // zodat het stereotype z'n betekenis behoudt zodra de
      // gebruiker 'm aanzet.
      let crudBadge: { letter: string; color: string; textColor: string } | null = null;
      if (r.typeId === "archimate.Access" && r.stereotypes.includes("sharp.AccessCRUD")) {
        const mode = typeof r.attributes.crud_mode === "string" ? r.attributes.crud_mode : "";
        switch (mode) {
          case "create":
            crudBadge = { letter: "C", color: "#16a34a", textColor: "#ffffff" };
            break;
          case "read":
            crudBadge = { letter: "R", color: "#1d4ed8", textColor: "#ffffff" };
            break;
          case "update":
            crudBadge = { letter: "U", color: "#ea580c", textColor: "#ffffff" };
            break;
          case "delete":
            crudBadge = { letter: "D", color: "#dc2626", textColor: "#ffffff" };
            break;
          case "read_write":
            // Lichtgroen — onderscheid met C (verzadigd groen) — donkere
            // tekst voor leesbaarheid op deze lichte achtergrond.
            crudBadge = { letter: "R/W", color: "#86efac", textColor: "#0f172a" };
            break;
        }
        if (crudBadge) {
          edgeLabel = userLabel ? `${crudBadge.letter} ${userLabel}` : crudBadge.letter;
        }
        // «Access CRUD»-attribuut `hide_label`: aangevinkt → geen label op de
        // lijn (tekst én gekleurde badge weg). Standaard uit = tonen.
        if (r.attributes.hide_label === true) {
          edgeLabel = undefined;
          crudBadge = null;
        }
      }
      // handleState, sourceResolved, targetResolved zijn parameters van
      // de outer function — al berekend in de relationLoop hierboven.
      const sourceNodeId = sourceResolved.anchor ?? r.sourceConceptId;
      const targetNodeId = targetResolved.anchor ?? r.targetConceptId;
      // Wanneer een endpoint re-anchored is naar een container (de
      // oorspronkelijke instance is hidden), horen de saved sourceAngle/
      // targetAngle en sourceHandle/targetHandle bij die hidden endpoint
      // — niet bij de container. We berekenen een nieuwe hoek vanuit
      // container-center naar het ORIGINELE endpoint center (de hidden
      // instance, nog steeds in viewState). Resultaat: de lijn raakt de
      // container-rand op het punt waar je intuïtief verwacht dat de
      // lijn doorheen ging — TOP-rand als de inner-instance bovenin de
      // container zat, LEFT-rand als 'ie linksonder zat, etc.
      // Wanneer de chain niet bepaalbaar is (geen containerId-pad), valt
      // RoutedEdge terug op de center-naar-other-endpoint-heuristiek
      // (zie sourceReAnchored-flag-pad daar).
      const originalSourceInstance = handleState?.sourceInstanceId;
      const originalTargetInstance = handleState?.targetInstanceId;
      const reAnchoredSourceAngle =
        sourceResolved.reAnchored && originalSourceInstance && sourceResolved.anchor
          ? computeReAnchorAngle(originalSourceInstance, sourceResolved.anchor)
          : undefined;
      const reAnchoredTargetAngle =
        targetResolved.reAnchored && originalTargetInstance && targetResolved.anchor
          ? computeReAnchorAngle(originalTargetInstance, targetResolved.anchor)
          : undefined;
      // xyflow's default edge aria-label is `Edge from ${source} to ${target}`
      // met de RAW node/concept-uuid's — onbruikbaar voor screen readers en
      // (bleek in de praktijk) ook zichtbaar via macOS' "Hover Text"-
      // accessibility-feature (Cmd ingedrukt houden boven de lijn toont 'm
      // als grote on-screen caption). Overschrijf met de resolved concept-
      // namen zodat wat er ook getoond/voorgelezen wordt zinnig is.
      const sourceConceptForLabel = store.getConcept(r.sourceConceptId);
      const targetConceptForLabel = store.getConcept(r.targetConceptId);
      const edgeAriaLabel = t("canvas.edge.ariaLabel", {
        source: sourceConceptForLabel
          ? conceptDisplayLabel(sourceConceptForLabel)
          : r.sourceConceptId,
        target: targetConceptForLabel
          ? conceptDisplayLabel(targetConceptForLabel)
          : r.targetConceptId,
      });
      const baseEdge: Edge = {
        id: entryKey,
        // Custom edge: smoothstep routing by default, polyline through
        // waypoints when the user has bent the line. RoutedEdge reads
        // waypoints from `data`.
        type: "routed",
        source: sourceNodeId,
        target: targetNodeId,
        ariaLabel: edgeAriaLabel,
        // Edges altijd boven alle nodes — React Flow's default legt
        // SVG-edges in een laag onder de HTML-nodes, waardoor lijnen
        // achter een node-vakje verdwijnen wanneer ze er doorheen
        // lopen. Met expliciete zIndex 5000 staan ze boven elke
        // gewone node (incl. user-set z-stack-zOrder via de
        // toolbar). Enige uitzondering: dichtgeklapte containers
        // krijgen zIndex 10000 zodat ze edges erbinnen visueel
        // afdekken — bewust gedrag, conform "lijnen bovenop behalve
        // in een dichtgeklapte groepering". Selected edges krijgen
        // 6000 in `applySelectedStyle` (boven andere edges, onder
        // collapsed containers).
        zIndex: 5000,
        data: {
          // Multi-instance: edge.id is per-instance (UUID of relationId
          // voor canonical). Handlers die de onderliggende relation
          // willen ophalen lezen `data.relationId` i.p.v. edge.id.
          relationId: r.id,
          waypoints: handleState?.waypoints,
          // C4-Flow én c4.Uses default-routing is "straight" (Simon
          // Brown-stijl): rechte lijn met richting i.p.v. smoothstep-
          // curve. c4.Uses rendert hier als visuele tegenhanger van
          // Flow op container/component/dynamic/deployment-views.
          // User-override via toolbar wint nog steeds.
          routing:
            handleState?.routing ??
            ((r.typeId === "archimate.Flow" || r.typeId === "c4.Uses") &&
            diagram?.viewKind?.startsWith("c4.")
              ? "straight"
              : undefined),
          labelOffset: handleState?.labelOffset,
          labelMaxWidth: handleState?.labelMaxWidth,
          labelTextAlign: handleState?.labelTextAlign,
          labelWrap: handleState?.labelWrap,
          // Bij re-anchor: gebruik de uit-viewState-berekende hoek; valt
          // terug op undefined wanneer de chain niet bepaalbaar is, dan
          // doet RoutedEdge zelf een center-naar-other-endpoint-fallback.
          // Bij niet-re-anchor: gebruik de saved angle van de gebruiker.
          sourceAngle: sourceResolved.reAnchored ? reAnchoredSourceAngle : handleState?.sourceAngle,
          targetAngle: targetResolved.reAnchored ? reAnchoredTargetAngle : handleState?.targetAngle,
          sourceReAnchored: sourceResolved.reAnchored,
          targetReAnchored: targetResolved.reAnchored,
          // Default edge stroke = the SOURCE concept's layer-border
          // colour (e.g. blue for application, green for technology).
          // This visually ties an edge to the layer it originates from
          // and keeps the canvas colour-coherent. The user can still
          // override per-edge via the toolbar swatch — `handleState
          // .lineColor` (when set) wins over the auto-derived value.
          // Falls back to undefined when source concept can't be
          // resolved, so the relation-type default takes over.
          // «Access CRUD»-stereotype gebruikt sinds 2026-05-10 GEEN
          // eigen lijnkleur meer — de CRUD-info reist via de
          // gekleurde letter-badge plus mode-specifieke pijlpunten
          // (zie edgeStyles.ts). Lijn behoudt z'n standaard
          // ArchiMate-Access-styling (slate, gestippeld).
          lineColor: (() => {
            if (handleState?.lineColor) return handleState.lineColor;
            // C4-Flow + c4.Uses op c4.* views: donkergrijze lijn
            // (Simon Brown-conventie), niet de auto-derived
            // source-layer-kleur.
            if (
              (r.typeId === "archimate.Flow" || r.typeId === "c4.Uses") &&
              diagram?.viewKind?.startsWith("c4.")
            ) {
              return "#334155";
            }
            // Association + «bmc.Realization» op bmc.* views:
            // donkergrijze lijn (parallel aan c4-conventie) zodat
            // 't visueel uit de source-layer-kleur loskomt — een
            // bridging-realisatie hoort niet de gele/blauwe
            // ArchiMate-laag-kleur van source over te nemen.
            if (
              r.typeId === "archimate.Association" &&
              r.stereotypes?.includes("bmc.Realization") &&
              diagram?.viewKind?.startsWith("bmc.")
            ) {
              return "#334155";
            }
            const src = store.getConcept(r.sourceConceptId);
            if (!src) return undefined;
            const t = reg.getElementType(src.typeId);
            return colorForType(src.typeId, t?.layer ?? "composite").border;
          })(),
          // User-override stroke pattern (solid / dashed / dotted) —
          // applied on top of any relation-type-default in RoutedEdge.
          lineStyle: handleState?.lineStyle,
          // Node sizes are needed by RoutedEdge to compute angle-based
          // attachment points via pointFromAngle.
          sourceSize: {
            w: viewState[sourceNodeId]?.width ?? 140,
            h: viewState[sourceNodeId]?.height ?? 60,
          },
          targetSize: {
            w: viewState[targetNodeId]?.width ?? 140,
            h: viewState[targetNodeId]?.height ?? 60,
          },
          // C4 convention: render een protocol/technology-subtitle
          // onder het edge-label (e.g. "[Telephone]", "[gRPC]"). Bron-
          // volgorde: de c4.Connection-stereotype's `protocol`-veld
          // wint (M5-canonical) en valt terug op de legacy
          // `attributes.technology` voor pre-M5 data. Alleen op
          // C4-flavoured views; ArchiMate-diagrammen blijven kaal.
          technology: (() => {
            if (!diagram || !isC4ViewKind(diagram.viewKind)) return undefined;
            if (r.stereotypes?.includes("c4.Connection")) {
              const proto = r.attributes.protocol;
              if (typeof proto === "string" && proto.trim().length > 0) {
                return proto.trim();
              }
            }
            const tech = r.attributes.technology;
            return typeof tech === "string" ? tech.trim() || undefined : undefined;
          })(),
          // Sharp Concept-Model rendering — forward/reverse-namen
          // parallel aan de lijn i.p.v. één naam in het midden.
          // Activeert op de twee viewpoints waar cardinaliteit + naam-
          // richting betekenis hebben (concept-model / detailed-
          // concept). Contextual blijft één label want daar is de
          // associatie inhoudelijk minder zwaar.
          dualEndLabels: (() => {
            if (!diagram) return undefined;
            if (
              diagram.viewKind !== "sharp.concept-model" &&
              diagram.viewKind !== "sharp.detailed-concept"
            ) {
              return undefined;
            }
            const isSharpAssoc =
              r.stereotypes.includes("sharp.ConceptAssociation") ||
              r.stereotypes.includes("sharp.IdentifyingAssociation");
            if (!isSharpAssoc) return undefined;
            const fwd = r.attributes.forward_name;
            const rev = r.attributes.reverse_name;
            const resolveBi = (v: unknown): string => {
              if (typeof v === "string") return v;
              if (v && typeof v === "object" && !Array.isArray(v)) {
                const o = v as { nl?: unknown; en?: unknown };
                const langKey = lang === "en" ? "en" : "nl";
                const otherKey = langKey === "nl" ? "en" : "nl";
                const primary = o[langKey];
                if (typeof primary === "string" && primary.trim()) return primary;
                const fallback = o[otherKey];
                if (typeof fallback === "string") return fallback;
              }
              return "";
            };
            const forward = resolveBi(fwd);
            const reverse = resolveBi(rev);
            if (!forward && !reverse) return undefined;
            return { forward, reverse };
          })(),
          sourceLabelInset: handleState?.sourceLabelInset,
          targetLabelInset: handleState?.targetLabelInset,
          sourceLabelWidth: handleState?.sourceLabelWidth,
          targetLabelWidth: handleState?.targetLabelWidth,
          sourceLabelOffset: handleState?.sourceLabelOffset,
          targetLabelOffset: handleState?.targetLabelOffset,
        },
        label: edgeLabel,
        // Influence weight (just `+` / `-` etc.) renders larger + bold so
        // it's as readable as a concept name — matches the ArchiMate
        // convention for labelling Influence strength.
        // RoutedEdge renders labels in HTML (EdgeLabelRenderer), so
        // use CSS color/backgroundColor instead of SVG fill.
        labelStyle: crudBadge
          ? {
              fontSize: 11,
              fontWeight: 700,
              color: crudBadge.textColor,
              lineHeight: 1,
            }
          : influenceWeight
            ? {
                fontSize: 20,
                fontWeight: 700,
                color: handleState?.labelTextColor ?? "#334155",
              }
            : {
                // Standaard 11 in plaats van 10: op een lijn met een pijlpunt
                // ernaast leest 10 te klein. De modelleur kan 'm per relatie
                // overschrijven via de werkbalk.
                fontSize: handleState?.labelFontSize ?? 11,
                color: handleState?.labelTextColor ?? "#475569",
                ...(handleState?.labelBold ? { fontWeight: 700 } : {}),
                ...(handleState?.labelItalic ? { fontStyle: "italic" } : {}),
                ...(handleState?.labelUnderline ? { textDecoration: "underline" } : {}),
                ...(handleState?.labelFontFamily
                  ? { fontFamily: handleState.labelFontFamily }
                  : {}),
              },
        // Default = transparent + white text-shadow halo so the label
        // inherits the canvas / parent-container background colour
        // (e.g. light-blue tinted SoftwareSystem boundaries on C4
        // views). User can opt for an explicit colour via the toolbar
        // swatch (see `setRelationLabelBgColorOnCanvas`); when set,
        // we drop the text-shadow because the solid background
        // already provides contrast.
        // CRUD-badge wint van álles — vol-gekleurde rondje/pill,
        // borderRadius 999 om RoutedEdge's default-radius (3) te
        // overrulen, geen text-shadow want kleurcontrast doet 't werk.
        labelBgStyle: crudBadge
          ? {
              backgroundColor: crudBadge.color,
              borderRadius: 999,
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }
          : handleState?.labelBgColor
            ? { backgroundColor: handleState.labelBgColor }
            : {
                backgroundColor: "transparent",
                textShadow: "0 0 2px rgba(255,255,255,0.9), 0 0 4px rgba(255,255,255,0.7)",
              },
        labelBgPadding: crudBadge
          ? // Symmetrische padding zodat éen-letter-modes (C/R/U/D)
            // een (bijna-)cirkel vormen; "R/W" wordt automatisch een
            // pil door de bredere tekst.
            [8, 4]
          : influenceWeight
            ? [12, 6]
            : [4, 2],
      };
      if (handleState?.sourceHandle && !sourceResolved.reAnchored)
        baseEdge.sourceHandle = handleState.sourceHandle;
      if (handleState?.targetHandle && !targetResolved.reAnchored)
        baseEdge.targetHandle = handleState.targetHandle;
      return applyRelationStyle(baseEdge, r.typeId, r.attributes, r.stereotypes, diagram?.viewKind);
    }

    return {
      nodes: computedNodes,
      edges: computedEdges,
      // Exposed so handlers (selection, drag) can map a node-instance id
      // back to the underlying concept id.
      instanceToConcept,
      // Aantal viewState-entries op dit diagram met `hidden: true` én
      // een position (concept-instances). De totaal-teller-badge in
      // de JSX gebruikt deze waarde.
      hiddenTotalOnDiagram,
    };
  }, [revision, activeDiagramId, lang, conceptsCount]);

  // React Flow drives node-position updates through `onNodesChange`. We mirror
  // the derived nodes into local state so live-drag is smooth, and re-sync
  // whenever the underlying store changes. Selection follows the UI store's
  // `selectedConceptId` (set by canvas clicks AND by clicks in the Concept
  // Explorer), so picking a concept anywhere highlights it on the canvas.
  const [localNodes, setLocalNodes] = useState<Node[]>(derived.nodes);
  // Mirror the latest localNodes into a ref so async/event callbacks
  // (drag completion, intersection testing) can read fresh node bounds
  // without going through React's deps churn.
  const localNodesRef = useRef<Node[]>(localNodes);
  localNodesRef.current = localNodes;
  // Stabiele sleutel voor "welke nodes staan er nu op canvas" — verandert
  // ALLEEN als de SET wijzigt (toevoegen/verwijderen), niet bij drag/resize.
  // Voedt `EdgeGeometryRefresher`'s handle-measurement-race-fix hieronder.
  const nodeIdsKey = useMemo(
    () =>
      localNodes
        .map((n) => n.id)
        .sort()
        .join(","),
    [localNodes],
  );

  // Focus-model: cursor-cel + scope. Subscribe op de twee velden die
  // we voor render nodig hebben; verandering triggert re-render zodat
  // de cursor mee-schuift en de ring meerendeert.
  const canvasCursor = useKeymap((s) => s.canvasCursor);
  const canvasScope = useKeymap((s) => s.scope);
  // Zoom-level tracken zodat de cursor-cel constant ~80 screen-px is
  // ongeacht zoom-level (in flow-coords = base / zoom). useViewport()
  // van React Flow werkt alleen IN <ReactFlow> z'n descendants, niet
  // in Canvas zelf die <ReactFlow> rendert — daarom een lokale state
  // die we bijhouden via de ReactFlow `onMove`-callback.
  const [viewportZoom, setViewportZoom] = useState(1);
  // Cel-grootte in flow-coords = basis / zoom zodat 't visueel een
  // constante screen-grootte heeft. Geen auto-shrink: bij meerdere
  // items in cel zoomt de gebruiker zelf in op het canvas (+/- toets
  // of scroll-wheel) zodat de items uit elkaar trekken.
  const effectiveCellSize = CANVAS_CURSOR_CELL_SIZE / Math.max(viewportZoom, 0.01);

  // React-19 + React-Flow: snel scroll-wiel-zoomen vuurt onMove tientallen
  // keren per frame. setViewportZoom op élk event herrendert de cursor-cel-node
  // (effectiveCellSize) en desynct onder React 19's renderer de edges (lijnen
  // "vliegen" weg, herstellen bij langzaam scrollen). Coalesce de zoom-update
  // naar ~1× per frame zodat de re-render-rate begrensd is. #46.
  const zoomRafRef = useRef<number | null>(null);
  const pendingZoomRef = useRef(1);
  const scheduleViewportZoom = useCallback((zoom: number) => {
    pendingZoomRef.current = zoom;
    if (zoomRafRef.current !== null) return;
    zoomRafRef.current = requestAnimationFrame(() => {
      zoomRafRef.current = null;
      setViewportZoom(pendingZoomRef.current);
    });
  }, []);
  useEffect(
    () => () => {
      if (zoomRafRef.current !== null) cancelAnimationFrame(zoomRafRef.current);
    },
    [],
  );

  // Persist de viewport (pan+zoom) per diagram, gedebounced (300ms na de
  // laatste pan/zoom/fit) zodat continu scroll-wiel-zoomen niet op elke
  // frame naar localStorage schrijft. Zie `writePersistedViewport` hierboven
  // voor de reden (fitView-timing-race bij cold page-load).
  const viewportPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleViewportPersist = useCallback(
    (viewport: Viewport) => {
      if (!workspaceId || !activeDiagramId) return;
      if (viewportPersistTimerRef.current !== null) clearTimeout(viewportPersistTimerRef.current);
      viewportPersistTimerRef.current = setTimeout(() => {
        viewportPersistTimerRef.current = null;
        writePersistedViewport(workspaceId, activeDiagramId, viewport);
      }, 300);
    },
    [workspaceId, activeDiagramId],
  );
  useEffect(
    () => () => {
      if (viewportPersistTimerRef.current !== null) clearTimeout(viewportPersistTimerRef.current);
    },
    [],
  );

  // IDs of every node currently in a multi-selection (rubber-band or
  // Shift+click). Tracked as a ref so mutations are synchronous and never
  // trigger a separate render-cycle that can race with the useEffect below.
  const selectedNodeIdsRef = useRef<Set<string>>(new Set());

  // Modifier-state-tracking ref. RF's `onSelectionChange` krijgt geen
  // event-object (geen modifier-info), maar voor de plain-vs-additive-
  // beslissing (Excel-stijl single-click clears multi, Cmd+klik adds)
  // moet de handler weten of Cmd/Ctrl/Shift/Alt op het moment van
  // klikken werd ingedrukt. We tracken 't via window-niveau keydown/
  // keyup-listeners. Op `blur` resetten naar `false` zodat alt-tab
  // tijdens een ingedrukte Cmd geen stale `true` achterlaat.
  const isModifierHeldRef = useRef(false);
  /**
   * Zet door het sleepkader, gelezen door `onSelectionChange`: die selectie
   * heeft geen maatstaf. Een vlag en niet de modifier-toets, want die zegt
   * alleen iets over het moment van de muisklik en niet over hoe de selectie
   * tot stand kwam.
   */
  const bandSelectionRef = useRef(false);
  useEffect(() => {
    const syncFromEvent = (e: KeyboardEvent | MouseEvent) => {
      isModifierHeldRef.current = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    };
    const resetOnBlur = () => {
      isModifierHeldRef.current = false;
    };
    window.addEventListener("keydown", syncFromEvent as EventListener, { capture: true });
    window.addEventListener("keyup", syncFromEvent as EventListener, { capture: true });
    // Mouse-events brengen ook modifier-state mee. Vooral mousedown op
    // canvas wordt vlak vóór RF's selection-update gevuurd.
    window.addEventListener("mousedown", syncFromEvent as EventListener, { capture: true });
    window.addEventListener("blur", resetOnBlur);
    return () => {
      window.removeEventListener("keydown", syncFromEvent as EventListener, { capture: true });
      window.removeEventListener("keyup", syncFromEvent as EventListener, { capture: true });
      window.removeEventListener("mousedown", syncFromEvent as EventListener, { capture: true });
      window.removeEventListener("blur", resetOnBlur);
    };
  }, []);

  // Custom rubber-band selection state. React Flow's built-in SelectionMode
  // does not correctly compute hit positions for nested nodes (it uses the
  // stored relative `position` rather than the absolute canvas position).
  // We replace it with our own: track a selection box in screen coordinates,
  // then on mouseup check each node's getBoundingClientRect so that ALL FOUR
  // corners must lie within the box (full containment, not partial touch).
  const selectionStartRef = useRef<{ x: number; y: number; additive: boolean } | null>(null);
  /** `onPaneClick`, voor het sleepkader (zie daar). */
  const paneClickRef = useRef<() => void>(() => {});
  const wrapperRectRef = useRef<DOMRect | null>(null);
  const [selectionBox, setSelectionBox] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);

  // React Flow instance (captured via `onInit`). Provides
  // `screenToFlowPosition` so a drop event's pointer coordinates can be
  // converted to canvas coordinates regardless of pan / zoom.
  const rfInstance = useRef<ReactFlowInstance | null>(null);

  // Anti-flikker bij branch-wissel: de Canvas remount (key={branchId}), en
  // React Flow toont één frame op de default-viewport (0,0 / zoom 1) vóórdat
  // de `fitView`-prop landt → zichtbaar geflikker. We houden het canvas
  // VERBORGEN (visibility:hidden, behoudt meting zodat fitView kan rekenen)
  // tot `onInit` + 2 frames → dan is de fit toegepast en onthullen we 't.
  // ALLEEN bij een remount (branch-wissel); de eerste mount na page-load (hard
  // refresh) toont meteen — anders zie je daar onnodig een blank. Een safety-
  // net-timeout zorgt dat 't nooit verborgen blijft hangen.
  const [canvasReady, setCanvasReady] = useState<boolean>(() => {
    if (!canvasEverMounted) {
      canvasEverMounted = true;
      return true; // eerste mount na page-load → meteen tonen
    }
    return false; // remount (branch-wissel) → verborgen tot fit
  });
  useEffect(() => {
    if (canvasReady) return;
    const t = setTimeout(() => setCanvasReady(true), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto fit-view bij elke diagram-switch. React Flow's `fitView`-prop
  // doet dat alléén bij de eerste mount — switching naar een andere
  // diagram-tab houdt de oude viewport, wat tot een lege canvas leidt
  // als de nieuwe content op andere coördinaten staat (bv. negatieve
  // X). We refitten daarom expliciet wanneer de active diagram-id
  // verandert.
  //
  // MAAR niet op de eerste run (mount/remount): daar fit de `fitView`-prop
  // al instant. Een tweede, geanimeerde fit erbovenop gaf bij branch-wissel
  // (Canvas remount via key={branchId}) een zichtbare "verspring-en-terug".
  // Skip 'm dus op run #1; alleen latere tab-switches binnen dezelfde mount
  // krijgen de expliciete refit.
  const didMountFitRef = useRef(false);
  useEffect(() => {
    if (!activeDiagramId) return;
    if (!didMountFitRef.current) {
      didMountFitRef.current = true;
      return;
    }
    const instance = rfInstance.current;
    if (!instance) return;
    // Microtask zodat de nodes uit de zustand-store eerst zijn verwerkt
    // door React Flow voordat we fitten.
    const id = setTimeout(() => {
      try {
        instance.fitView({ padding: 0.1, duration: 250 });
      } catch (err) {
        console.error("[canvas] fit-view on diagram-switch failed", err);
      }
    }, 50);
    return () => clearTimeout(id);
  }, [activeDiagramId]);

  // "Spring naar"-handler — gebruiker kiest "Toon op canvas" in
  // GridPane's context-menu, dat zet `useUi.viewportFocusRequest` met
  // een nieuwe nonce. Wij centreren de viewport op het concept (z'n
  // canonical node) of, als 't een relation is, fitView op source +
  // target. Bij multi-instance concepten (zelfde concept-id meerdere
  // keer op 't diagram) fitView over alle voorkomens zodat ze allemaal
  // in beeld komen. Huidige zoom-niveau wordt behouden voor single-
  // node; fitView past zoom aan voor multi-node om alles te zien.
  useEffect(() => {
    if (!viewportFocusRequest) return;
    const instance = rfInstance.current;
    if (!instance) return;
    const { id } = viewportFocusRequest;

    const node = instance.getNode(id);
    if (node) {
      const measured = (node as { measured?: { width?: number; height?: number } }).measured ?? {};
      const w = measured.width ?? (node as { width?: number }).width ?? 100;
      const h = measured.height ?? (node as { height?: number }).height ?? 50;
      instance.setCenter(node.position.x + w / 2, node.position.y + h / 2, {
        zoom: instance.getZoom(),
        duration: 400,
      });
      return;
    }

    // Geen direct-id-match → wellicht concept met meerdere instances.
    const instanceIds: string[] = [];
    for (const [instId, conceptId] of derived.instanceToConcept) {
      if (conceptId === id) instanceIds.push(instId);
    }
    if (instanceIds.length > 0) {
      instance.fitView({
        nodes: instanceIds.map((nid) => ({ id: nid })),
        padding: 0.2,
        duration: 400,
      });
      return;
    }

    // Relation: zoek edge + fit op source/target.
    const edge = instance.getEdge(id);
    if (edge) {
      const src = instance.getNode(edge.source);
      const tgt = instance.getNode(edge.target);
      if (src && tgt) {
        instance.fitView({
          nodes: [{ id: src.id }, { id: tgt.id }],
          padding: 0.3,
          duration: 400,
        });
      }
    }
  }, [viewportFocusRequest, derived.instanceToConcept]);

  // Track the latest mouse position so onConnect can snapshot where the
  // user dropped the connection endpoint (used to compute targetAngle).
  const latestMouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      latestMouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  useEffect(() => {
    // Two highlight modes:
    // - If `selectedInstanceId` is set (canvas-driven selection), only
    //   that exact instance is highlighted — so clicking one copy of a
    //   repeated concept doesn't light up the others.
    // - Otherwise, fall back to `selectedConceptId` (Explorer-driven) and
    //   highlight every instance of that concept on the diagram.
    // Additionally, keep any nodes that are part of the current
    // multi-selection (rubber-band / alignment group) highlighted.
    //
    // Side-effect: ook `selectedNodeIdsRef` bijwerken zodat een
    // explorer-selectie van een multi-instance concept een echte
    // multi-selection oplevert (Delete-toets, toolbar-bulk-actions
    // werken dan op alle voorkomens). Zonder deze sync blijft de set
    // leeg en zou de gebruiker visueel selectie zien maar effectief
    // niets multi-actie kunnen doen.
    const matched = new Set<string>();
    // Multi-select-set vanuit external panels (GridPane Shift/Cmd-klik).
    // We breiden de canvas-eigen multi-set uit met deze externe ids
    // zodat de visuele highlight álle gemarkeerde concepten oplicht.
    const externalMultiConceptIds = new Set(selectedConceptIds);
    // Multi-instance-refactor (2026-05-26) Bug-2-fix: bepaal welke
    // concept-ids al via specifieke instance-keys in de canvas-multi-set
    // zitten. Voor die concept-ids mag externalMatch NIET fire'n — anders
    // lichten alle instances van het concept op terwijl de gebruiker
    // alleen die ene instance heeft geselecteerd via cmd-klik. Voor
    // concepten DIE NIET in de ref zitten (= bron is GridPane / externe
    // panel die per-concept-id selecteert) blijft externalMatch werken.
    const conceptIdsRepresentedInCanvasMulti = new Set<string>();
    for (const id of selectedNodeIdsRef.current) {
      const cid = derived.instanceToConcept.get(id);
      if (cid) conceptIdsRepresentedInCanvasMulti.add(cid);
    }
    // Single-select-modus = useUi.selectedConceptIds is leeg (een
    // select*-actie wist 'm atomair). In die modus is élke residual in
    // `selectedNodeIdsRef.current` STALE — bewaard van een eerdere
    // multi-selectie die door selectConcept/selectRelation is opgeheven.
    // Negeren we 'm niet, dan blijft de oude node visueel selected, ziet
    // RF meerdere geselecteerde nodes, firet onSelectionChange met de
    // union [oud, nieuw], pusht ui.setSelectedConceptIds met die union
    // terug, en spiegelt GridPane (en wij hier) accumuleren. Zie
    // regressie 2026-05-22 in HANDOFF.md.
    const isSingleSelectMode = selectedConceptIds.length === 0;
    setLocalNodes(
      derived.nodes.map((n) => {
        const conceptId = derived.instanceToConcept.get(n.id);
        const uiMatch = selectedInstanceId
          ? n.id === selectedInstanceId
          : conceptId === selectedConceptId;
        const multiMatch = !isSingleSelectMode && selectedNodeIdsRef.current.has(n.id);
        // externalMatch alleen wanneer er GEEN single-instance focus is.
        // Multi-instance bug-fix 2026-05-26: bij canvas-klik op één
        // instance zet onSelectionChange ook selectedConceptIds op
        // [conceptId]. Als we hier conceptId-match toepassen, lichten
        // ALLE instances van dat concept op (externalMatch klopt
        // workspace-breed, niet instance-precies). Door 'm te gaten
        // op !selectedInstanceId blijft externalMatch alleen werken
        // voor explicit-multi-select-flows (rubber-band, Cmd+klik in
        // Explorer) waar geen single-instance-focus actief is.
        // Bug-2-fix 2026-05-26 (multi-instance): suppress externalMatch
        // voor concept-ids die al via een specifieke instance in canvas-
        // multi-set zitten. Anders lichten ALLE instances op terwijl
        // gebruiker er maar één expliciet selecteerde via cmd-klik.
        const externalMatch =
          !selectedInstanceId && conceptId && !conceptIdsRepresentedInCanvasMulti.has(conceptId)
            ? externalMultiConceptIds.has(conceptId)
            : false;
        if (uiMatch) matched.add(n.id);
        const shouldBeSelected = uiMatch || multiMatch || externalMatch;
        // Expliciet selected:false zetten zodat een eerdere selectie
        // niet visueel blijft plakken nadat de gebruiker iets anders
        // selecteert (zelfde reden als hierboven, andere flow).
        return { ...n, selected: shouldBeSelected };
      }),
    );
    if (isSingleSelectMode) {
      // Single-select-modus: ref één-op-één synchroon met uiMatch. Voorheen
      // beperkt tot Explorer-pad (`selectedConceptId && !selectedInstanceId`)
      // maar dat miste de tabel-klik-flow, waar onSelectionChange direct
      // hierna selectedInstanceId zet, waardoor deze cleanup overgeslagen
      // werd en de ref bij de vólgende klik nog op de oude node stond.
      selectedNodeIdsRef.current = matched;
    } else if (selectedConceptId && !selectedInstanceId) {
      // Explorer-pad MET multi (zelden — bv. cmd-klik op een explorer-rij
      // terwijl canvas-multi al draait): vervang ref door matched zodat
      // alle instances van het gekozen concept onderdeel zijn van de
      // multi-set. Behoud van pre-bestaand gedrag.
      selectedNodeIdsRef.current = matched;
    }
  }, [
    derived.nodes,
    derived.instanceToConcept,
    selectedConceptId,
    selectedInstanceId,
    selectedConceptIds,
  ]);

  // Mirror derived edges into local state so React Flow can manage transient
  // state (drag-in-progress, selection-by-click). Handle ids now live in the
  // diagram view_state (see store.setRelationHandlesOnCanvas), so we no
  // longer need a separate Map. Selected edges get a higher zIndex so their
  // endpoint handles render on top of any overlapping edges.
  // Visual highlight applied to ANY selected edge — both single-select
  // (the primary) and additional multi-selected edges. We deliberately
  // do NOT override `stroke` (the line colour) so the user's per-edge
  // colour choice keeps showing. Highlight is communicated via
  //   - bumped strokeWidth (the line gets thicker)
  //   - drop-shadow filter (a blue glow halo around the line)
  //   - elevated zIndex (stays on top of crossing edges)
  // Pulled out as a helper so onEdgeClick / rubber-band can apply the
  // same styling immediately when toggling `.selected`.
  const applySelectedStyle = useCallback((edge: Edge): Edge => {
    const baseWidth = typeof edge.style?.strokeWidth === "number" ? edge.style.strokeWidth : 1.4;
    return {
      ...edge,
      zIndex: 6000,
      style: {
        ...edge.style,
        // Subtle stroke bump only — no `filter: drop-shadow` because that
        // promotes the SVG to its own composite layer and triggers
        // layout/paint thrash when waypoint/attachment-handle drags
        // bump the workspace revision at 60Hz. Visible-enough on its
        // own; the orange/blue endpoint dots already mark "selected".
        strokeWidth: baseWidth + 0.8,
      },
    };
  }, []);
  // Remove the selection-highlight styling so the edge falls back to
  // its base look. We rebuild from `derived.edges` (which holds the
  // unmutated upstream style) keyed by id; this avoids accumulated
  // mutations from earlier select/deselect cycles. If the edge isn't
  // in derived (rare, e.g. just after a reconnect), we just clear the
  // highlight fields manually.
  const derivedEdgesById = useMemo(() => {
    const m = new Map<string, Edge>();
    for (const e of derived.edges) m.set(e.id, e);
    return m;
  }, [derived.edges]);
  const stripSelectedStyle = useCallback(
    (edge: Edge): Edge => {
      const base = derivedEdgesById.get(edge.id);
      if (base) {
        // Preserve the in-flight selected flag the caller just set.
        return { ...base, selected: edge.selected ?? false };
      }
      // No upstream entry — strip only the highlight fields and leave
      // the original stroke colour alone.
      const next: Edge = { ...edge };
      delete (next as { zIndex?: number }).zIndex;
      next.style = {
        ...edge.style,
        strokeWidth: undefined,
        filter: undefined,
      };
      return next;
    },
    [derivedEdgesById],
  );

  const buildEdges = useCallback(
    (edges: Edge[], selectedId: string | null): Edge[] =>
      edges.map((e) => {
        if (e.id === selectedId) return applySelectedStyle(e);
        return e;
      }),
    [applySelectedStyle],
  );

  const [localEdges, setLocalEdges] = useState<Edge[]>(() => {
    const enriched = buildEdges(derived.edges, selectedRelationId);
    return enriched.map((e) => ({ ...e, selected: e.id === selectedRelationId }));
  });
  // Stable ref voor keydown-listeners die de huidige edges willen
  // lezen zonder dat de listener bij elke localEdges-mutatie opnieuw
  // afkoppelt+aankoppelt. Spiegel-pattern van `localNodesRef`.
  const localEdgesRef = useRef<Edge[]>(localEdges);
  localEdgesRef.current = localEdges;

  // Re-derive edges when the upstream data changes. Selectie-logica:
  //  - Externe single-select via `selectedRelationId` (set door
  //    TextPane, GridPane, Explorer, hover-tooltip-click) is een
  //    eenduidig-intent: alleen DIE relatie is geselecteerd. We
  //    spiegelen de UI-store-key naar de canvas-edge.
  //  - Canvas-eigen multi-select (rubber-band, Cmd+klik) blijft
  //    behouden ZOLANG geen externe single-select is gezet. Wanneer
  //    de externe set wijzigt, vervangt 'ie de multi-select-state
  //    (zelfde gedrag als concepten — explorer-click wist multi-set).
  //
  // Vroegere bug: `prevSelected.get(e.id) ?? e.id === selectedRelationId`
  // gaf de prev-state ALTIJD voorrang (booleans zijn niet nullish), dus
  // GridPane-rij-klik kwam nooit door op canvas.
  useEffect(() => {
    setLocalEdges(() => {
      // Multi-instance-refactor (2026-05-26): visuele edge-highlight
      // matcht op ENTRY-KEY (e.id), niet op relation-id. selectedEdgeIds
      // bevat de specifieke instance-entry-keys die de gebruiker
      // selecteerde (per rubber-band / cmd-klik / context-menu). Voor
      // canonical entries is entry-key === relation-id; voor multi-
      // instance entries is entry-key een UUID. Highlight dus exact
      // de geselecteerde instance, niet alle instances van hetzelfde
      // domain-relation.
      //
      // Fallback voor single-select via selectedRelationId: ook hier
      // vergelijken we direct met e.id, want onEdgeContextMenu zet
      // selectedRelationId op de relation-id (= entry-key voor canonical)
      // en selectedEdgeIds op de entry-key (= UUID voor multi-instance).
      const externalMultiSet = new Set(selectedEdgeIds);
      const hasExternalMulti = externalMultiSet.size > 0;
      const hasExternalSingle = selectedRelationId !== null;
      return derived.edges.map((e) => {
        // Single-fallback (geen multi-set actief): vergelijk EERST op
        // domain-relation-id (data.relationId) — externe bronnen zoals
        // Explorer-dubbelklik of Relaties-paneel-klik pushen een
        // relation-id en willen ÁLLE instances oplichten. Fallback op
        // entry-key (e.id) voor pure-canvas-flows waar selectedRelationId
        // toevallig al een entry-key is.
        const edgeRelId = (e.data as { relationId?: unknown } | undefined)?.relationId;
        const matchesSingle =
          hasExternalSingle &&
          (e.id === selectedRelationId ||
            (typeof edgeRelId === "string" && edgeRelId === selectedRelationId));
        const isSelected = hasExternalMulti ? externalMultiSet.has(e.id) : matchesSingle;
        const withSelectedFlag = { ...e, selected: isSelected };
        return isSelected ? applySelectedStyle(withSelectedFlag) : withSelectedFlag;
      });
    });
  }, [derived.edges, selectedRelationId, selectedEdgeIds, applySelectedStyle]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setLocalNodes((curr) => applyNodeChanges(changes, curr));
      // Keep selectedNodeIdsRef in sync with React Flow's own select/deselect
      // events (emitted during rubber-band drag and Shift+click). Using a ref
      // ensures the mutation is synchronous — no state-batching race.
      for (const c of changes) {
        if (c.type !== "select") continue;
        if (c.selected) selectedNodeIdsRef.current.add(c.id);
        else selectedNodeIdsRef.current.delete(c.id);
      }
      if (!workspaceId || !branchId) {
        return;
      }
      for (const change of changes) {
        if (change.type === "dimensions" && change.dimensions && change.resizing === false) {
          // The user finished a resize-handle drag — persist size.
          useWorkspaceData.getState().resizeNodeOnCanvas(workspaceId, branchId, change.id, {
            width: change.dimensions.width,
            height: change.dimensions.height,
          });
          // Verschalen kan óók nesting opleveren: trek je een vakje om andere
          // objecten heen, dan is dat de beweging waarmee een groep ontstaat.
          // Zie `handleResizeForGrouping`.
          handleResizeForGrouping(change.id, change.dimensions, workspaceId, branchId);
        }
        if (change.type === "position" && change.position && !change.dragging) {
          // Re-detect nest/unnest when the drag finishes. We use the
          // canvas's current localNodes (post-drag) to compute absolute
          // positions and intersection-test against potential parents.
          const dragged = (() => {
            // localNodes is captured by reference; the closure runs at
            // dispatch time and reads the latest value via setLocalNodes.
            const live = localNodesRef.current.find((n) => n.id === change.id);
            return live ?? null;
          })();
          handlePositionChangeForNesting(
            change.id,
            change.position,
            dragged,
            workspaceId,
            branchId,
          );
        }
      }
    },
    [workspaceId, branchId],
  );

  // Waypoint-mee-verschuiven (knikpunt-fix). Waypoints zijn absolute canvas-
  // coördinaten in viewState; endpoints zijn node-verankerd en volgen vanzelf,
  // maar knikpunten niet. Wanneer BEIDE eindpunten van een relatie mee-versleept
  // worden, verschuift de hele lijn (incl. knikpunten) uniform mee. Bij drag-begin
  // snapshotten we de start-posities + de originele waypoints van de betrokken
  // relaties; tijdens de drag verschuiven we de gerenderde waypoints LIVE (via
  // setLocalEdges) zodat de knik meebeweegt; op drag-stop persisten we één keer.
  // Verschuift maar één eindpunt, dan blijft de knik staan (lijn herschikt).
  const dragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const dragEdgeWaypointsRef = useRef<Map<string, Array<{ x: number; y: number }>>>(new Map());
  const onNodeDragStart = useCallback((_e: MouseEvent | TouchEvent, _node: Node, nodes: Node[]) => {
    // Undo-batch openen: alle positie- + waypoint-mutaties van deze drag landen
    // in één undo-entry (endUserUndoBatch in onNodeDragStop via microtask).
    useWorkspaceData.getState().beginUserUndoBatch();
    const snap = new Map<string, { x: number; y: number }>();
    for (const n of nodes) snap.set(n.id, { x: n.position.x, y: n.position.y });
    dragStartPositionsRef.current = snap;
    // Snapshot de originele waypoints van relaties waarvan beide eindpunten in de
    // versleepte set zitten, zodat we ze zonder drift live kunnen verschuiven.
    const movedSet = new Set(nodes.map((n) => n.id));
    const wpMap = new Map<string, Array<{ x: number; y: number }>>();
    const ws = useWorkspaceData.getState();
    const diagram = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;
    if (diagram) {
      for (const edge of localEdgesRef.current) {
        if (!movedSet.has(edge.source) || !movedSet.has(edge.target)) continue;
        const wps = diagram.viewState[edge.id]?.waypoints;
        if (wps && wps.length > 0)
          wpMap.set(
            edge.id,
            wps.map((p) => ({ x: p.x, y: p.y })),
          );
      }
    }
    dragEdgeWaypointsRef.current = wpMap;
  }, []);
  const onNodeDrag = useCallback((_e: MouseEvent | TouchEvent, node: Node, _nodes: Node[]) => {
    const wpMap = dragEdgeWaypointsRef.current;
    const start = dragStartPositionsRef.current.get(node.id);
    if (!start || wpMap.size === 0) return;
    const dx = node.position.x - start.x;
    const dy = node.position.y - start.y;
    setLocalEdges((prev) =>
      prev.map((e) => {
        const orig = wpMap.get(e.id);
        if (!orig) return e;
        return {
          ...e,
          data: { ...e.data, waypoints: orig.map((p) => ({ x: p.x + dx, y: p.y + dy })) },
        };
      }),
    );
  }, []);
  const onNodeDragStop = useCallback(
    (_e: MouseEvent | TouchEvent, node: Node, _nodes: Node[]) => {
      const start = dragStartPositionsRef.current.get(node.id);
      const wpMap = dragEdgeWaypointsRef.current;
      dragStartPositionsRef.current = new Map();
      dragEdgeWaypointsRef.current = new Map();
      const ws = useWorkspaceData.getState();
      // Waypoint-shifts persisten (indien beide eindpunten mee-versleept + delta).
      if (workspaceId && branchId && start && wpMap.size > 0) {
        const dx = node.position.x - start.x;
        const dy = node.position.y - start.y;
        if (dx !== 0 || dy !== 0) {
          for (const [edgeId, orig] of wpMap) {
            ws.setRelationWaypointsOnCanvas(
              workspaceId,
              branchId,
              edgeId,
              orig.map((p) => ({ x: p.x + dx, y: p.y + dy })),
            );
          }
        }
      }
      // Batch sluiten NA de trailing per-node onNodesChange-positie-persist. React-
      // Flow's drag-end draait synchroon in één tick (onNodesChange + drag-stop),
      // dus een microtask pakt zowel de node-moves als de waypoint-shifts mee. Altijd
      // sluiten (ook zonder waypoints) zodat een pure multi-node-move óók één entry is.
      queueMicrotask(() => useWorkspaceData.getState().endUserUndoBatch("Verplaats objecten"));
    },
    [workspaceId, branchId],
  );

  /**
   * On drag-stop, decide whether the moved node should:
   * - become NESTED inside another node (drop center inside another node's
   *   bounds), or
   * - become UNNESTED if it was previously inside a parent but its center
   *   is now outside that parent's bounds.
   *
   * Uses absolute coordinates throughout so the intersection check works
   * regardless of how many levels of containment there already are.
   */
  const handlePositionChangeForNesting = useCallback(
    (
      nodeId: string,
      newPosition: { x: number; y: number },
      _dragged: Node | null,
      workspaceIdParam: string,
      branchIdParam: string,
    ) => {
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;
      const ve = diagram.viewState[nodeId];
      if (!ve) return;

      // Synthetische super-node (Fase 2 vereenvoudiging): doet NIET mee aan
      // nest/unnest-detectie (het is geen echt concept en heeft geen
      // containment-semantiek). Direct als plain-move persisten zodat de
      // hit-test-heuristiek 'm nooit per ongeluk ergens in nest.
      if (ve.type === "supernode") {
        ws.moveConceptOnCanvas(workspaceIdParam, branchIdParam, nodeId, newPosition);
        return;
      }

      // Build a map of node-id → absolute position so we can hit-test
      // against ALL nodes (incl. parents that might themselves be nested).
      const nodes = localNodesRef.current;
      const absPositions = computeAbsolutePositions(nodes);

      // The dragged node's NEW absolute position. React Flow's
      // `change.position` is relative-to-parent if the node had a parent,
      // or absolute otherwise. We compute its new absolute by adding the
      // current parent's absolute (if any).
      const previousParent = ve.containerId ?? null;
      const previousParentAbs = previousParent ? absPositions.get(previousParent) : null;
      const draggedAbs =
        previousParent && previousParentAbs
          ? { x: previousParentAbs.x + newPosition.x, y: previousParentAbs.y + newPosition.y }
          : newPosition;

      // Use the rendered DOM rect when available so we hit-test against
      // the actual size; fall back to a default 140×60 box for nodes
      // that haven't measured yet.
      const draggedSize = readNodeSize(nodeId) ?? { width: 140, height: 60 };
      const center = {
        x: draggedAbs.x + draggedSize.width / 2,
        y: draggedAbs.y + draggedSize.height / 2,
      };

      // Find the smallest other node whose absolute bounds contain the
      // dragged node's center. Smallest = most-specific = the right
      // nesting target when there's overlap.
      //
      // INVARIANT: een container moet groter zijn dan z'n kind. Anders
      // krijg je inverted-nesting (een grote pill claimt child te zijn
      // van een kleinere binnen-shape) wat visueel klopt nergens en de
      // chevron op de "ouder" een ghost-kind laat tonen. We filteren
      // dus kandidaten waarvan de area niet GROTER is dan de dragged.
      const draggedArea = draggedSize.width * draggedSize.height;
      let candidate: { id: string; area: number } | null = null;
      for (const n of nodes) {
        if (n.id === nodeId) continue;
        // Don't allow nesting into our own descendant (would create a cycle).
        if (isDescendantOf(n.id, nodeId, nodes)) continue;
        const abs = absPositions.get(n.id);
        if (!abs) continue;
        const size = readNodeSize(n.id) ?? { width: 140, height: 60 };
        const targetArea = size.width * size.height;
        // Container moet strikt groter zijn dan kind.
        if (targetArea <= draggedArea) continue;
        if (
          center.x >= abs.x &&
          center.x <= abs.x + size.width &&
          center.y >= abs.y &&
          center.y <= abs.y + size.height
        ) {
          if (!candidate || targetArea < candidate.area) candidate = { id: n.id, area: targetArea };
        }
      }

      if (candidate && candidate.id !== previousParent) {
        // Drag → nest into a new (or different) parent. Position is
        // stored RELATIVE to that parent.
        const parentAbs = absPositions.get(candidate.id);
        if (!parentAbs) return;
        const relative = { x: draggedAbs.x - parentAbs.x, y: draggedAbs.y - parentAbs.y };
        // Herschikken (A → B): het concept van de oude container vóór de
        // verplaatsing vastleggen — de nest-vraag biedt straks aan de
        // relatie(s) die de oude nesting uitdrukte te verwijderen.
        const oldParentConceptId = previousParent
          ? (diagram.viewState[previousParent]?.ref ?? previousParent)
          : null;
        const undoTopBefore = useWorkspaceData.getState().userUndoStack.at(-1);
        const result = ws.nestOnDiagram(
          workspaceIdParam,
          branchIdParam,
          nodeId,
          candidate.id,
          relative,
        );
        // De undo-stap van de verplaatsing zelf: verwijderen we straks ook
        // de oude relatie, dan voegen we die samen tot één Cmd+Z. Zette
        // nestOnDiagram geen stap (vroege uitstap), dan is de bovenste een
        // eerdere, losse actie — die mag er niet in opgaan.
        const undoTopAfter = useWorkspaceData.getState().userUndoStack.at(-1);
        const nestUndoEntry = undoTopAfter !== undoTopBefore ? undoTopAfter : undefined;
        /** De relatie(s) die de oude nesting uitdrukte, gezien de stand ná de verplaatsing. */
        const oldNestingRelationIds = (
          childConceptId: string,
          parentConceptId: string,
        ): string[] => {
          const live = useWorkspaceData.getState().store.getDiagram(diagramId);
          if (!oldParentConceptId || !live) return [];
          return relationsRepresentedByOldNesting({
            relations: useWorkspaceData.getState().store.listRelations(),
            viewState: live.viewState,
            childConceptId,
            oldParentConceptId,
            newParentConceptId: parentConceptId,
          });
        };
        const removeOldNesting = async (relationIds: string[]) => {
          await useWorkspaceData
            .getState()
            .removeBatch(workspaceIdParam, branchIdParam, [], relationIds);
          if (nestUndoEntry) {
            useWorkspaceData
              .getState()
              .mergeUserUndoSince(nestUndoEntry, "Verplaats naar andere container");
          }
        };
        if (result.hiddenCount > 0) {
          // De nieuwe container had al een relatie met het kind (die is nu
          // verborgen); er komt dus geen typevraag. De oude nesting-relatie
          // zou dan stil blijven staan — vraag het hier apart.
          const childConceptId = ve.ref ?? nodeId;
          const parentConceptId = diagram.viewState[candidate.id]?.ref ?? candidate.id;
          const oldIds = oldNestingRelationIds(childConceptId, parentConceptId);
          if (oldIds.length > 0 && oldParentConceptId) {
            const oldConcept = ws.store.getConcept(oldParentConceptId);
            const oldName =
              (oldConcept ? resolveBilingual(oldConcept.name, i18n.language) : "") ||
              oldParentConceptId;
            if (
              window.confirm(
                t("canvas.nesting.removeOldConfirm", { count: oldIds.length, parent: oldName }),
              )
            ) {
              void removeOldNesting(oldIds);
            }
          }
        } else {
          // No existing relation between the two concepts → ask the user
          // whether they want to create one. Matrix-valid types filtered
          // by the active diagram's view-kind.
          const childConceptId = ve.ref ?? nodeId;
          const parentVe = diagram.viewState[candidate.id];
          const parentConceptId = parentVe?.ref ?? candidate.id;
          const childConcept = ws.store.getConcept(childConceptId);
          const parentConcept = ws.store.getConcept(parentConceptId);
          if (!childConcept || !parentConcept) return;
          // Drawing-primitieven zijn niet-semantisch — geen relatie-
          // suggestie wanneer een vrije rechthoek over een concept wordt
          // gesleept (of omgekeerd). Visuele nesting blijft wel staan.
          if (
            childConcept.typeId.startsWith("drawing.") ||
            parentConcept.typeId.startsWith("drawing.")
          ) {
            return;
          }
          // Sharp Attribute / Identifier zijn structurele onderdelen
          // van een Concept (containment via containerId is genoeg).
          // Een extra ArchiMate-relatie ertussen is semantisch onnodig
          // en zou alleen visuele ruis op het detailed-concept-diagram
          // geven — dus geen relation-prompt.
          if (
            childConcept.typeId === "sharp.Attribute" ||
            childConcept.typeId === "sharp.Identifier"
          ) {
            return;
          }
          // C4-views: nesting is visuele containment per C4-conventie,
          // niet een ArchiMate-relatie. Component-in-Container,
          // Container-in-System-Boundary, alles-in-Enterprise-Boundary —
          // geen prompt nodig. Op andere views (ArchiMate / Sharp)
          // blijft de prompt gewoon werken.
          if (diagram.viewKind?.startsWith("c4.")) {
            return;
          }
          // Sharp's procesniveaus: gebied ⊃ bedrijfsproces ⊃ subproces. Daar is
          // het antwoord op "welk relatietype?" altijd hetzelfde, dus de vraag
          // is er alleen wrijving. Zie `sharpNiveauNesting`.
          if (
            sharpNiveauNesting(
              workspaceIdParam,
              branchIdParam,
              childConcept,
              parentConcept,
              childConceptId,
              parentConceptId,
              t,
            )
          ) {
            return;
          }
          // Direction "child → parent" is the natural composition vector
          // (the part is the source, the whole is the target — see the
          // Composition relation convention used elsewhere).
          let validTypes = validRelationTypesBetween(childConcept.typeId, parentConcept.typeId);
          const vk = getRegistry().getViewKind(diagram.viewKind);
          const allow = vk?.included_relation_types;
          if (allow && allow.length > 0) {
            // Drawing-relaties staan boven de view-kind-restrictie
            // (zelfde rationale als drawing-elementen): ook op een
            // Capability Map moet een vrije lijn getekend kunnen worden.
            const allowSet = new Set([...allow, ...alwaysAllowedRelationTypeIds()]);
            validTypes = validTypes.filter((id) => allowSet.has(id));
          }
          // Herschikken: welke relaties drukte de oude nesting uit? Stand ná
          // de verplaatsing, zodat een andere instance die nog in de oude
          // container staat meetelt (dan blijft de relatie gewoon staan).
          const oldRelationIds = oldNestingRelationIds(childConceptId, parentConceptId);
          // Hand off to the modal; user picks a type or cancels.
          void useUi
            .getState()
            .promptNestRelationType(
              childConceptId,
              parentConceptId,
              validTypes,
              oldParentConceptId && oldRelationIds.length > 0
                ? { parentConceptId: oldParentConceptId, relationIds: oldRelationIds }
                : undefined,
            )
            .then(async ({ chosen, removeOld }) => {
              if (chosen) {
                const relation = await useWorkspaceData
                  .getState()
                  .connectConcepts(
                    workspaceIdParam,
                    branchIdParam,
                    childConceptId,
                    parentConceptId,
                    chosen,
                  );
                // Hide the freshly created relation on this diagram so the
                // visual nesting graphically represents it — the same rule
                // nestOnDiagram applies to pre-existing relations.
                if (relation) {
                  useWorkspaceData
                    .getState()
                    .hideRelationOnDiagram(workspaceIdParam, branchIdParam, relation.id);
                }
              }
              // Oude nesting-relatie(s) uit het model — alleen als de
              // modelleur het vinkje aan liet. Samen met de verplaatsing
              // (en een eventuele nieuwe relatie) één undo-stap.
              if (removeOld && oldRelationIds.length > 0) {
                await removeOldNesting(oldRelationIds);
              }
            });
        }
      } else if (!candidate && previousParent) {
        // Drag → unnest. Store new absolute position.
        const result = ws.unnestOnDiagram(workspaceIdParam, branchIdParam, nodeId, draggedAbs);
        const ids = result.previouslyHiddenRelationIds;
        if (ids.length === 1) {
          ws.unhideRelationOnDiagram(workspaceIdParam, branchIdParam, ids[0] as string);
        } else if (ids.length > 1) {
          void useUi
            .getState()
            .promptHiddenRelationPick(ids)
            .then((chosen) => {
              if (!chosen) return;
              useWorkspaceData
                .getState()
                .unhideRelationOnDiagram(workspaceIdParam, branchIdParam, chosen);
            });
        }
      } else {
        // Plain move — no change in containment. Persist the new
        // position via the regular move action. React Flow's
        // `change.position` is already in the correct frame for the
        // current container (relative when nested, absolute otherwise).
        ws.moveConceptOnCanvas(workspaceIdParam, branchIdParam, nodeId, newPosition);
      }
    },
    [],
  );

  /**
   * Nesting die ontstaat door **verschalen** in plaats van slepen.
   *
   * Bij Sharp is dat de natuurlijke volgorde: je legt eerst de stappen in
   * volgorde neer en trekt er dáárna een bedrijfsproces omheen — de groep is de
   * conclusie, niet het startpunt. Tot 2026-09-06 kende het canvas alleen de
   * omgekeerde beweging (sleep een object ín een ander), en deed het einde van
   * een verschaling niets dan de maat opslaan. Een vergroot vakje legde zich
   * dus als een deken over de rij en aan het model veranderde niets.
   *
   * Drie keuzes die hier bewust zo staan:
   *
   * 1. **Volledige omsluiting**, niet het midden zoals bij slepen. Je trekt er
   *    doelbewust omheen; een object dat half onder de rand ligt heb je niet
   *    bedoeld.
   * 2. **Alleen ouderloze objecten.** Wat al in een andere groep zit blijft
   *    daar — anders rooft een vergrote buurman stilletjes andermans kinderen.
   * 3. **De vraag stellen.** Een vlak over andere objecten trekken is ook een
   *    legitieme niet-groeperende handeling (een legenda, een achtergrond).
   */
  const handleResizeForGrouping = useCallback(
    (
      nodeId: string,
      maat: { width: number; height: number },
      workspaceIdParam: string,
      branchIdParam: string,
    ) => {
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;
      const groepVe = diagram.viewState[nodeId];
      if (!groepVe || groepVe.type === "supernode") return;
      const groepConceptId = groepVe.ref ?? nodeId;
      const groepConcept = ws.store.getConcept(groepConceptId);
      if (!groepConcept) return;

      const nodes = localNodesRef.current;
      const absPositions = computeAbsolutePositions(nodes);
      const groepAbs = absPositions.get(nodeId);
      if (!groepAbs) return;
      const groep = {
        x: groepAbs.x,
        y: groepAbs.y,
        rechts: groepAbs.x + maat.width,
        onder: groepAbs.y + maat.height,
      };

      // Eerst de omgekeerde beweging: maak je 'm kleiner en valt een kind
      // erbuiten, dan hoort het er niet meer bij. Zonder dit kun je een groep
      // wel maken maar niet meer ontbinden zonder te slepen. Geen vraag —
      // slepen-naar-buiten vraagt ook niets.
      for (const n of nodes) {
        const ve = diagram.viewState[n.id];
        if (!ve || ve.containerId !== nodeId) continue;
        const abs = absPositions.get(n.id);
        if (!abs) continue;
        const size = readNodeSize(n.id) ?? { width: 140, height: 60 };
        const nogBinnen =
          abs.x >= groep.x &&
          abs.y >= groep.y &&
          abs.x + size.width <= groep.rechts &&
          abs.y + size.height <= groep.onder;
        if (nogBinnen) continue;
        const uit = ws.unnestOnDiagram(workspaceIdParam, branchIdParam, n.id, abs);
        const ids = uit.previouslyHiddenRelationIds;
        if (ids.length === 1) {
          ws.unhideRelationOnDiagram(workspaceIdParam, branchIdParam, ids[0] as string);
        } else if (ids.length > 1) {
          void useUi
            .getState()
            .promptHiddenRelationPick(ids)
            .then((gekozen) => {
              if (!gekozen) return;
              useWorkspaceData
                .getState()
                .unhideRelationOnDiagram(workspaceIdParam, branchIdParam, gekozen);
            });
        }
      }

      const omsloten: string[] = [];
      for (const n of nodes) {
        if (n.id === nodeId) continue;
        const ve = diagram.viewState[n.id];
        if (!ve || ve.type === "supernode") continue;
        if (ve.containerId) continue; // zit al in een groep — afblijven
        const abs = absPositions.get(n.id);
        if (!abs) continue;
        const size = readNodeSize(n.id) ?? { width: 140, height: 60 };
        const past =
          abs.x >= groep.x &&
          abs.y >= groep.y &&
          abs.x + size.width <= groep.rechts &&
          abs.y + size.height <= groep.onder;
        if (past) omsloten.push(n.id);
      }
      if (omsloten.length === 0) return;

      // Per omsloten object bepalen wat er te kiezen valt. De matrix zegt wat
      // er tússen dít object en de groep mag; het viewpoint snoeit dat verder.
      // Waar het antwoord vastligt of niet van toepassing is, komt er geen
      // keuzelijst maar de reden.
      const vk = getRegistry().getViewKind(diagram.viewKind);
      const allow = vk?.included_relation_types;
      const items: GroupEncloseItem[] = [];
      for (const instanceId of omsloten) {
        const ve = diagram.viewState[instanceId];
        const conceptId = ve?.ref ?? instanceId;
        const concept = ws.store.getConcept(conceptId);
        if (!concept) continue;
        const elementType = getRegistry().getElementType(concept.typeId);
        const typeLabel = elementType ? resolveBilingual(elementType.label, lang) : concept.typeId;
        const naam = resolveConceptName(concept, (id) => ws.store.getConcept(id), lang);
        const geenRelatie =
          concept.typeId.startsWith("drawing.") ||
          groepConcept.typeId.startsWith("drawing.") ||
          concept.typeId === "sharp.Attribute" ||
          concept.typeId === "sharp.Identifier" ||
          (diagram.viewKind?.startsWith("c4.") ?? false);
        if (geenRelatie) {
          items.push({
            conceptId,
            naam,
            typeLabel,
            relationTypeIds: [],
            vastgelegd: t("groupEnclose.fixedNone"),
          });
          continue;
        }
        if (
          sharpNestVerdict(concept.stereotypes, groepConcept.stereotypes).soort !==
          "niet_van_toepassing"
        ) {
          items.push({
            conceptId,
            naam,
            typeLabel,
            relationTypeIds: [],
            vastgelegd: t("groupEnclose.fixedSharp"),
          });
          continue;
        }
        let types = validRelationTypesBetween(concept.typeId, groepConcept.typeId);
        if (allow && allow.length > 0) {
          const toegestaan = new Set([...allow, ...alwaysAllowedRelationTypeIds()]);
          types = types.filter((id) => toegestaan.has(id));
        }
        items.push({ conceptId, naam, typeLabel, relationTypeIds: types });
      }
      if (items.length === 0) return;

      void useUi
        .getState()
        .promptGroupOnEnclose(
          resolveConceptName(groepConcept, (id) => ws.store.getConcept(id), lang),
          items,
        )
        .then(async (keuze) => {
          if (!keuze) return; // niet groeperen — het blijft een vlak
          const nu = useWorkspaceData.getState();
          const diagramNu = nu.store.getDiagram(diagramId);
          if (!diagramNu) return;
          const absNu = computeAbsolutePositions(localNodesRef.current);
          const ouderAbs = absNu.get(nodeId);
          if (!ouderAbs) return;
          for (const instanceId of omsloten) {
            const abs = absNu.get(instanceId);
            if (!abs) continue;
            const relatief = { x: abs.x - ouderAbs.x, y: abs.y - ouderAbs.y };
            const resultaat = nu.nestOnDiagram(
              workspaceIdParam,
              branchIdParam,
              instanceId,
              nodeId,
              relatief,
            );
            if (resultaat.hiddenCount > 0) continue; // er was al een relatie
            const ve = diagramNu.viewState[instanceId];
            const conceptId = ve?.ref ?? instanceId;
            const concept = nu.store.getConcept(conceptId);
            if (!concept) continue;
            // Sharp's procesniveaus leggen de aggregatie zelf aan.
            if (
              sharpNiveauNesting(
                workspaceIdParam,
                branchIdParam,
                concept,
                groepConcept,
                conceptId,
                groepConceptId,
                t,
              )
            ) {
              continue;
            }
            const gekozen = keuze[conceptId];
            if (!gekozen) continue;
            const relation = await nu.connectConcepts(
              workspaceIdParam,
              branchIdParam,
              conceptId,
              groepConceptId,
              gekozen,
            );
            if (relation) {
              useWorkspaceData
                .getState()
                .hideRelationOnDiagram(workspaceIdParam, branchIdParam, relation.id);
            }
          }
        });
    },
    [lang, t],
  );

  // Custom rubber-band: native pointerdown/mousedown listeners at the
  // document level in capture phase, so they fire before any descendant
  // (React Flow / D3-zoom) handler. React Flow v12 uses pointerdown for its
  // gesture detection — if we don't stop *that*, the node-drag / pan
  // detection still kicks in alongside our rubber-band. We then filter by
  // checking the target is inside our wrapper and not interactive.
  const instanceToConcept = derived.instanceToConcept;
  useEffect(() => {
    const onDown = (e: MouseEvent | PointerEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (!wrapper.contains(target)) return;
      if (
        target.closest(".react-flow__node") ||
        target.closest(".react-flow__edge") ||
        target.closest(".react-flow__edges") ||
        target.closest(".react-flow__edge-label") ||
        target.closest(".react-flow__edgelabel-renderer") ||
        target.closest(".react-flow__handle") ||
        target.closest(".react-flow__controls") ||
        target.closest(".react-flow__panel") ||
        target.closest("[data-edge-attachment]") ||
        target.closest(".nodrag") ||
        target.closest("button") ||
        target.closest("input") ||
        target.closest("select") ||
        target.closest("textarea")
      )
        return;

      // We're going to consume this event for our own rubber-band. Stop
      // it from reaching React Flow / D3-zoom. We deliberately do NOT call
      // preventDefault on pointerdown — per the spec, that suppresses the
      // matching mouse events (mousedown/mousemove/mouseup), which we still
      // need for our move/up tracking. preventDefault on mousedown is safe
      // (only blocks text-selection, not subsequent events), so we do it
      // there.
      e.stopPropagation();
      (e as PointerEvent).stopImmediatePropagation?.();
      if (e.type === "mousedown") e.preventDefault();

      // If a rubber-band drag is already in progress (pointerdown already
      // fired and we're now seeing the matching mousedown, or vice-versa),
      // just swallow this duplicate event without restarting.
      if (selectionStartRef.current) return;

      wrapperRectRef.current = wrapperRef.current?.getBoundingClientRect() ?? null;
      const startX = e.clientX;
      const startY = e.clientY;
      // Capture modifier-state at drag-start so the lasso can ADD to
      // an existing multi-selection instead of replacing it.
      const additive = e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
      selectionStartRef.current = { x: startX, y: startY, additive };
      // Begon de klik op de lege achtergrond (en niet op een paneel dat in het
      // canvas zweeft)? Dan deselecteert een gewone klik, zie hieronder.
      const downOnPane = !!target.closest(".react-flow__pane");

      const onMove = (ev: PointerEvent | MouseEvent) => {
        setSelectionBox({
          x1: Math.min(startX, ev.clientX),
          y1: Math.min(startY, ev.clientY),
          x2: Math.max(startX, ev.clientX),
          y2: Math.max(startY, ev.clientY),
        });
      };

      const onUp = (ev: PointerEvent | MouseEvent) => {
        // Capture phase, see addEventListener calls below.
        window.removeEventListener("pointermove", onMove as EventListener, true);
        window.removeEventListener("pointerup", onUp as EventListener, true);
        window.removeEventListener("pointercancel", onUp as EventListener, true);
        window.removeEventListener("mousemove", onMove as EventListener, true);
        window.removeEventListener("mouseup", onUp as EventListener, true);
        // Snapshot the modifier-state captured at drag-start before we
        // null out the ref — the closure below uses it to decide
        // additive vs replace selection.
        const additive = !!selectionStartRef.current?.additive;
        selectionStartRef.current = null;

        const x1 = Math.min(startX, ev.clientX);
        const y1 = Math.min(startY, ev.clientY);
        const x2 = Math.max(startX, ev.clientX);
        const y2 = Math.max(startY, ev.clientY);
        setSelectionBox(null);

        // Een klein kader telt als een gewone klik. Die deselecteert op de
        // lege achtergrond — maar dat doen we hier zelf: React Flow roept
        // `onPaneClick` in selectiemodus (`selectionOnDrag`) pas bij het
        // loslaten aan als het het indrukken zelf zag, en dat indrukken
        // hebben we hierboven tegengehouden. Zonder dit bleef een selectie
        // na een klik ernaast gewoon staan. Met een wijzigtoets blijft de
        // selectie staan, net als bij een kader erbij slepen.
        if (x2 - x1 < 5 || y2 - y1 < 5) {
          if (downOnPane && !additive) paneClickRef.current();
          return;
        }

        // Real rubber-band release. Stop the up-event from reaching React
        // Flow on the underlying node, otherwise React Flow processes it
        // as a node click and blows away our just-computed multi-selection.
        ev.stopPropagation();
        (ev as PointerEvent).stopImmediatePropagation?.();
        // The browser fires a `click` event AFTER mouseup whenever
        // mousedown+mouseup land on the same element. If our pointerup is
        // released over a node, that node will receive a click event and
        // React Flow's onPaneClick / onSelectionChange will fire and wipe
        // our fresh selection. One-shot capture-phase click swallower:
        const swallowClick = (cev: MouseEvent) => {
          cev.stopPropagation();
          cev.stopImmediatePropagation();
          cev.preventDefault();
          window.removeEventListener("click", swallowClick, true);
        };
        window.addEventListener("click", swallowClick, true);
        // Belt-and-braces: also clear it after a tick in case no click fires.
        setTimeout(() => window.removeEventListener("click", swallowClick, true), 0);

        // Full-containment hit test (Gerald-spec 2026-05-27): de node's
        // hele bounding-rect moet binnen de lasso liggen — alle 4
        // randen aan de binnenkant van de selectie-rechthoek. Partial-
        // overlap (waar 1 of 2 randen buiten de lasso vallen) telt
        // bewust NIET — anders worden grote containers waarvan slechts
        // een hoek in de lasso valt ten onrechte geselecteerd.
        //
        // Tolerantie van TOL px op elke as absorbeert het verschil
        // tussen visueel-zichtbare node-rand en getBoundingClientRect
        // (CSS shadows, transparent borders, layer-container padding).
        // Zonder tolerantie zou een lasso die strak om de zichtbare
        // node staat alsnog falen omdat DOM-rect een paar pixels groter
        // is dan de visuele node.
        const TOL = 8;
        const selectedIds: string[] = [];
        const nodeEls = document.querySelectorAll<HTMLElement>(".react-flow__node");
        for (const el of nodeEls) {
          const id = el.getAttribute("data-id");
          if (!id) continue;
          const r = el.getBoundingClientRect();
          const fullyInside =
            r.left >= x1 - TOL && r.right <= x2 + TOL && r.top >= y1 - TOL && r.bottom <= y2 + TOL;
          if (fullyInside) {
            selectedIds.push(id);
          }
        }

        const selectedSet = new Set(selectedIds);
        selectedNodeIdsRef.current = selectedSet;
        setLocalNodes((curr) => curr.map((n) => ({ ...n, selected: selectedSet.has(n.id) })));

        // Rubber-band edge-select (Gerald-spec 2026-05-27): een edge is
        // geselecteerd wanneer z'n endpoint-COORDINATEN binnen de lasso
        // liggen — niet wanneer de bijbehorende objecten erin liggen.
        // Endpoint-coord = waar de lijn het kader raakt (= positie van
        // de oranje attachment-cirkel).
        //
        // Implementatie: we lezen de bounding-rect van de VISIBLE
        // edge-path (.react-flow__edge-path) i.p.v. het hele <g>-
        // element. De visible-path bbox geeft de tight bbox over alle
        // path-vertices (endpoints + waypoints) zonder de wide
        // interaction-path (~20px stroke) of label-padding mee te
        // tellen. Edge selecteert als die bbox volledig binnen lasso
        // ligt (= alle path-punten in lasso).
        //
        // Behouden fallback: ook "BEIDE endpoint-nodes binnen lasso"
        // selecteert nog steeds (= klassieke "lasso de objecten, edges
        // gaan automatisch mee").
        // Bereken eerst de set van edge-ids die in de band vallen — losse
        // pure functie zonder side-effects, zodat StrictMode-double-fire
        // van de setLocalEdges-updater hieronder GEEN duplicaten kan
        // creëren. Eerdere bug: push-inside-updater werd 2× uitgevoerd
        // in dev en gaf `selectedRelationIds = [id, id]`.
        const selectedEdgeIdSet = new Set<string>();
        const prevSelectedEdgeIds = new Set(
          localEdgesRef.current.filter((e) => e.selected).map((e) => e.id),
        );
        // Pre-fetch tight path-rects van alle gerenderde edges.
        const edgePathRectByDataId = new Map<string, DOMRect>();
        const edgeEls = document.querySelectorAll<SVGGElement>(".react-flow__edge");
        for (const el of edgeEls) {
          const id = el.getAttribute("data-id");
          if (!id) continue;
          const path = el.querySelector(".react-flow__edge-path") as SVGPathElement | null;
          if (path) edgePathRectByDataId.set(id, path.getBoundingClientRect());
        }
        for (const e of localEdgesRef.current) {
          // Route 1: beide endpoint-nodes geselecteerd (= gehele bronnen
          // in lasso). Behouden voor de "lasso de objecten"-workflow.
          const bothEndpointsInBand = selectedSet.has(e.source) && selectedSet.has(e.target);
          // Route 2: edge-path-bbox volledig binnen lasso (= endpoint-
          // coords + waypoints liggen allemaal in lasso).
          let edgePathInBand = false;
          const r = edgePathRectByDataId.get(e.id);
          if (r && r.width > 0 && r.height > 0) {
            edgePathInBand =
              r.left >= x1 - TOL &&
              r.right <= x2 + TOL &&
              r.top >= y1 - TOL &&
              r.bottom <= y2 + TOL;
          }
          const isInBand = bothEndpointsInBand || edgePathInBand;
          const nextSelected = additive ? prevSelectedEdgeIds.has(e.id) || isInBand : isInBand;
          if (nextSelected) selectedEdgeIdSet.add(e.id);
        }
        const selectedEdgeIds = [...selectedEdgeIdSet];
        // Multi-instance-refactor (2026-05-26): selectedEdgeIds bevat per-
        // instance entry-keys (UUID voor multi-instance, relation-id voor
        // canonical). Voor het useUi-multi-set willen we relation-ids zodat
        // AttributePanel / Relations-paneel correct kunnen highlight'en.
        // edge.data.relationId is geset door edge-derivation; fallback op
        // edge.id voor legacy/canonical waar die twee gelijk zijn.
        const selectedRelationIdsForUi: string[] = [];
        const seenRelIds = new Set<string>();
        for (const e of localEdgesRef.current) {
          if (!selectedEdgeIdSet.has(e.id)) continue;
          const rid = (e.data as { relationId?: unknown } | undefined)?.relationId;
          const resolved = typeof rid === "string" ? rid : e.id;
          if (seenRelIds.has(resolved)) continue;
          seenRelIds.add(resolved);
          selectedRelationIdsForUi.push(resolved);
        }

        setLocalEdges((curr) => {
          return curr.map((e) => {
            const nextSelected = selectedEdgeIdSet.has(e.id);
            const wasSelected = !!e.selected;
            const withFlag = { ...e, selected: nextSelected };
            if (nextSelected) return applySelectedStyle(withFlag);
            return wasSelected ? stripSelectedStyle(withFlag) : withFlag;
          });
        });

        if (selectedIds.length > 0) {
          const firstId = selectedIds[0]!;
          const conceptId = instanceToConcept.get(firstId) ?? firstId;
          const ui = useUi.getState();
          ui.selectInstance(firstId, conceptId);
          // Push álle concept-ids + relation-ids uit de band naar de
          // useUi-multi-sets zodat GridPane elke geselecteerde rij geel
          // kan oplichten. `selectInstance` wist de sets eerst (single-
          // select-semantiek); de set-calls hieronder vullen 'm dan
          // direct weer met de complete inhoud van de rubber-band.
          const bandConceptIds = selectedIds.map((id) => instanceToConcept.get(id) ?? id);
          ui.setSelectedConceptIds(bandConceptIds);
          // Een sleepkader kent geen klikvolgorde, dus is er geen maatstaf.
          // De werkbalk valt daardoor terug op de omhullende rechthoek bij
          // uitlijnen en op de grootste bij gelijke maat.
          ui.setSelectionAnchorConceptId(null);
          bandSelectionRef.current = true;
          selectedNodeIdsRef.current = new Set(selectedIds);
          ui.setSelectedRelationIds(selectedRelationIdsForUi);
          // Per-instance entry-keys voor de visuele edge-highlight
          // (multi-instance-refactor). Zonder dit valt de useEffect terug
          // op selectedRelationIds en pakt de canonical i.p.v. de
          // lassoed multi-instance entry.
          ui.setSelectedEdgeIds(selectedEdgeIds);
        } else if (selectedEdgeIds.length > 0) {
          // Edge-only rubber-band (zeldzaam — kader trekt over alleen
          // edges, geen nodes binnen tolerance). Single-focus + multi.
          const firstRelationId = selectedRelationIdsForUi[0]!;
          const ui = useUi.getState();
          ui.selectRelation(firstRelationId);
          ui.setSelectedRelationIds(selectedRelationIdsForUi);
          ui.setSelectedEdgeIds(selectedEdgeIds);
        } else {
          useUi.getState().selectConcept(null);
          selectedNodeIdsRef.current = new Set();
        }
      };

      // Capture phase fires window → document → target. Capturing on
      // window guarantees our listener runs BEFORE any React Flow handler
      // on the underlying node — otherwise React Flow on a node-target
      // pointerup can stopPropagation and we'd never see the release.
      window.addEventListener("pointermove", onMove as EventListener, true);
      window.addEventListener("pointerup", onUp as EventListener, true);
      window.addEventListener("pointercancel", onUp as EventListener, true);
      window.addEventListener("mousemove", onMove as EventListener, true);
      window.addEventListener("mouseup", onUp as EventListener, true);
    };

    // Capture phase at the document level fires before any descendant
    // listener can call stopPropagation (which React Flow / D3-zoom does).
    // We listen to BOTH pointerdown and mousedown — React Flow v12 uses
    // pointer events for its gesture detection, so blocking only mousedown
    // would leave node-grab / pan handlers active in parallel.
    document.addEventListener("pointerdown", onDown as EventListener, { capture: true });
    document.addEventListener("mousedown", onDown as EventListener, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", onDown as EventListener, { capture: true });
      document.removeEventListener("mousedown", onDown as EventListener, { capture: true });
    };
  }, [instanceToConcept]);

  const onConnect: OnConnect = useCallback(
    async (connection) => {
      if (!workspaceId || !branchId || !connection.source || !connection.target) {
        return;
      }
      // connection.source/target are canvas-instance ids; translate to
      // their underlying concept ids before talking to the workspace.
      let sourceConceptId = derived.instanceToConcept.get(connection.source) ?? connection.source;
      let targetConceptId = derived.instanceToConcept.get(connection.target) ?? connection.target;
      const store = useWorkspaceData.getState().store;
      let sourceConcept = store.getConcept(sourceConceptId);
      let targetConcept = store.getConcept(targetConceptId);
      if (!sourceConcept || !targetConcept) {
        return;
      }
      // Duplicaat-detectie (Stage A, besluit 2026-06-18): de modelleur mag
      // meerdere relaties tussen twee objecten leggen. We vragen ALLEEN wanneer
      // de nieuwe relatie een EXACT duplicaat zou zijn (zelfde type + lege naam +
      // zelfde richting) van een bestaande. Die check hangt op het gekozen type
      // (`chosen`) en gebeurt daarom verderop, ná de risk-reverse + type-keuze.
      // (Vervangt de bredere Feature-B-twee-laagse-popup.)
      // Risk-pack convention: relaties tussen Asset en risk/security-
      // stereotype-concepten lopen canoniek FROM risk/security-concept
      // TO asset (Countermeasure → Asset, Risk → Asset, Vulnerability →
      // Asset, ControlObjective → Asset, etc.). Wanneer de modelleur per
      // ongeluk vanaf de Asset tekent, draaien we de relatie automatisch
      // om — matrix-validatie + auto-stereotype-flow pakken daarna de
      // juiste richting. Geldt voor alle risk-pack concept-stereotypes
      // behalve risk.Asset zelf (die markeert juist 't target).
      /**
       * Interne active-structure-elementen: wat een Path verbindt, en dus wat
       * 'm realiseert. C260 somt ze op — actoren, applicatiecomponenten,
       * nodes, devices, systeemsoftware, apparatuur, faciliteiten, rollen en
       * collaboraties — en voegt de netwerken toe die 'm fysiek waarmaken.
       */
      const PATH_REALIZERS = new Set([
        "archimate.BusinessActor",
        "archimate.BusinessRole",
        "archimate.Role",
        "archimate.Collaboration",
        "archimate.ApplicationComponent",
        "archimate.Node",
        "archimate.Device",
        "archimate.SystemSoftware",
        "archimate.Equipment",
        "archimate.Facility",
        "archimate.CommunicationNetwork",
        "archimate.DistributionNetwork",
      ]);
      const RISK_NONASSET_STEREOTYPES = new Set([
        "risk.Countermeasure",
        "risk.Risk",
        "risk.Vulnerability",
        "risk.ThreatEvent",
        "risk.LossEvent",
        "risk.ThreatActor",
        "risk.ControlObjective",
        "risk.SecurityPrinciple",
        "risk.ClassifiedData",
        "risk.ClassifiedSecurityDomain",
        "risk.FrameworkControlObjective",
        "risk.StandardisedSecurityPrinciple",
      ]);
      const shouldReverseDirection =
        sourceConcept.stereotypes.includes("risk.Asset") &&
        targetConcept.stereotypes.some((s) => RISK_NONASSET_STEREOTYPES.has(s));
      if (shouldReverseDirection) {
        [sourceConceptId, targetConceptId] = [targetConceptId, sourceConceptId];
        [sourceConcept, targetConcept] = [targetConcept, sourceConcept];
      }

      // Een pad wordt gerealiseerd, niet geaggregeerd (C260 Appendix E.4). Wie
      // vanaf een Path naar een node of actor sleept, bedoelt vrijwel zeker de
      // verbinding tussen die twee — dus vragen we of we 'm de goede kant op
      // mogen leggen in plaats van 'm te weigeren met een spec-verwijzing.
      //
      // De vraag hangt niet aan de 3.x/4-weergave: die schakelaar zegt hoe je
      // 't ziet, niet wat mag. Zou de regel eraan hangen, dan was 'ie een
      // achterdeur — zet 'm op 3.x, teken de aggregatie, zet 'm terug.
      if (
        sourceConcept.typeId === "archimate.Path" &&
        PATH_REALIZERS.has(targetConcept.typeId) &&
        sourceConceptId &&
        targetConceptId
      ) {
        const padNaam =
          resolveBilingual(sourceConcept.name, lang) || t("canvas.pathRealization.fallbackPath");
        const elementNaam =
          resolveBilingual(targetConcept.name, lang) || t("canvas.pathRealization.fallbackElement");
        const akkoord = window.confirm(
          t("canvas.pathRealization.confirm", { element: elementNaam, path: padNaam }),
        );
        if (!akkoord) return;
        void useWorkspaceData
          .getState()
          .connectConcepts(
            workspaceId,
            branchId,
            targetConceptId,
            sourceConceptId,
            "archimate.Realization",
          )
          .catch(() => {
            useWorkspaceData.setState({
              lastError: t("canvas.pathRealization.failed"),
            });
          });
        return;
      }

      const matrixValid = validRelationTypesBetween(sourceConcept.typeId, targetConcept.typeId);
      // If the active diagram's view-kind narrows the allowed relation
      // types (`included_relation_types`), intersect with that allowlist.
      // No allowlist → matrix decides on its own. Drawing-relaties staan
      // boven die intersectie zodat ze op elke diagram-soort tekenbaar
      // zijn (zelfde rationale als drawing-elementen).
      const ws = useWorkspaceData.getState();
      const activeDiagram = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;
      const viewKind = activeDiagram
        ? getRegistry().getViewKind(activeDiagram.viewKind)
        : undefined;
      const viewAllow = viewKind?.included_relation_types;
      const allowSet = viewAllow
        ? new Set([...viewAllow, ...alwaysAllowedRelationTypeIds()])
        : null;
      const valid = allowSet ? matrixValid.filter((id) => allowSet.has(id)) : matrixValid;
      // View-kind kan een eigen default voorkeur opgeven (bv. Sharp
      // Contextual heeft default_relation_type=archimate.Association
      // zodat een drag tussen twee Concepts geen Serving oplevert maar
      // een neutrale Association). Valt terug op de globale preference
      // wanneer afwezig of niet matrix-valid.
      // C4-uitzondering: Flow is alleen tussen behavior-elementen
      // (ApplicationComponent ↔ ApplicationComponent etc.) zinvol.
      // Zodra een endpoint Node / Device / SystemSoftware (incl.
      // Container infrastructure) is, switchen we naar `c4.Uses` als
      // generieke runtime-relatie. Visueel rendert die identiek aan
      // Flow op container/component/dynamic/deployment-views (zie
      // edgeStyles + Canvas-lineColor).
      const c4UsesEligibleViews = new Set([
        "c4.container",
        "c4.component",
        "c4.dynamic",
        "c4.deployment",
      ]);
      const techEndpointTypes = new Set([
        "archimate.Node",
        "archimate.Device",
        "archimate.SystemSoftware",
      ]);
      const preferC4Uses =
        viewKind?.id !== undefined &&
        c4UsesEligibleViews.has(viewKind.id) &&
        (techEndpointTypes.has(sourceConcept.typeId) ||
          techEndpointTypes.has(targetConcept.typeId)) &&
        valid.includes("c4.Uses");
      // Raakt de lijn een tekenelement, dan is 'ie per definitie niet-
      // semantisch: een rechthoek "bedient" geen proces. Elke ArchiMate-relatie
      // die daar als standaard uit rolt beweert iets wat het model niet kan
      // bedoelen — en Serving rolde er tot 2026-09-02 gewoon uit. De vrije lijn
      // is hier de standaard; een pijlpunt is één rechtermuisklik verderop.
      const raaktTekening =
        sourceConcept.typeId.startsWith("drawing.") || targetConcept.typeId.startsWith("drawing.");
      const tekenDefault = raaktTekening && valid.includes("drawing.Line") ? "drawing.Line" : null;
      // Een proces **maakt** z'n resultaat; het triggert 'm niet. Sharp tekent
      // in figuur 3.14 een pijl van het proces naar het resultaat, maar in de
      // tekst gaat het over wat het proces oplevert — en dat is in ArchiMate
      // een realisatie. De trigger zit een stap verderop: het opgeleverde
      // resultaat kan een vólgend proces op gang brengen.
      //
      // Vandaar dat een lijn náár een «Procesresultaat» standaard een
      // Realization wordt. Het stereotype «Realiseert resultaat» zetten we
      // niet automatisch: dat is een uitspraak die de modelleur zelf doet.
      const resultaatDefault =
        (targetConcept.stereotypes ?? []).includes("sharp.ProcessResult") &&
        valid.includes("archimate.Realization")
          ? "archimate.Realization"
          : null;
      const viewKindDefault = preferC4Uses ? "c4.Uses" : viewKind?.default_relation_type;
      const chosen =
        tekenDefault ??
        resultaatDefault ??
        (viewKindDefault && valid.includes(viewKindDefault) ? viewKindDefault : null) ??
        RELATION_TYPE_PREFERENCE.find((id) => valid.includes(id)) ??
        valid[0] ??
        null;
      if (!chosen) {
        useWorkspaceData.setState({
          lastError: viewAllow
            ? t("canvas.relationNotAllowedByViewKind", {
                source: sourceConcept.typeId,
                target: targetConcept.typeId,
              })
            : t("canvas.noValidRelation", {
                source: sourceConcept.typeId,
                target: targetConcept.typeId,
              }),
        });
        return;
      }

      // Exact-duplicaat-vraag (Stage A, besluit 2026-06-18). Alleen vragen
      // wanneer de nieuwe relatie EXACT gelijk zou zijn aan een bestaande:
      // zelfde type (`chosen`), lege naam (nl + en leeg, = wat een verse
      // getekende relatie heeft) en zelfde richting (source→target, na de
      // risk-reverse hierboven). Andere richting telt nooit als duplicaat.
      // Geen exact duplicaat → val door en maak gewoon een tweede relatie aan.
      if (chosen && !chosen.startsWith("drawing.")) {
        // Identiteit-check via de gedeelde helper: type + (lege) naam +
        // richting (ongerichte Association = ongeordend → case 3).
        const exactDups = findDuplicateRelations(
          {
            typeId: chosen,
            label: {},
            sourceConceptId,
            targetConceptId,
            attributes: {},
          },
          store.listRelations(),
        );
        if (exactDups.length > 0) {
          const choice = await useUi.getState().promptExistingOrNewRelation(
            sourceConceptId,
            targetConceptId,
            exactDups.map((r) => r.id),
          );
          if (choice === null) return; // geannuleerd
          if (choice !== "new") {
            // Bestaande gebruiken → plaats een nieuwe instance ervan met de
            // geklikte instances als pins. Exact-dup = zelfde richting, dus
            // geen reverse-mapping nodig; we koppelen alleen de juiste
            // instance aan de juiste endpoint-zijde.
            const srcInst =
              derived.instanceToConcept.get(connection.source) === sourceConceptId
                ? connection.source
                : connection.target;
            const tgtInst = srcInst === connection.source ? connection.target : connection.source;
            const opts: { sourceInstanceId?: string; targetInstanceId?: string } = {};
            if (srcInst) opts.sourceInstanceId = srcInst;
            if (tgtInst) opts.targetInstanceId = tgtInst;
            useWorkspaceData
              .getState()
              .placeExistingRelationOnDiagram(workspaceId, branchId, choice, opts);
            return;
          }
          // choice === "new" → val door naar de gewone create hieronder.
        }
      }
      // Op C4-views: zodra het gekozen relatie-type binnen de
      // applies_to van «C4 Connection» valt, automatisch het
      // stereotype meesturen — protocol/format/synchronous krijgen
      // dan NTB/TBD/false-defaults via de connectConcepts-flow.
      // Modelleur kan 'm via 't AttributePanel weer uitzetten als
      // 't echt geen runtime-call is. Spec §9.1 ziet «C4 Connection»
      // als de canonical wrapper voor C4-runtime-relaties.
      const c4ConnectionApplicable =
        viewKind?.id?.startsWith("c4.") &&
        (chosen === "archimate.Serving" ||
          chosen === "archimate.Flow" ||
          chosen === "archimate.Triggering" ||
          chosen === "archimate.Access" ||
          chosen === "c4.Uses");
      // BMC-parallel: een Association op een bmc.*-view is per spec
      // §10.12 een bridging-realisatie tussen een ArchiMate/Sharp/C4-
      // element en een BMC-blok (directe Realization is voor de meeste
      // BMC-paren matrix-rejected — vandaar Association als drager met
      // «bmc.Realization»-stereotype). Auto-apply zodat modelleur niet
      // handmatig hoeft te taggen. Modelleur kan via AttributePanel
      // alsnog uitzetten als de Association puur semantisch-neutraal
      // bedoeld is (zelden).
      const bmcRealizationApplicable =
        viewKind?.id?.startsWith("bmc.") && chosen === "archimate.Association";
      // Risk-pack library/deployment-split (2026-05-21): wanneer source een
      // template-stereotype heeft uit Base.Risks / Base.Countermeasures,
      // auto-apply 't bijbehorende relation-stereotype zodat deployment-
      // attrs (owner, effectiveness, residual scoring, etc.) op de relatie
      // zichtbaar worden. Voorwaarde: chosen-relatie-type past binnen de
      // applies_to van het deployment-stereotype. Modelleur kan 'm via
      // AttributePanel uitzetten als dit een puur-categoriale link is.
      const RISK_DEPLOYMENT_STEREO: Record<
        string,
        { stereo: string; appliesTo: ReadonlyArray<string> }
      > = {
        "risk.Countermeasure": {
          stereo: "risk.CountermeasureDeployment",
          appliesTo: ["archimate.Association"],
        },
        "risk.Risk": {
          stereo: "risk.RiskAssessment",
          appliesTo: ["archimate.Association"],
        },
        "risk.Vulnerability": {
          stereo: "risk.VulnerabilityPresence",
          appliesTo: ["archimate.Association"],
        },
        "risk.ThreatEvent": {
          stereo: "risk.ThreatObservation",
          appliesTo: ["archimate.Access", "archimate.Association", "archimate.Triggering"],
        },
        "risk.LossEvent": {
          stereo: "risk.LossIncident",
          appliesTo: ["archimate.Access", "archimate.Association", "archimate.Triggering"],
        },
        "risk.ThreatActor": {
          stereo: "risk.ObservedAdversaryActivity",
          appliesTo: ["archimate.Association"],
        },
      };
      const sourceRiskMatch = sourceConcept.stereotypes
        .map((s) => RISK_DEPLOYMENT_STEREO[s])
        .find((m) => m !== undefined);
      const riskDeploymentApplicable = sourceRiskMatch?.appliesTo.includes(chosen);
      const autoStereotypes = c4ConnectionApplicable
        ? ["c4.Connection"]
        : bmcRealizationApplicable
          ? ["bmc.Realization"]
          : riskDeploymentApplicable && sourceRiskMatch
            ? [sourceRiskMatch.stereo]
            : undefined;
      // Risk-deployment-relaties zijn meestal Associations; Association
      // rendert per ArchiMate-default als ongerichte lijn. Voor het visueel
      // kunnen onderscheiden van "template → asset"-richting (vooral
      // relevant na een auto-reverse) zetten we `directed: true` zodat de
      // edge een halve pijl krijgt richting de asset.
      const extraAttributes =
        riskDeploymentApplicable && chosen === "archimate.Association"
          ? { directed: true }
          : undefined;
      void useWorkspaceData
        .getState()
        .connectConcepts(
          workspaceId,
          branchId,
          sourceConceptId,
          targetConceptId,
          chosen,
          autoStereotypes,
          extraAttributes,
        )
        .then((relation) => {
          // Pin de aanhechting van de nieuwe edge zodat React Flow 'm
          // niet auto-routet door 'n andere handle op de volgende render.
          // We zetten BEWUST geen `sourceHandle`/`targetHandle` meer in
          // de viewState — alleen de matching angle. Reden: zowel
          // handle-id (React Flow's interne handle-tracking) als
          // sourceAngle (RoutedEdge's `pointFromAngle`-routing) tegelijk
          // zetten leidt tot een paar-pixel-mismatch tussen waar React
          // Flow de handle-dot rendert en waar RoutedEdge de lijn doet
          // eindigen — visueel een zichtbare gap tussen lijn-uiteinde
          // en node-rand. Alleen-angle voorkomt die conflict.
          //
          // Ook remember WHICH instance the user picked, zodat de edge
          // op de juiste copy van de concept blijft op diagrammen met
          // multi-instance-placements.
          const ws = useWorkspaceData.getState();
          const diagramId = ws.activeDiagramId;
          if (diagramId) {
            const veRaw = ws.store.getDiagram(diagramId)?.viewState[relation.id];
            const ve: ViewElementState = veRaw ?? { type: "relation", ref: relation.id };
            const next: ViewElementState = { ...ve };
            if (connection.source && connection.source !== sourceConceptId) {
              next.sourceInstanceId = connection.source;
            }
            if (connection.target && connection.target !== targetConceptId) {
              next.targetInstanceId = connection.target;
            }
            // Self-loop visualisatie (2026-05-26): wanneer source-node ===
            // target-node, zet initieel 2 waypoints rechts van de node zodat
            // de edge als kleine arc rendert i.p.v. samen te vallen op de
            // zijkant. RoutedEdge respecteert waypoints; gebruiker kan ze
            // daarna verslepen voor andere positionering.
            if (connection.source && connection.target && connection.source === connection.target) {
              const diagram = ws.store.getDiagram(diagramId);
              const ve2 = diagram?.viewState[connection.source];
              if (ve2?.position) {
                const w = ve2.width ?? 140;
                const h = ve2.height ?? 60;
                const rightX = ve2.position.x + w;
                const centerY = ve2.position.y + h / 2;
                // Arc-waypoints: 60px naar rechts, 30px omhoog en omlaag
                // van het mid-vertical-punt. Eindigen aan de rechterzijde
                // zodat 't 'n nette loop wordt naar dezelfde zijde.
                next.waypoints = [
                  { x: rightX + 60, y: centerY - 30 },
                  { x: rightX + 60, y: centerY + 30 },
                ];
                next.sourceHandle = "right";
                next.targetHandle = "right";
              }
            }
            ws.store.setViewElementState(diagramId, relation.id, next);
          }

          // Compute angle-based attachment points so the edge can start/end
          // at an arbitrary border position rather than a fixed handle midpoint.
          // Source angle = derived from which handle side was dragged from.
          // Target angle = computed from where the mouse was at drop time.
          const sourceAngle = handleIdToAngle(connection.sourceHandle ?? undefined);
          const mouseFlow = rfInstance.current?.screenToFlowPosition(latestMouseRef.current);
          if (mouseFlow && connection.target) {
            const wsNow = useWorkspaceData.getState();
            const activeDiagId = wsNow.activeDiagramId;
            if (activeDiagId) {
              const diagram = wsNow.store.getDiagram(activeDiagId);
              if (diagram) {
                // Resolve target node's absolute position (handles nesting).
                const absOf = (key: string): { x: number; y: number } | null => {
                  const entry = diagram.viewState[key];
                  if (!entry?.position) return null;
                  let ax = entry.position.x;
                  let ay = entry.position.y;
                  let cur = entry;
                  while (cur.containerId) {
                    const parent = diagram.viewState[cur.containerId];
                    if (!parent?.position) break;
                    ax += parent.position.x;
                    ay += parent.position.y;
                    cur = parent;
                  }
                  return { x: ax, y: ay };
                };
                const targetVe = diagram.viewState[connection.target];
                const targetAbs = absOf(connection.target);
                if (targetAbs && targetVe) {
                  // Use measured size so the border maths are accurate even
                  // for auto-sized nodes that have no stored width/height.
                  const tw =
                    rfInstance.current?.getNode(connection.target)?.measured?.width ??
                    targetVe.width ??
                    140;
                  const th =
                    rfInstance.current?.getNode(connection.target)?.measured?.height ??
                    targetVe.height ??
                    60;
                  const targetAngle = nearestBorderAngle(
                    { x: targetAbs.x, y: targetAbs.y, w: tw, h: th },
                    mouseFlow.x,
                    mouseFlow.y,
                  );
                  wsNow.setRelationAnglesOnCanvas(
                    workspaceId,
                    branchId,
                    relation.id,
                    sourceAngle,
                    targetAngle,
                  );
                }
              }
            }
          }
        });
    },
    [workspaceId, branchId, derived.instanceToConcept, t],
  );

  // We take over Delete/Backspace entirely (deleteKeyCode={null} below)
  // because Chrome on macOS swallows Cmd+Backspace as "navigate back" before
  // React Flow's internal handler can see it. This window-level capture
  // listener calls preventDefault first thing, so the browser stops
  // intercepting and we get consistent behaviour on every platform.
  //
  // Behaviour:
  // - Without modifier  → hide selected nodes/edges on the active diagram.
  // - With Cmd or Ctrl  → confirm + hard-delete from the workspace.
  //
  // Edges that React Flow would cascade (incident to a deleted node) are
  // filtered out here too, so the concept's own undo entry handles their
  // restoration (otherwise undo would need multiple Cmd+Z presses).
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (inEditableField(e.target)) return;
      // Focus in het Onderwater-venster (tabel): die verwijdert z'n eigen
      // rij-selectie (met dezelfde diagram/model-semantiek). Anders pakt
      // deze window-capture-listener de toets eerst en haalt alleen het
      // ene canvas-geselecteerde object weg.
      if (e.target instanceof HTMLElement && e.target.closest('[data-scope="text-pane"]')) return;
      if (useSession.getState().readOnly) return;
      if (!workspaceId || !branchId) return;

      // Waypoint takes priority: if the user has clicked a bend-point and
      // then presses Delete, remove that single point from the relation's
      // waypoints rather than deleting the whole edge.
      const wp = useUi.getState().selectedWaypoint;
      if (wp) {
        e.preventDefault();
        e.stopPropagation();
        const ws = useWorkspaceData.getState();
        const diagramId = ws.activeDiagramId;
        if (!diagramId) return;
        const live = ws.store.getDiagram(diagramId)?.viewState[wp.edgeId]?.waypoints ?? [];
        const next = live.filter((_, i) => i !== wp.index);
        ws.setRelationWaypointsOnCanvas(
          workspaceId,
          branchId,
          wp.edgeId,
          next.length === 0 ? null : next,
        );
        useUi.getState().clearWaypointSelection();
        return;
      }

      const selectedNodes = localNodesRef.current.filter((n) => n.selected);
      const selectedEdges = localEdges.filter((e) => e.selected);
      if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

      // Stop the browser from doing anything (Chrome on macOS treats
      // Cmd+Backspace as "navigate back" by default).
      e.preventDefault();
      e.stopPropagation();

      // Hard delete: any of Cmd / Ctrl / Alt held with Delete. Alt is
      // the macOS-friendly modifier when Cmd / Ctrl get hijacked by
      // system shortcuts.
      const hard = e.metaKey || e.ctrlKey || e.altKey;
      const deletedNodeIds = new Set(selectedNodes.map((n) => n.id));
      const standaloneEdges = selectedEdges.filter(
        (edge) => !deletedNodeIds.has(edge.source) && !deletedNodeIds.has(edge.target),
      );

      const ws = useWorkspaceData.getState();
      if (hard) {
        const conceptIds = new Set<string>();
        for (const n of selectedNodes) {
          conceptIds.add(derived.instanceToConcept.get(n.id) ?? n.id);
        }
        // Async cascade-aware confirm; await before mutating. The
        // outer key-handler is `void`-fired so we can't await
        // directly, hence the IIFE.
        void (async () => {
          const langKey: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
          const ok = await confirmHardDelete({
            workspaceId,
            branchId,
            conceptIds: [...conceptIds],
            lang: langKey,
            standaloneEdgeCount: standaloneEdges.length,
            conceptName: (id) => ws.store.getConcept(id)?.name,
          });
          if (!ok) return;
          // Batched delete met één gecombineerde undo-entry. Cruciaal
          // voor de Cmd+Z-roundtrip wanneer een relatie + beide
          // endpoints samen verwijderd worden: per-concept-undo's
          // kunnen de relatie niet restoren omdat 't andere endpoint
          // inmiddels 'n nieuwe id heeft. Zie `removeBatch`-docstring.
          await ws.removeBatch(
            workspaceId,
            branchId,
            [...conceptIds],
            // Hard-delete = de DOMAIN-relation tombstonen (al z'n instances
            // verdwijnen daarmee tegelijk). edge.id is per-instance (UUID
            // bij multi-placement); de relation-id leeft in data.relationId.
            standaloneEdges.map((edge) => edgeRelationId(edge)),
          );
        })();
      } else {
        for (const edge of standaloneEdges) {
          ws.hideRelationOnDiagram(workspaceId, branchId, edge.id);
        }
        for (const n of selectedNodes) {
          ws.hideConceptOnDiagram(workspaceId, branchId, n.id);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [workspaceId, branchId, localEdges, derived.instanceToConcept]);

  // Pijl-toetsen → verschuif alle geselecteerde concept-instances
  // (Gerald-spec 2026-05-27).
  //   • ←/↑/→/↓ = 1px in die richting
  //   • Shift+pijl = 10px voor grovere navigatie
  //   • Skipt wanneer focus op een input/textarea/contentEditable
  //     (label-editing, attributenpaneel-velden, etc.)
  //   • Skipt wanneer er geen selectie is — geeft browser ruimte voor
  //     z'n eigen pijl-gedrag (scroll, focus-cycling)
  // Per beweging één moveConceptOnCanvas per node. Bij ingedrukt
  // houden vuurt de browser repeat-events ~30/s — de store-update is
  // in-memory en de D1-persist via schedulePersistDiagram is gedebounced,
  // dus geen flood-risk.
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onArrow = (e: KeyboardEvent) => {
      if (
        e.key !== "ArrowUp" &&
        e.key !== "ArrowDown" &&
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight"
      ) {
        return;
      }
      if (useSession.getState().readOnly) return;
      if (inEditableField(e.target)) return;
      // Modifiers Cmd/Ctrl/Alt = laat de browser de combo afhandelen
      // (Cmd+ArrowLeft = navigate back, Alt+ArrowDown = native menu's,
      // etc.). Alleen Shift mag voor de 10px-variant.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const selected = localNodesRef.current.filter((n) => n.selected);
      if (selected.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;
      for (const n of selected) {
        const ve = diagram.viewState[n.id];
        if (!ve?.position) continue;
        ws.moveConceptOnCanvas(workspaceId, branchId, n.id, {
          x: ve.position.x + dx,
          y: ve.position.y + dy,
        });
      }
    };
    window.addEventListener("keydown", onArrow, { capture: true });
    return () => window.removeEventListener("keydown", onArrow, { capture: true });
  }, [workspaceId, branchId]);

  // M6.5 accessibility bucket 1 — canvas-niveau shortcuts die op de
  // huidige selectie + React Flow-instance werken. Toets-keuzes na
  // browser-conflict-tests (Cmd+] = back, Cmd+Shift+] = nieuwe tab,
  // Cmd+0 = page-zoom-reset, Cmd++/- = page-zoom — Chrome wint allemaal
  // van preventDefault). Zoom in/uit op canvas blijft via scroll-wheel/
  // pinch (zie sectie "Modifier-conventies bij muis-acties" in
  // user-guide); F3 doet fit-view.
  //   Cmd/Ctrl+PgUp     → z-order forward
  //   Cmd/Ctrl+PgDown   → z-order backward
  //   Cmd/Ctrl+Home     → to front
  //   Cmd/Ctrl+End      → to back
  //   F3                → fit-to-view
  //   PgUp / PgDown     → container collapse-toggle (zonder Cmd; mét
  //                       Cmd is 't z-order)
  //   Escape            → clear selectie (canvas-pane = wel actief, dus
  //                       we deselecten zonder de gebruiker te dwingen
  //                       eerst op een lege pane te klikken)
  // Skipt editable targets zodat typen in attribuut-velden niet wordt
  // overruled.
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (inEditableField(e.target)) return;
      if (!workspaceId || !branchId) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      const meta = e.metaKey || e.ctrlKey;

      // F3 voor fit-view. Cmd+0 / Cmd++/- vallen af omdat Chrome's
      // page-zoom wint van preventDefault op de meeste platforms; voor
      // canvas-zoom blijven scroll-wheel + pinch de geadviseerde routes.
      if (e.key === "F3") {
        e.preventDefault();
        rfInstance.current?.fitView({ padding: 0.1, duration: 200 });
        return;
      }

      // Z-order — werkt op alle geselecteerde concept-instances.
      // Cmd+PgUp/PgDn = stap-voor-stap, Cmd+Home/End = helemaal voor/achter.
      // De plain `]`/`[`-bindings vallen af omdat Chrome ze claimt voor
      // browser-back/forward (Cmd+]) en Shift+Cmd+] voor nieuwe tab.
      if (meta && (e.key === "PageUp" || e.key === "PageDown")) {
        const selectedNodes = localNodesRef.current.filter((n) => n.selected);
        if (selectedNodes.length === 0) return;
        e.preventDefault();
        const ids = selectedNodes.map((n) => n.id);
        const action: "forward" | "backward" = e.key === "PageUp" ? "forward" : "backward";
        ws.changeZOrderOnCanvas(workspaceId, branchId, ids, action);
        return;
      }
      if (meta && (e.key === "Home" || e.key === "End")) {
        const selectedNodes = localNodesRef.current.filter((n) => n.selected);
        if (selectedNodes.length === 0) return;
        e.preventDefault();
        const ids = selectedNodes.map((n) => n.id);
        const action: "toFront" | "toBack" = e.key === "Home" ? "toFront" : "toBack";
        ws.changeZOrderOnCanvas(workspaceId, branchId, ids, action);
        return;
      }

      // Container collapse-toggle. Eerste geselecteerde node die ook
      // daadwerkelijk een container is wint. Bij multi-selectie is dat
      // zeldzaam zinnig, dus we beperken ons tot precies-één-selectie.
      // Plain PgUp/PgDn (zonder Cmd) — Chrome scrollt dan de pagina
      // maar binnen onze editor is er niets te scrollen, dus preventDefault
      // werkt veilig.
      if ((e.key === "PageUp" || e.key === "PageDown") && !meta && diagramId) {
        const selectedNodes = localNodesRef.current.filter((n) => n.selected);
        if (selectedNodes.length !== 1) return;
        const node = selectedNodes[0];
        if (!node) return;
        e.preventDefault();
        ws.toggleCollapseContainer(workspaceId, branchId, node.id);
        return;
      }
    };
    // Capture-fase: stelt zeker dat browsers / React Flow / nested
    // handlers ons niet pre-empten op shortcut-toetsen. Voor Escape
    // gebruiken modals óók capture en stoppen propagation, dus zij
    // sluiten zichzelf eerst — onze selectie-wisser komt pas op tweede
    // beurt aan bod (de listener is dezelfde maar de useEffect runt
    // alleen z'n cleanup wanneer een modal Escape consumeert).
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [workspaceId, branchId]);

  // Focus-navigatie binnen canvas-scope (M6.5-W11 slice B):
  //   Tab / Shift+Tab  → next/prev node in visuele rij-orde
  //   Arrow ↑↓←→        → geometrische buurman (90°-cone, fallback nearest)
  //   Enter             → focus → selectie (selectInstance op focused id)
  // Alleen actief wanneer `useKeymap.scope === "canvas"`. Buiten die
  // scope hands de Arrow/Tab terug aan andere componenten (browser
  // Tab in panelen, etc.). De focused node krijgt een dashed-ring via
  // ConceptNode's `isCanvasFocused`-prop-check.
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    // Bouw Focusable[] uit huidige localNodes + localEdges met absolute
    // centerposities. Walks containerId-keten omhoog voor genest items
    // (containers dragen hun kinderen relatief). Op sharp.concept-model
    // en sharp.detailed-concept-views krijgt elke relatie twee labels
    // (start = forward_name, end = reverse_name) i.p.v. één middle —
    // matcht de RoutedEdge-rendering (dualEndLabels). Andere view-kinds
    // krijgen één middle-label per relatie met niet-lege label-tekst.
    const buildFocusables = (): Focusable[] => {
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return [];
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return [];
      const viewKind = diagram.viewKind;
      const hasDualEndLabels =
        viewKind === "sharp.concept-model" || viewKind === "sharp.detailed-concept";
      const absPos = (key: string): { x: number; y: number } | null => {
        let ax = 0;
        let ay = 0;
        let cur = diagram.viewState[key];
        let safety = 0;
        while (cur && safety++ < 50) {
          if (!cur.position) return null;
          ax += cur.position.x;
          ay += cur.position.y;
          if (!cur.containerId) break;
          cur = diagram.viewState[cur.containerId];
        }
        return { x: ax, y: ay };
      };
      const out: Focusable[] = [];
      // 1) Nodes — area = w × h zodat in de lees-volgorde grote
      //    containers vóór hun kinderen komen.
      const nodeCenter = new Map<string, { cx: number; cy: number }>();
      for (const n of localNodesRef.current) {
        if (n.hidden) continue;
        const ve = diagram.viewState[n.id];
        if (!ve?.position) continue;
        const pos = absPos(n.id);
        if (!pos) continue;
        const w = ve.width ?? n.measured?.width ?? 140;
        const h = ve.height ?? n.measured?.height ?? 60;
        const cx = pos.x + w / 2;
        const cy = pos.y + h / 2;
        out.push({
          id: n.id,
          kind: "node",
          cx,
          cy,
          bboxW: w,
          bboxH: h,
          area: w * h,
        });
        nodeCenter.set(n.id, { cx, cy });
      }
      // 2) Edges + labels — gebruik localEdges (heeft source/target als
      //    node-instance-ids; relation.id = edge.id).
      //
      // Edge-positie = echte midpoint. Cel-detectie voor edges loopt
      // via line-rect-intersection (Liang-Barsky in focusablesInCell);
      // de cx/cy worden alleen gebruikt voor de Tab-sort, niet voor
      // hit-testing. Labels gebruiken midpoint + labelOffset zodat hun
      // detectie-positie matched met de visuele rendering.
      for (const edge of localEdgesRef.current) {
        if (edge.hidden) continue;
        const sCtr = nodeCenter.get(edge.source);
        const tCtr = nodeCenter.get(edge.target);
        if (!sCtr || !tCtr) continue;
        const dx = tCtr.cx - sCtr.cx;
        const dy = tCtr.cy - sCtr.cy;
        const edgeCx = (sCtr.cx + tCtr.cx) / 2;
        const edgeCy = (sCtr.cy + tCtr.cy) / 2;
        // Edge bbox = bounding box over source/target met een minimale
        // breedte/hoogte (12 px) zodat een verticale of horizontale
        // lijn nog raakbaar is door een cel-zoek.
        const edgeBboxW = Math.max(Math.abs(dx), 12);
        const edgeBboxH = Math.max(Math.abs(dy), 12);
        out.push({
          id: edge.id,
          kind: "edge",
          cx: edgeCx,
          cy: edgeCy,
          bboxW: edgeBboxW,
          bboxH: edgeBboxH,
          // Edges een kleine area zodat ze binnen een rij ná de echte
          // nodes (groter) en vóór labels (nog kleiner) komen.
          area: 50,
          relationId: edge.id,
          sourceNodeId: edge.source,
          targetNodeId: edge.target,
          sourceCx: sCtr.cx,
          sourceCy: sCtr.cy,
          targetCx: tCtr.cx,
          targetCy: tCtr.cy,
        });
        // Label-offsets uit viewState meenemen — zo komt de Focusable-
        // positie overeen met de visuele rendering, óók wanneer de
        // gebruiker een label verschoven heeft (anders vangt een
        // label op de oorspronkelijke midpoint per ongeluk een
        // ander item's cursor-cel).
        const edgeVe = diagram.viewState[edge.id];
        if (hasDualEndLabels) {
          // 25 % en 75 % langs de lijn — visueel "nabij source" en
          // "nabij target".
          const sx = sCtr.cx + dx * 0.25;
          const sy = sCtr.cy + dy * 0.25;
          const ex = sCtr.cx + dx * 0.75;
          const ey = sCtr.cy + dy * 0.75;
          const sOff = edgeVe?.sourceLabelOffset ?? { x: 0, y: 0 };
          const tOff = edgeVe?.targetLabelOffset ?? { x: 0, y: 0 };
          out.push({
            id: edgeLabelFocusId(edge.id, "start"),
            kind: "edge-label",
            cx: sx + sOff.x,
            cy: sy + sOff.y,
            bboxW: 60,
            bboxH: 20,
            area: 10,
            relationId: edgeRelationId(edge),
            labelPos: "start",
          });
          out.push({
            id: edgeLabelFocusId(edge.id, "end"),
            kind: "edge-label",
            cx: ex + tOff.x,
            cy: ey + tOff.y,
            bboxW: 60,
            bboxH: 20,
            area: 10,
            relationId: edgeRelationId(edge),
            labelPos: "end",
          });
        } else {
          const rel = ws.store.getRelation(edgeRelationId(edge));
          const hasLabel = !!(rel?.label?.nl || rel?.label?.en);
          if (hasLabel) {
            const off = edgeVe?.labelOffset ?? { x: 0, y: 0 };
            out.push({
              id: edgeLabelFocusId(edge.id, "middle"),
              kind: "edge-label",
              cx: edgeCx + off.x,
              cy: edgeCy + off.y,
              bboxW: 60,
              bboxH: 20,
              area: 10,
              relationId: edgeRelationId(edge),
              labelPos: "middle",
            });
          }
        }
      }
      return out;
    };
    const onKey = (e: KeyboardEvent) => {
      const km = useKeymap.getState();
      if (km.scope !== "canvas") return;
      if (inEditableField(e.target)) return;

      // Actuele effectieve cel-grootte (constant ~CELL_SIZE screen-px).
      const zoom = rfInstance.current?.getViewport().zoom ?? 1;
      const cellSize = CANVAS_CURSOR_CELL_SIZE / Math.max(zoom, 0.01);

      // Init cursor — alleen wanneer de gebruiker een toets drukt die
      // grid-navigatie START (plain Arrow of Tab, geen modifier). Andere
      // shortcuts (Alt+Arrow voor wrap-resize, F2 voor rename, etc.)
      // mogen door naar hun eigen handler zonder dat we de cursor
      // ongevraagd activeren.
      const isPlainArrowOrTab =
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        (e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight" ||
          e.key === "Tab");
      if (!km.canvasCursor && isPlainArrowOrTab) {
        const focusables = buildFocusables();
        if (focusables.length === 0) return;
        const first = focusables[0];
        if (!first) return;
        const snapped = snapToCell({ x: first.cx, y: first.cy }, cellSize);
        km.setCanvasCursor(snapped);
        e.preventDefault();
        return;
      }

      // Arrow → cursor schuift exact één cel in pijl-richting.
      if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !e.shiftKey &&
        (e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowRight")
      ) {
        const cursor = km.canvasCursor;
        if (!cursor) return;
        let next = { x: cursor.x, y: cursor.y };
        if (e.key === "ArrowRight") next = { x: cursor.x + cellSize, y: cursor.y };
        else if (e.key === "ArrowLeft") next = { x: cursor.x - cellSize, y: cursor.y };
        else if (e.key === "ArrowDown") next = { x: cursor.x, y: cursor.y + cellSize };
        else if (e.key === "ArrowUp") next = { x: cursor.x, y: cursor.y - cellSize };
        e.preventDefault();
        km.setCanvasCursor(next);
        // Reset focus-ring: nieuwe cel-positie begint schoon.
        km.setCanvasFocus(null);
        return;
      }

      // Tab / Shift+Tab — cycle door alle focusables op het canvas in
      // visuele top-left → bottom-right volgorde. Wraps rond aan beide
      // kanten. Werkt alleen wanneer er al een grid-cursor is (anders
      // pakt het init-pad hierboven 'm op).
      //
      // Verplaatst de grid-cursor naar de bbox-center van het volgende
      // focusable en zet canvasFocus zodat het item visueel highlighted
      // is. preventDefault zodat browser-default Tab niet naar buiten
      // het canvas-scope cyclet (Editor.tsx's Tab-trap skipt canvas
      // expliciet, dus zonder onze preventDefault zou Tab eruit gaan).
      if (km.canvasCursor && e.key === "Tab" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        const focusables = buildFocusables();
        if (focusables.length === 0) return;
        const sorted = [...focusables].sort((a, b) => {
          // 5px tolerantie zodat items op "dezelfde rij" links→rechts
          // lopen i.p.v. exact-Y-sort die soms wisselt door floating-
          // point-y-drift.
          const dy = a.cy - b.cy;
          if (Math.abs(dy) > 5) return dy;
          return a.cx - b.cx;
        });
        const currentId = km.focusedCanvasId;
        let idx = sorted.findIndex((f) => f.id === currentId);
        if (idx === -1) idx = e.shiftKey ? sorted.length - 1 : 0;
        else idx = e.shiftKey ? idx - 1 : idx + 1;
        if (idx < 0) idx = sorted.length - 1;
        if (idx >= sorted.length) idx = 0;
        const target = sorted[idx];
        if (!target) return;
        const snapped = snapToCell({ x: target.cx, y: target.cy }, cellSize);
        km.setCanvasCursor(snapped);
        km.setCanvasFocus(target.id);
        return;
      }

      // Plain +/= (zoom in) en - (zoom out) op canvas-niveau. 0 reset
      // de zoom naar 100 %. Geen modifier zodat Chrome's page-zoom niet
      // wordt getriggerd (die luistert op Cmd+/Cmd-). Werken alleen in
      // canvas-scope buiten editable targets, dus typen in attribute-
      // velden blijft onaangetast.
      if (!e.metaKey && !e.ctrlKey && !e.altKey && (e.key === "+" || e.key === "=")) {
        e.preventDefault();
        rfInstance.current?.zoomIn({ duration: 150 });
        return;
      }
      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === "-") {
        e.preventDefault();
        rfInstance.current?.zoomOut({ duration: 150 });
        return;
      }
      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === "0") {
        e.preventDefault();
        rfInstance.current?.zoomTo(1, { duration: 200 });
        return;
      }
      // Enter — selecteer item in cursor-cel. Twee detectie-paden:
      //   1. Edges + labels via mijn bbox-test (focusablesInCell)
      //   2. Nodes via React Flow's `getIntersectingNodes` — die heeft
      //      altijd de correcte measured bbox, ook bij geneste containers
      //      waar onze eigen ve.width-fallback niet altijd klopt.
      // Bij meerdere hits: sort op area aflopend, kies kleinste (=
      // meest-specifieke; net als muis-klik op een geneste container
      // het inner-item kiest).
      if (e.key === "Enter") {
        const cursor = km.canvasCursor;
        if (!cursor) return;
        const focusables = buildFocusables();
        // Niet-node-hits (edges + labels) via mijn bbox-test.
        const nonNodeHits = focusablesInCell(focusables, cursor, cellSize).filter(
          (f) => f.kind !== "node",
        );
        // Node-hits via React Flow — gebruik z'n interne measured bbox.
        const intersecting =
          rfInstance.current?.getIntersectingNodes(
            { x: cursor.x, y: cursor.y, width: cellSize, height: cellSize },
            false,
          ) ?? [];
        const nodeHits: Focusable[] = [];
        for (const rfNode of intersecting) {
          if (rfNode.id === CURSOR_CELL_NODE_ID) continue;
          if (rfNode.hidden) continue;
          const w = rfNode.measured?.width ?? rfNode.width ?? 140;
          const h = rfNode.measured?.height ?? rfNode.height ?? 60;
          const fallback = focusables.find((f) => f.id === rfNode.id);
          nodeHits.push({
            id: rfNode.id,
            kind: "node",
            cx: fallback?.cx ?? rfNode.position.x + w / 2,
            cy: fallback?.cy ?? rfNode.position.y + h / 2,
            bboxW: w,
            bboxH: h,
            area: w * h,
          });
        }
        const allHits = [...nodeHits, ...nonNodeHits].sort((a, b) => (a.area ?? 0) - (b.area ?? 0));
        if (allHits.length === 0) return;
        const target = allHits[0];
        if (!target) return;
        e.preventDefault();
        km.setCanvasFocus(target.id);
        // Helper om React Flow's interne `selected`-flags consistent
        // te zetten: anders blijft een eerdere muis-selectie (van een
        // ander item) zichtbaar als blauwe selectie-rand.
        const selectOnly = (nodeId: string | null, edgeId: string | null) => {
          setLocalNodes((curr) =>
            curr.map((n) => {
              const want = n.id === nodeId;
              if (n.selected === want) return n;
              return { ...n, selected: want };
            }),
          );
          setLocalEdges((curr) =>
            curr.map((edge) => {
              const want = edge.id === edgeId;
              if (edge.selected === want) return edge;
              return { ...edge, selected: want };
            }),
          );
        };
        const labelMeta = parseEdgeLabelFocusId(target.id);
        if (labelMeta) {
          useUi.getState().selectRelation(labelMeta.relationId);
          selectOnly(null, labelMeta.relationId);
          return;
        }
        const ws = useWorkspaceData.getState();
        if (ws.store.getRelation(target.id)) {
          useUi.getState().selectRelation(target.id);
          selectOnly(null, target.id);
          return;
        }
        const conceptId =
          ws.store.getDiagram(ws.activeDiagramId ?? "")?.viewState[target.id]?.ref ?? target.id;
        useUi.getState().selectInstance(target.id, conceptId);
        selectOnly(target.id, null);
        return;
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [workspaceId, branchId]);

  // Cmd+C / Cmd+V / Cmd+X — place-mode copy/paste/cut tussen diagrammen.
  // Place-mode = bestaande concept-id's verschijnen ook op target-diagram
  // (zoals drag uit explorer). Paste filtert op view-kind's
  // `included_types`-allowlist; items die niet passen worden geskipt.
  // Cross-workspace + duplicate-mode: zie 05-backlog.md.
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const key = e.key.toLowerCase();
      if (key !== "c" && key !== "v" && key !== "x") return;
      if (inEditableField(e.target)) return;
      if (useSession.getState().readOnly) return;
      if (!workspaceId || !branchId) return;
      const ws = useWorkspaceData.getState();
      const sourceDiagramId = ws.activeDiagramId;
      if (!sourceDiagramId) return;

      if (key === "c" || key === "x") {
        // ── COPY / CUT ───────────────────────────────────────────────
        const selectedNodes = localNodesRef.current.filter((n) => n.selected);
        const selectedEdges = localEdges.filter((edge) => edge.selected);
        if (selectedNodes.length === 0 && selectedEdges.length === 0) return;
        e.preventDefault();
        e.stopPropagation();

        const sourceDiagram = ws.store.getDiagram(sourceDiagramId);
        if (!sourceDiagram) return;

        // Walk up containerId-chain om absolute positie te bepalen,
        // gebruikt voor top-level items vd selectie-tree (hun parent
        // zit niet in de selectie, dus relative-pos heeft op target
        // geen anchor).
        const absolutePos = (instanceId: string): { x: number; y: number } | null => {
          let x = 0;
          let y = 0;
          let cur = sourceDiagram.viewState[instanceId];
          let safety = 0;
          while (cur && safety++ < 50) {
            if (!cur.position) return null;
            x += cur.position.x;
            y += cur.position.y;
            if (!cur.containerId) break;
            cur = sourceDiagram.viewState[cur.containerId];
          }
          return { x, y };
        };

        const selectedSet = new Set(selectedNodes.map((n) => n.id));

        // Build childrenByParent voor O(1)-lookup van directe
        // descendants per parent. Alleen items binnen de selectie
        // worden meegenomen — externe descendants zijn buiten scope.
        const childrenByParent = new Map<string, string[]>();
        const topLevelIds: string[] = [];
        for (const n of selectedNodes) {
          const ve = sourceDiagram.viewState[n.id];
          const cid = ve?.containerId;
          if (cid !== undefined && selectedSet.has(cid)) {
            const list = childrenByParent.get(cid) ?? [];
            list.push(n.id);
            childrenByParent.set(cid, list);
          } else {
            topLevelIds.push(n.id);
          }
        }

        // Recursieve tree-builder: elk top-level item krijgt een tuple
        // (zelf + viewState met absolute positie, lijst kinderen). Elk
        // kind heeft (zelf + viewState met RELATIEVE positie tov parent,
        // eigen sublijst kinderen). Op die manier kan paste recursief
        // descenden zonder ooit een parent-lookup-fail te hebben.
        type VE = import("@ae/domain").ViewElementState;
        type NodeTreeEntry = {
          instanceId: string;
          conceptId: string;
          viewState: VE;
          children: NodeTreeEntry[];
        };
        // Compartiment-kinderen (Sharp Detailed Concept): sharp.Attribute /
        // sharp.Identifier worden inline in de Concept-box gerenderd en zijn
        // niet los selecteerbaar, dus ze zitten nooit in de selectie. Bij copy
        // moeten ze tóch mee — anders plakt een Concept zonder z'n attributen/
        // identifiers op het doel-diagram. We rapen ze per parent-instance uit
        // de viewState (containerId) bovenop de geselecteerde kinderen.
        const compartmentChildIdsOf = (parentInstanceId: string): string[] => {
          if (sourceDiagram.viewKind !== "sharp.detailed-concept") return [];
          const out: string[] = [];
          for (const [k, v] of Object.entries(sourceDiagram.viewState)) {
            if (v.containerId !== parentInstanceId || v.position === undefined) continue;
            const childConceptId = v.ref ?? k;
            const child = ws.store.getConcept(childConceptId);
            if (child?.typeId === "sharp.Attribute" || child?.typeId === "sharp.Identifier") {
              out.push(k);
            }
          }
          return out;
        };
        const buildEntry = (instanceId: string, isTopLevel: boolean): NodeTreeEntry | null => {
          const ve = sourceDiagram.viewState[instanceId];
          if (!ve?.position) return null;
          const conceptId = derived.instanceToConcept.get(instanceId) ?? instanceId;
          const { containerId: _ci, ...rest } = ve;
          void _ci;
          const position = isTopLevel ? absolutePos(instanceId) : ve.position;
          if (!position) return null;
          const selChildIds = childrenByParent.get(instanceId) ?? [];
          const childIds = [
            ...selChildIds,
            ...compartmentChildIdsOf(instanceId).filter((id) => !selChildIds.includes(id)),
          ];
          const children = childIds
            .map((id) => buildEntry(id, false))
            .filter((c): c is NodeTreeEntry => c !== null);
          return {
            instanceId,
            conceptId,
            viewState: { ...rest, position },
            children,
          };
        };
        const nodeTree = topLevelIds
          .map((id) => buildEntry(id, true))
          .filter((e): e is NodeTreeEntry => e !== null);

        const edgeItems = selectedEdges
          .map((edge) => {
            const viewState = sourceDiagram.viewState[edge.id];
            if (!viewState) return null;
            // `edge.id` is de gerenderde entry-key (canonical relationId
            // vóór multi-instance, of een verse UUID daarna) — voor de
            // PASTE-logica (die `store.getRelation(item.relationId)` doet)
            // moet dit de ECHTE domain-relation-id zijn. Gebruik dezelfde
            // resolver als `onEdgeContextMenu` (bug gevonden 2026-07-14,
            // zelfde klasse als de routingMenu-fix): zonder dit faalt
            // `getRelation` stilletjes voor een multi-instance-relatie,
            // waardoor de gehele relatie zonder foutmelding wordt overgeslagen
            // bij paste.
            return { kind: "relation" as const, relationId: edgeRelationId(edge), viewState };
          })
          .filter((x): x is NonNullable<typeof x> => x !== null);

        // Anchor = midpoint van top-level items. Children volgen hun
        // parent automatisch via de tree-recursie.
        const topPositions = nodeTree
          .map((e) => e.viewState.position)
          .filter((p): p is { x: number; y: number } => p !== undefined);
        const anchor =
          topPositions.length > 0
            ? {
                x: topPositions.reduce((s, p) => s + p.x, 0) / topPositions.length,
                y: topPositions.reduce((s, p) => s + p.y, 0) / topPositions.length,
              }
            : { x: 0, y: 0 };

        const payload = {
          kind: "ae-canvas-clipboard" as const,
          version: 1 as const,
          workspaceId,
          branchId,
          sourceDiagramId,
          anchor,
          nodeTree,
          edgeItems,
        };
        void navigator.clipboard.writeText(JSON.stringify(payload)).then(() => {
          if (key === "x") {
            // Cut: hide alle gekopieerde items op source-diagram. Walk
            // de tree recursief zodat ook descendants geraakt worden.
            const hideAll = (entries: NodeTreeEntry[]) => {
              for (const e of entries) {
                ws.hideConceptOnDiagram(workspaceId, branchId, e.instanceId);
                hideAll(e.children);
              }
            };
            hideAll(nodeTree);
            for (const it of edgeItems) {
              ws.hideRelationOnDiagram(workspaceId, branchId, it.relationId);
            }
          }
        });
      } else if (key === "v") {
        // ── PASTE ────────────────────────────────────────────────────
        e.preventDefault();
        e.stopPropagation();

        void (async () => {
          let raw: string;
          try {
            raw = await navigator.clipboard.readText();
          } catch {
            return; // permission denied or empty
          }
          type VE = import("@ae/domain").ViewElementState;
          type ClipboardNode = {
            instanceId: string;
            conceptId: string;
            viewState: VE;
            children: ClipboardNode[];
          };
          let payload: {
            kind?: string;
            version?: number;
            workspaceId?: string;
            branchId?: string;
            anchor?: { x: number; y: number };
            nodeTree?: ClipboardNode[];
            edgeItems?: { kind: "relation"; relationId: string; viewState: VE }[];
          };
          try {
            payload = JSON.parse(raw);
          } catch {
            return;
          }
          if (payload.kind !== "ae-canvas-clipboard" || payload.version !== 1) return;
          if (payload.workspaceId !== workspaceId || payload.branchId !== branchId) {
            // Cross-workspace paste — niet ondersteund in v0.1, zie
            // 05-backlog.md "Cross-workspace copy/paste op canvas".
            console.warn("[paste] cross-workspace clipboard skipped");
            return;
          }
          const nodeTree = payload.nodeTree ?? [];
          const edgeItems = payload.edgeItems ?? [];
          if (nodeTree.length === 0 && edgeItems.length === 0) return;

          const targetDiagram = ws.store.getDiagram(sourceDiagramId);
          if (!targetDiagram) return;
          const targetViewKindId = targetDiagram.viewKind;

          // Cursor-anchor in flow-coords. Als cursor net buiten canvas
          // is bij keyboard-paste, valt screenToFlowPosition terug op
          // (0, 0) — niet ideaal maar zelden geraakt.
          const cursorScreen = latestMouseRef.current;
          const cursorFlow = rfInstance.current?.screenToFlowPosition(cursorScreen) ?? {
            x: 0,
            y: 0,
          };
          const anchor = payload.anchor ?? { x: 0, y: 0 };

          const placedKeys: string[] = [];
          let skippedTypeFilter = 0;
          // conceptId → nieuw-toegewezen instanceId voor ELKE geplaatste
          // concept-entry in DEZE paste. Gebruikt hieronder om te bepalen of
          // een gepaste relatie een eigen nieuwe relatie-instance moet
          // krijgen (zie edgeItems-loop) — alleen relevant als de waarde
          // afwijkt van de conceptId zelf (afwijkend = duplicate-instance
          // aangemaakt, gelijk = eerste-plaatsing/hergebruik canonical-id).
          const conceptIdToNewInstanceId = new Map<string, string>();

          // Recursieve plaatsing: voor elk top-level entry plaatsen we
          // het op cursor + (entry-pos - anchor); voor descendants
          // plaatsen we ze relatief tov de NIEUW-geplaatste parent.
          // Geen sortering, geen oldToNew-map nodig — de tree-structuur
          // garandeert correcte volgorde door constructie. Werkt voor
          // willekeurige nesting-diepte.
          const placeEntry = (entry: ClipboardNode, parentNewInstanceId: string | null): void => {
            const concept = ws.store.getConcept(entry.conceptId);
            if (!concept) return;
            if (!isElementTypeAllowedOnViewKind(concept.typeId, targetViewKindId)) {
              skippedTypeFilter++;
              // Children van een geskipt parent: behandel ze als nieuwe
              // top-level (anchor = parent's bedoelde target-positie zou
              // mooier zijn, maar voor v0.1 plot ze los op cursor —
              // edge-case, treedt alleen op bij gemixte type-allowlists).
              for (const c of entry.children) placeEntry(c, null);
              return;
            }
            const pos = entry.viewState.position ?? anchor;
            let targetPos: { x: number; y: number };
            let newContainerId: string | undefined;
            if (parentNewInstanceId === null) {
              targetPos = {
                x: cursorFlow.x + (pos.x - anchor.x),
                y: cursorFlow.y + (pos.y - anchor.y),
              };
              newContainerId = undefined;
            } else {
              // Child: position is relative to original parent, hetzelfde
              // relative-frame geldt voor de nieuwe parent (zelfde grootte,
              // zelfde layout-bedoeling).
              targetPos = pos;
              newContainerId = parentNewInstanceId;
            }
            // "Al geplaatst?" moet over ALLE instances van dit concept op dit
            // diagram kijken, niet alleen de canonical-key-slot (`entry.
            // conceptId` zelf) — een concept kan uitsluitend als non-
            // canonical multi-instance aanwezig zijn (bv. via drag-from-
            // explorer op een elders-al-geplaatst concept, of een eerdere
            // paste). Keek deze check ALLEEN naar de canonical-slot, dan
            // werd zo'n concept bij paste ten onrechte als "eerste
            // plaatsing" behandeld — de nieuwe kopie kreeg de canonical-key
            // (i.p.v. een herkenbare nieuwe instance-id), waardoor de
            // duplicate-detectie hieronder (`conceptIdToNewInstanceId`)
            // 'm niet als duplicate zag en de gepaste relatie alsnog zonder
            // instance-pin geplaatst werd (gevonden 2026-07-13, na de
            // instance-pin-fix hierboven).
            const isFirstPlacement = !Object.entries(targetDiagram.viewState).some(
              ([k, v]) => v.position !== undefined && (v.ref ?? k) === entry.conceptId,
            );
            const newInstanceId = isFirstPlacement ? entry.conceptId : crypto.randomUUID();
            const newVe: VE = {
              ...entry.viewState,
              position: targetPos,
              ...(newContainerId !== undefined ? { containerId: newContainerId } : {}),
              ...(isFirstPlacement ? {} : { conceptRef: entry.conceptId }),
            };
            ws.applyViewElementOnCanvas(
              workspaceId,
              branchId,
              sourceDiagramId,
              newInstanceId,
              newVe,
            );
            placedKeys.push(newInstanceId);
            conceptIdToNewInstanceId.set(entry.conceptId, newInstanceId);
            // Recurse naar children met deze nieuwe instanceId als parent.
            for (const c of entry.children) placeEntry(c, newInstanceId);
          };

          for (const entry of nodeTree) placeEntry(entry, null);

          // Top-level paste-shift = cursor - anchor. Waypoints in relation
          // viewState zijn absolute flow-coords; zonder translate komen
          // ze op de oude posities terecht en lopen niet meer mee met
          // de verschoven nodes. Hier verschuiven we ze hetzelfde delta
          // zodat de hele lijn z'n vorm behoudt.
          const pasteDelta = {
            x: cursorFlow.x - anchor.x,
            y: cursorFlow.y - anchor.y,
          };

          for (const item of edgeItems) {
            // Als (minstens) één endpoint van deze relatie in DEZE paste een
            // eigen nieuwe concept-instance kreeg (duplicate, niet eerste-
            // plaatsing), moet de relatie ook een eigen nieuwe relatie-
            // instance krijgen die aan die nieuwe instance(s) vastzit —
            // anders "springt" de bestaande enkelvoudige relatie-weergave
            // naar de nieuwste instance (zie firstInstancePerConcept-
            // fallback in `resolveEndpointAnchor` hierboven) en verliezen de
            // originele concepten hun zichtbare lijn. `placeExistingRelation-
            // OnDiagram` ondersteunt dit al via instance-pins (Feature A,
            // drag-relatie-naar-specifieke-instance) — we hergebruiken 't
            // hier voor paste.
            const relation = ws.store.getRelation(item.relationId);
            const sourcePin = relation
              ? conceptIdToNewInstanceId.get(relation.sourceConceptId)
              : undefined;
            const targetPin = relation
              ? conceptIdToNewInstanceId.get(relation.targetConceptId)
              : undefined;
            const sourceIsDuplicate =
              sourcePin !== undefined && sourcePin !== relation?.sourceConceptId;
            const targetIsDuplicate =
              targetPin !== undefined && targetPin !== relation?.targetConceptId;
            const needsNewInstance = sourceIsDuplicate || targetIsDuplicate;
            const entryKey = needsNewInstance ? crypto.randomUUID() : item.relationId;
            const result = ws.placeExistingRelationOnDiagram(
              workspaceId,
              branchId,
              item.relationId,
              needsNewInstance
                ? {
                    entryKey,
                    ...(sourceIsDuplicate ? { sourceInstanceId: sourcePin } : {}),
                    ...(targetIsDuplicate ? { targetInstanceId: targetPin } : {}),
                  }
                : undefined,
            );
            if (result === "placed") {
              placedKeys.push(entryKey);
              // placeExistingRelationOnDiagram zet alleen een lege
              // viewState. Voor Cmd+C/V verwacht de gebruiker dezelfde
              // visuele uitstraling — incl. label-offsets, breedtes,
              // line-color, waypoints, etc. Kopiëer de relevante velden
              // uit de source-viewState. position/conceptRef/containerId
              // zijn concept-specifiek en horen niet op een relation
              // viewState (filteren via destructuring).
              const {
                position: _p,
                width: _w,
                height: _h,
                containerId: _ci,
                hidden: _hid,
                hiddenByAutoNest: _hba,
                collapsedAsContainer: _co,
                ...relationViewFields
              } = item.viewState;
              void _p;
              void _w;
              void _h;
              void _ci;
              void _hid;
              void _hba;
              void _co;
              // Translate absolute waypoint-coords door de paste-shift
              // zodat de lijn-vorm consistent blijft met de nodes.
              if (Array.isArray(relationViewFields.waypoints)) {
                relationViewFields.waypoints = relationViewFields.waypoints.map((wp) => ({
                  x: wp.x + pasteDelta.x,
                  y: wp.y + pasteDelta.y,
                }));
              }
              if (Object.keys(relationViewFields).length > 0) {
                // `applyViewElementOnCanvas` → `store.setViewElementState` is
                // een VOLLEDIGE REPLACE van de entry, geen merge. Als de
                // entry hierboven via instance-pins is aangemaakt
                // (`sourceInstanceId`/`targetInstanceId`, needsNewInstance-
                // pad), zou een kale write van `relationViewFields` (die
                // geen pins bevat — die zaten nooit op de gekopieerde
                // BRON-viewState) die pins stilletjes wissen, en zou de
                // gepaste relatie terugvallen op de firstInstancePerConcept-
                // fallback — exact de "lijn springt naar verkeerde instance"-
                // bug die deze hele paste-fix moest oplossen. Lees daarom de
                // net-geschreven entry terug en merge erbovenop.
                const justPlaced = ws.store.getDiagram(sourceDiagramId)?.viewState[entryKey];
                ws.applyViewElementOnCanvas(workspaceId, branchId, sourceDiagramId, entryKey, {
                  ...justPlaced,
                  ...relationViewFields,
                });
              }
            }
          }

          // Single undo-entry voor de hele paste — Cmd+Z verwijdert
          // alle nieuwe viewState-entries. Redo plaatst ze opnieuw via
          // dezelfde logica (gewoon opnieuw aanroepen werkt niet want
          // de cursor is verschoven; we capturen daarom de exacte
          // (instanceId, viewState)-paren en zetten die terug).
          if (placedKeys.length > 0) {
            // Capture na de plaatsing zodat undo precies de viewState
            // herstelt zoals 'ie geworden is, en redo dezelfde set
            // weer kan toepassen.
            const placedSnapshot = placedKeys
              .map((id) => {
                const ve = ws.store.getDiagram(sourceDiagramId)?.viewState[id];
                return ve ? { id, ve } : null;
              })
              .filter((x): x is { id: string; ve: VE } => x !== null);
            ws.pushUserUndo({
              label: "Plakken",
              undo: () => {
                const s = useWorkspaceData.getState();
                for (const { id } of placedSnapshot) {
                  s.removeViewElementOnCanvas(workspaceId, branchId, sourceDiagramId, id);
                }
              },
              redo: () => {
                const s = useWorkspaceData.getState();
                for (const { id, ve } of placedSnapshot) {
                  s.applyViewElementOnCanvas(workspaceId, branchId, sourceDiagramId, id, ve);
                }
              },
            });
          }

          if (skippedTypeFilter > 0) {
            console.warn(
              `[paste] ${skippedTypeFilter} item(s) skipped — typeId not in viewpoint allowlist`,
            );
          }
        })();
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [workspaceId, branchId, localEdges, derived.instanceToConcept]);

  // Relation-quick-rename (relatie selecteren + typen = direct hernoemen)
  // is UITGEZET (2026-06-19, op verzoek). Dat pad patchte per toetsaanslag
  // alleen de actieve taal zónder commit-moment — dus zonder auto-translate
  // naar de andere taal en zonder duplicaat-controle. Hernoemen gaat nu
  // uitsluitend via dubbelklik-inline (RoutedEdge) of het AttributePanel —
  // beide met translate + dup-check op commit.
  //
  // Dit effect houdt nog twee dingen in stand zodat het uitzetten géén
  // neveneffect heeft:
  //  - Escape → deselecteert de relatie (zat hier voorheen ook).
  //  - printable / Backspace → geslikt (preventDefault + stopPropagation)
  //    zodat een per ongeluk getypte toets niets doet. Backspace mag NIET
  //    doorvallen naar de Delete/Backspace-handler (regel ~2718), anders
  //    zou typen ineens de relatie verbergen. De Delete-toets blijft
  //    verwijderen; dat pad raken we niet.
  useEffect(() => {
    if (!selectedRelationId) return;
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inEditableField(e.target)) return;
      if (!selectedRelationId) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        useUi.getState().selectRelation(null);
        return;
      }
      // Quick-rename uit: typen/Backspace doen niets meer, maar slik ze
      // wel zodat Backspace de relatie niet via de delete-handler verbergt.
      if (e.key === "Backspace" || e.key.length === 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [selectedRelationId]);

  // RF's edge-changes (position, dimensions) lopen hier door; select-
  // changes komen er ook doorheen maar zijn meestal no-ops omdat we
  // edge-selectie via useUi → useEffect-mirror sturen i.p.v. via RF.
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setLocalEdges((curr) => applyEdgeChanges(changes, curr));
  }, []);

  /**
   * onSelectionChange — zuiver ADDITIEF. We wissen geen multi-sets meer
   * via `selectInstance` / `selectRelation` (die hebben de multi-wipe
   * als bijwerking). Wissen-of-niet wordt bepaald door
   * `isModifierHeldRef`: plain klik = wissen, modifier-klik = behouden.
   *
   * Verantwoordelijkheidsverdeling:
   *  - **Concept-multi**: hier overgenomen uit RF's nodes-veld. Dat veld
   *    is betrouwbaar omdat we onNodesChange's select-changes WEL
   *    doorlaten (anders dan voor edges).
   *  - **Edge-multi**: NIET hier — exclusief beheerd door `onEdgeClick`
   *    (heeft event-modifier-info) + custom rubber-band. We filteren
   *    onEdgesChange's select-changes, dus RF's `edges`-veld in
   *    onSelectionChange is onbetrouwbaar voor edge-multi-state.
   *  - **Single-focus** (selectedConceptId / selectedInstanceId /
   *    selectedRelationId): hier via `useUi.setState` direct — géén
   *    `selectInstance`/`selectRelation` aanroepen, die wissen multi.
   *  - **Plain klik op node MET edge-multi actief**: detecteren we via
   *    `!isModifierHeldRef.current`, wissen we expliciet edge-multi.
   */
  const onSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes, edges }) => {
      const ui = useUi.getState();

      // Empty-event: phantom-fire van RF (rubber-band-flush, re-derive
      // na store-mutatie, etc.). Niet wissen — daar is onPaneClick voor.
      if (nodes.length === 0 && edges.length === 0) return;

      const isAdditive = isModifierHeldRef.current;

      // ── Concept-multi diff ── ALLEEN updaten als RF's nodes-field
      // anders is dan wat useUi heeft. `conceptsSame=true` betekent
      // typisch een echo (onze rubber-band zette concept-multi en RF
      // firet hierna onSelectionChange met dezelfde nodes) — niets doen.
      // Op klikvolgorde, niet op tekenvolgorde. `selectedNodeIdsRef` is een Set
      // die per selectie-event wordt bijgewerkt, en een Set bewaart z'n
      // invoegvolgorde — dat is precies "wie heb ik als eerste aangeklikt".
      // De werkbalk leunt daarop voor bewerkingen met een ankerobject
      // (gelijke maat als het eerst geselecteerde).
      const klikVolgorde = [...selectedNodeIdsRef.current];
      const opVolgorde = [...nodes].sort((a, b) => {
        const ia = klikVolgorde.indexOf(a.id);
        const ib = klikVolgorde.indexOf(b.id);
        return (
          (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib)
        );
      });
      const newConceptIds = opVolgorde.map((n) => derived.instanceToConcept.get(n.id) ?? n.id);
      const oldConceptIds = ui.selectedConceptIds;
      const conceptsSame =
        newConceptIds.length === oldConceptIds.length &&
        newConceptIds.every((id) => oldConceptIds.includes(id));

      // ── Single-focus ── via direct setState (geen selectInstance/
      // selectRelation, die wissen multi als bijwerking).
      if (nodes.length > 0) {
        const first = nodes[0];
        if (first) {
          const conceptId = derived.instanceToConcept.get(first.id) ?? first.id;
          useUi.setState({
            selectedConceptId: conceptId,
            selectedInstanceId: first.id,
            selectedRelationId: null,
            selectedDiagramId: null,
            selectedWaypoint: null,
          });
        }
        // Het register op KLIKvolgorde bijwerken, niet op de volgorde die
        // React Flow teruggeeft — die is de tekenvolgorde. Vulde je 'm
        // daarmee, dan overleefde de klikvolgorde precies één stap: bij de
        // derde klik las de sortering hierboven z'n eigen tekenvolgorde terug
        // en werd het eerst getekende object het anker in plaats van het eerst
        // aangeklikte. Gemeten 2026-09-04: drie vakjes, het middelste eerst
        // aangeklikt, en toch nam alles de maat van het linker over.
        selectedNodeIdsRef.current = new Set(opVolgorde.map((n) => n.id));

        // Het anker: het object waarvan de rest z'n eigenschap overneemt bij
        // gelijke maat en uitlijnen. Een gewone klik wijst 'm aan; erbij-
        // klikken laat 'm staan zolang 'ie geselecteerd blijft. Blijft 'ie
        // niet, of kwam de selectie uit een sleepkader (dan staat 'ie al op
        // null en is `isAdditive` false noch true relevant), dan is er geen
        // maatstaf en valt de werkbalk terug op z'n oude gedrag.
        const huidigAnker = ui.selectionAnchorConceptId;
        const uitSleepkader = bandSelectionRef.current;
        bandSelectionRef.current = false;
        const nieuwAnker = uitSleepkader
          ? null
          : isAdditive
            ? huidigAnker && newConceptIds.includes(huidigAnker)
              ? huidigAnker
              : null
            : (newConceptIds[0] ?? null);
        if (nieuwAnker !== huidigAnker) ui.setSelectionAnchorConceptId(nieuwAnker);
      } else if (edges.length > 0) {
        const first = edges[0];
        if (first) {
          // Multi-instance: first.id is de edge-id (entry-key). Voor
          // selectedRelationId willen we de underlying domain-relation-id
          // via data.relationId; fallback op edge.id voor canonical.
          const firstRelId =
            ((first.data as { relationId?: unknown } | undefined)?.relationId as
              | string
              | undefined) ?? first.id;
          useUi.setState({
            selectedRelationId: firstRelId,
            selectedConceptId: null,
            selectedInstanceId: null,
            selectedDiagramId: null,
            selectedWaypoint: null,
          });
          // Multi-instance-refactor (2026-05-26): zet OOK selectedEdgeIds
          // met de specifieke entry-keys zodat de visuele edge-highlight
          // exact die instances pakt en niet via single-fallback de
          // canonical entry highlight'd.
          ui.setSelectedEdgeIds(edges.map((e) => e.id));
        }
      }

      // Concept-multi update + Excel-stijl "single-click replaces multi"-
      // clear. Beide ALLEEN wanneer concept-multi echt verandert — anders
      // is dit een echo van onze eigen rubber-band of GridPane-push, en
      // moeten we de bestaande edge-multi NIET wegvegen.
      if (!conceptsSame) {
        ui.setSelectedConceptIds(newConceptIds);
      }
      // Symmetrisch: plain-klik op edge wist eerdere concept-multi.
      if (
        !isAdditive &&
        edges.length > 0 &&
        nodes.length === 0 &&
        ui.selectedConceptIds.length > 0
      ) {
        ui.setSelectedConceptIds([]);
        selectedNodeIdsRef.current = new Set();
      }

      // Edge-multi: NIET hier — `RoutedEdge.onLineMouseDown` is de bron
      // van waarheid (zie comment-block hierboven). RF's `edges`-veld in
      // `onSelectionChange` is onbetrouwbaar omdat we `edgesFocusable=
      // false` aanhouden om RF's interne edge-selection-store stil te
      // leggen.
    },
    [derived.instanceToConcept],
  );

  // GEEN `onEdgeClick`-callback meer — alle edge-clicks worden door
  // `RoutedEdge.onLineMouseDown` afgehandeld (de wide invisible overlay
  // vangt mousedown vóór RF's click-detection). Een framework-routed
  // `onEdgeClick`-fire op dezelfde click revertte voorheen de toggle: de
  // browser fired een synth-click na mouseup die door React's synthetic-
  // event-systeem op de root werd gedispatched, onafhankelijk van een
  // window-capture-swallower in RoutedEdge. Helemaal weghalen voorkomt
  // double-handling. Voor toekomstige keyboard-routing (Space op
  // gefocuste edge, vereist `edgesFocusable=true`) zou je 't apart
  // re-introduceren mét een explicit "kwam-niet-van-RoutedEdge"-flag.

  // Right-click context menu for picking the edge's routing strategy.
  // Position is in screen coordinates; the menu is rendered absolutely
  // on top of the canvas wrapper. `edgeId` = the RENDERED instance/entry-key
  // (viewState key — used for per-instance visual props like routing/
  // waypoints/angles). `relationId` = the underlying DOMAIN relation id
  // (used for type-change/reverse/directed — shared across all instances).
  // For a canonical (non-multi-instance) edge these are the same value; for
  // a multi-instance edge (entry-key = fresh UUID, `ref` → relationId)
  // they differ, and conflating them made the "Relatie-type" section
  // silently disappear (getRelation(entryKey) → undefined) whenever the
  // right-clicked edge was a multi-instance entry (bug found 2026-07-13 —
  // exposed by the paste-multi-instance-relation-fix, but pre-existing for
  // any multi-instance relation, e.g. Feature-A drag-to-specific-instance).
  const [routingMenu, setRoutingMenu] = useState<{
    x: number;
    y: number;
    edgeId: string;
    relationId: string;
  } | null>(null);

  /** Node-level context menu — opens bij rechtsklik op een concept. Werkt
   *  analoog aan onEdgeContextMenu maar voor nodes. Bevat minimaal een
   *  "Bewerken"-actie die het AttributePanel opent; toekomstige items
   *  (kopiëren, verwijderen, link openen voor drawing-rect) breiden 't uit. */
  const [nodeContextMenu, setNodeContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
  } | null>(null);
  // §12.3 — concept waarvoor de "Wijzig ArchiMate-type"-dialog open staat.
  const [changeTypeConceptId, setChangeTypeConceptId] = useState<string | null>(null);
  // Sub-stereotype-keuze bij «Container»-creatie (M8): gezet in de drop-`.then`
  // zodra een generieke Container is aangemaakt; de modal patcht 'm op keuze.
  const [subStereoPrompt, setSubStereoPrompt] = useState<{
    conceptId: string;
    baseLabel: string;
    options: Array<{ id: string; label: Record<string, string> }>;
  } | null>(null);
  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    event.preventDefault();
    event.stopPropagation();
    objectMenuNet.current = Date.now();
    setCanvasMenu(null);
    // Browser-conventie: rechtsklik selecteert eerst het object (alsof
    // de gebruiker linksklikt) — anders krijgt 'ie een context-menu
    // voor een ander object dan zichtbaar geselecteerd is. We
    // overschrijven de huidige selectie ALLEEN als de node nog niet
    // in de set zit; al-geselecteerde nodes laten we ongemoeid zodat
    // bulk-acties (rechtsklik na multi-select) op de hele set werken.
    const ui = useUi.getState();
    if (!ui.selectedConceptIds.includes(node.id)) {
      ui.selectConcept(node.id);
    }
    const rect = wrapperRef.current?.getBoundingClientRect();
    const x = rect ? event.clientX - rect.left : event.clientX;
    const y = rect ? event.clientY - rect.top : event.clientY;
    setNodeContextMenu({ x, y, nodeId: node.id });
  }, []);
  // Buitenom klikken en Escape sluiten het menu; dat zit in
  // `ConceptContextMenu` zelf. Het stond hier met `capture: true`, en dat
  // moest wég toen het menu een eigen component werd: een luisteraar in de
  // capture-fase sluit 'm vóór de klik z'n eigen knop bereikt, dus geen
  // enkele regel deed nog iets.

  /** Rechtermuis-"Samenvoegen" voor ≥2 geselecteerde kloon-concepten. De
   *  clone-check (type + naam + stereotypes) draait pas hier, op klik — niet
   *  continu (te veel impact op de canvas-render). Winnaar = eerst-gevonden;
   *  rest wordt erin gemerged. */
  const mergeSelectedConcepts = useCallback(() => {
    setNodeContextMenu(null);
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const seen = new Set<string>();
    const concepts: Concept[] = [];
    for (const id of useUi.getState().selectedConceptIds) {
      const cid = ws.store.getConcept(id) ? id : derived.instanceToConcept.get(id);
      const c = cid ? ws.store.getConcept(cid) : null;
      if (c && !seen.has(c.id)) {
        seen.add(c.id);
        concepts.push(c);
      }
    }
    if (concepts.length < 2) return;
    const [first, ...rest] = concepts;
    if (!first || !rest.every((c) => isSameConceptClone(first, c))) {
      window.alert(t("canvas.contextMenu.mergeConceptsNotClones"));
      return;
    }
    void ws.mergeConcepts(
      workspaceId,
      branchId,
      first.id,
      rest.map((c) => c.id),
    );
  }, [workspaceId, branchId, derived.instanceToConcept, t]);

  /** Rechtermuis-"Samenvoegen" voor ≥2 geselecteerde kloon-relaties (zelfde
   *  identiteit: type + naam + richting). Winnaar = eerst-gevonden. */
  const mergeSelectedRelations = useCallback(() => {
    setRoutingMenu(null);
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const seen = new Set<string>();
    const relations: Relation[] = [];
    for (const id of useUi.getState().selectedRelationIds) {
      const r = ws.store.getRelation(id);
      if (r && !seen.has(r.id)) {
        seen.add(r.id);
        relations.push(r);
      }
    }
    if (relations.length < 2) return;
    const [first, ...rest] = relations;
    if (!first || !rest.every((r) => isSameRelationIdentity(first, r))) {
      window.alert(t("canvas.contextMenu.mergeRelationsNotClones"));
      return;
    }
    void ws.mergeRelations(
      workspaceId,
      branchId,
      first.id,
      rest.map((r) => r.id),
    );
  }, [workspaceId, branchId, t]);

  /** Single-click op een node — voor drawing.Rectangle met link-attributen
   *  navigeren we naar de bestemming (externe URL of intern diagram).
   *  React Flow's standaard-selectie loopt parallel ongemoeid door.
   *  Geen actie voor non-rectangle of voor rectangles zonder ingevulde
   *  link_kind + link_target. */
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!workspaceId || !branchId) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws.store.getDiagram(diagramId);
      if (!diagram) return;
      const ve = diagram.viewState[node.id];
      if (!ve) return;
      const conceptId = ve.ref ?? node.id;
      const concept = ws.store.getConcept(conceptId);
      if (concept?.typeId !== "drawing.Rectangle") return;
      const linkKind = concept.attributes?.link_kind;
      const linkTarget = concept.attributes?.link_target;
      if (typeof linkTarget !== "string" || linkTarget.trim() === "") return;
      if (linkKind === "url") {
        // Nieuw tabblad — `noopener,noreferrer` voor security (link kan
        // van een ander team / externe bron komen, geen reden om
        // window.opener exposure te geven).
        window.open(linkTarget, "_blank", "noopener,noreferrer");
        event.stopPropagation();
        return;
      }
      if (linkKind === "diagram") {
        const target = ws.store.getDiagram(linkTarget);
        if (!target) return; // dangling diagram-ref (verwijderd / verkeerde id)
        ws.openDiagram(workspaceId, linkTarget);
        event.stopPropagation();
      }
    },
    [workspaceId, branchId],
  );

  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    event.stopPropagation();
    objectMenuNet.current = Date.now();
    setCanvasMenu(null);
    // Multi-instance-refactor: selectRelation wist selectedEdgeIds; daarna
    // direct herzetten met de geklikte entry-key zodat visueel exact die
    // ene instance gehighlight blijft i.p.v. via single-fallback de
    // canonical te pakken.
    useUi.getState().selectRelation(edgeRelationId(edge));
    useUi.getState().setSelectedEdgeIds([edge.id]);
    // The menu is positioned `absolute` inside `wrapperRef`, so its
    // `left/top` are wrapper-relative — not viewport-relative. Subtract
    // the wrapper's screen offset so the popup lands at the cursor
    // instead of being shifted by the diagrams-panel + toolbar widths.
    const rect = wrapperRef.current?.getBoundingClientRect();
    const x = rect ? event.clientX - rect.left : event.clientX;
    const y = rect ? event.clientY - rect.top : event.clientY;
    setRoutingMenu({ x, y, edgeId: edge.id, relationId: edgeRelationId(edge) });
  }, []);
  const routingMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!routingMenu) return;
    // capture-phase zodat React Flow's interne stopPropagation op
    // canvas-elementen ons niet blokkeert — anders blijft het popup
    // open ongeacht waar je klikt buiten z'n eigen container.
    // Maar: in capture-phase vuurt deze listener vóór het popup zelf,
    // dus we moeten expliciet checken of de click BINNEN het popup
    // valt en dan niets doen — anders sluit 't menu vóór een
    // menu-item z'n onClick kan afronden.
    const onAnyClick = (e: MouseEvent) => {
      if (e.target instanceof Node && routingMenuRef.current?.contains(e.target)) return;
      setRoutingMenu(null);
    };
    window.addEventListener("mousedown", onAnyClick, { capture: true });
    return () => window.removeEventListener("mousedown", onAnyClick, { capture: true });
  }, [routingMenu]);

  const onReconnect: OnReconnect = useCallback(
    (oldEdge, newConnection) => {
      // Update React Flow's controlled state immediately so the edge visually
      // attaches to the new handle without a flicker.
      setLocalEdges((edges) => reconnectEdge(oldEdge, newConnection, edges));

      if (!workspaceId || !branchId) return;

      // Pin de nieuwe aanhechting via alleen-angle (zelfde rationale
      // als in onConnect: handle + angle samen levert visuele pixel-
      // mismatch op tussen React Flow's handle-coord en RoutedEdge's
      // pointFromAngle-coord). De angle wordt iets verderop in dit
      // handler-blok berekend en met `setRelationAnglesOnCanvas` weg-
      // geschreven, dat is genoeg voor de routing. Wel oude
      // handle-id's mee opruimen mocht 't relatie 'r één hebben.
      useWorkspaceData
        .getState()
        .setRelationHandlesOnCanvas(workspaceId, branchId, oldEdge.id, null, null);

      const sameSource = oldEdge.source === newConnection.source;
      const sameTarget = oldEdge.target === newConnection.target;

      // When an endpoint moves to a different handle, update the stored angle
      // for that endpoint so it matches the new handle side.
      {
        const ws = useWorkspaceData.getState();
        const diagramId = ws.activeDiagramId;
        if (diagramId) {
          const existing = ws.store.getDiagram(diagramId)?.viewState[oldEdge.id];
          const newSourceAngle = !sameSource
            ? handleIdToAngle(newConnection.sourceHandle ?? undefined)
            : existing?.sourceAngle;
          // For the target, compute from current mouse position (same as onConnect).
          const mouseFlow = rfInstance.current?.screenToFlowPosition(latestMouseRef.current);
          let newTargetAngle = existing?.targetAngle;
          if (!sameTarget && mouseFlow && newConnection.target) {
            const diagram = ws.store.getDiagram(diagramId);
            if (diagram) {
              const absOf = (key: string): { x: number; y: number } | null => {
                const entry = diagram.viewState[key];
                if (!entry?.position) return null;
                let ax = entry.position.x;
                let ay = entry.position.y;
                let cur = entry;
                while (cur.containerId) {
                  const parent = diagram.viewState[cur.containerId];
                  if (!parent?.position) break;
                  ax += parent.position.x;
                  ay += parent.position.y;
                  cur = parent;
                }
                return { x: ax, y: ay };
              };
              const targetVe = diagram.viewState[newConnection.target];
              const targetAbs = absOf(newConnection.target);
              if (targetAbs && targetVe) {
                const tw =
                  rfInstance.current?.getNode(newConnection.target)?.measured?.width ??
                  targetVe.width ??
                  140;
                const th =
                  rfInstance.current?.getNode(newConnection.target)?.measured?.height ??
                  targetVe.height ??
                  60;
                newTargetAngle = nearestBorderAngle(
                  { x: targetAbs.x, y: targetAbs.y, w: tw, h: th },
                  mouseFlow.x,
                  mouseFlow.y,
                );
              }
            }
          }
          ws.setRelationAnglesOnCanvas(
            workspaceId,
            branchId,
            oldEdge.id,
            newSourceAngle,
            newTargetAngle,
          );
        }
      }
      if (sameSource && sameTarget) return;

      const patch: Record<string, string> = {};
      if (!sameSource && newConnection.source) {
        patch.source_concept_id = newConnection.source;
      }
      if (!sameTarget && newConnection.target) {
        patch.target_concept_id = newConnection.target;
      }
      void useWorkspaceData.getState().patchRelation(workspaceId, branchId, oldEdge.id, patch);
    },
    [workspaceId, branchId],
  );

  const onPaneClick = useCallback(() => {
    useUi.getState().selectConcept(null);
    selectedNodeIdsRef.current = new Set();
    setLocalNodes((curr) => curr.map((n) => (n.selected ? { ...n, selected: false } : n)));
    setLocalEdges((curr) => curr.map((e) => (e.selected ? { ...e, selected: false } : e)));
  }, []);
  // Voor het sleepkader, dat eerder in dit bestand staat dan deze functie.
  paneClickRef.current = onPaneClick;

  // Right-double-click op de canvas-wrapper zoomt uit (symmetrisch met
  // React Flow's standaard left-double-click = inzoomen). Twee
  // contextmenu-events binnen 400ms tellen als dubbelklik. Werkt ook met
  // panOnDrag op middel/rechts: als de gebruiker niet sleept, vuurt
  // contextmenu nog steeds bij muis-omhoog.
  const lastRightClickAt = useRef(0);
  const [canvasMenu, setCanvasMenu] = useState<CanvasMenuStand | null>(null);
  const onCanvasContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastRightClickAt.current < 400) {
      // Tweede rechtsklik binnen 400 ms: uitzoomen, en het menu weer weg.
      rfInstance.current?.zoomOut({ duration: 200 });
      lastRightClickAt.current = 0;
      setCanvasMenu(null);
      return;
    }
    lastRightClickAt.current = now;
    // Het menu komt niet hier maar bij LOSLATEN (`onCanvasMouseUp`): met de
    // rechterknop pan je ook, en op macOS vuurt `contextmenu` al bij
    // muis-omlaag — een menu hier zou dus over elke rechts-sleep heen
    // verschijnen.
  }, []);

  /**
   * Rechtsklik zonder sleep = het canvas-menu; mét sleep was het pannen.
   * Hetzelfde onderscheid als `ZoomPan` op de andere canvassen maakt.
   */
  const rechtsOmlaag = useRef<{ x: number; y: number } | null>(null);
  /**
   * Wanneer een object z'n eigen menu opende.
   *
   * Een ref en geen state: de muis-omhoog komt in dezelfde gebeurtenis-reeks
   * als de rechtsklik, dus React heeft dan nog niet opnieuw gerenderd en een
   * state-check ziet de oude waarde — het canvas-menu opende dan óók, naast
   * dat van het object.
   */
  const objectMenuNet = useRef(0);
  const onCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 2) rechtsOmlaag.current = { x: e.clientX, y: e.clientY };
    setCanvasMenu(null);
  }, []);
  const onCanvasMouseUp = useCallback((e: React.MouseEvent) => {
    const begin = rechtsOmlaag.current;
    rechtsOmlaag.current = null;
    if (e.button !== 2 || !begin) return;
    if (Math.abs(e.clientX - begin.x) > 3 || Math.abs(e.clientY - begin.y) > 3) return;
    if (lastRightClickAt.current === 0) return; // tweede klik van een dubbele
    // Een rechtsklik op een object heeft z'n eigen menu; dan geen tweede.
    if (Date.now() - objectMenuNet.current < 500) return;
    const rect = wrapperRef.current?.getBoundingClientRect();
    setCanvasMenu({
      x: rect ? e.clientX - rect.left : e.clientX,
      y: rect ? e.clientY - rect.top : e.clientY,
    });
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!workspaceId || !branchId) return;
      // Translate the pointer position to flow (canvas) coordinates so
      // panning + zooming don't misplace the dropped element. Subtract
      // half-default-size so the cursor lands in the centre rather than
      // on the top-left corner of the new vakje.
      const flow = rfInstance.current?.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const position = flow
        ? { x: flow.x - 70, y: flow.y - 25 }
        : { x: event.clientX - 70, y: event.clientY - 25 };

      const ws = useWorkspaceData.getState();
      // Resolve the active diagram's viewpoint so we can refuse drops of
      // concept types that aren't allowed there.
      const diagram = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;

      // Three drop sources:
      // 1. Type palette → drop a new concept of that type (creates concept).
      // 2. Concept explorer (single) → drop an existing concept onto the
      //    active diagram (no create, just a view_state entry).
      // 3. Concept explorer (multi) → drop a JSON array of existing concept
      //    ids; place each at a cascading offset.
      const bulkJson = event.dataTransfer.getData("application/ae-existing-concept-ids");
      if (bulkJson) {
        let ids: string[] = [];
        try {
          const parsed = JSON.parse(bulkJson);
          if (Array.isArray(parsed)) ids = parsed.filter((v): v is string => typeof v === "string");
        } catch {
          ids = [];
        }
        let skipped = 0;
        // Filter op toegestaan type + bouw de gecascadeerde posities.
        const allowed: {
          id: string;
          concept: NonNullable<ReturnType<typeof ws.store.getConcept>>;
        }[] = [];
        for (const id of ids) {
          const concept = ws.store.getConcept(id);
          if (!concept) continue;
          if (diagram && !isElementTypeAllowedOnViewKind(concept.typeId, diagram.viewKind)) {
            skipped += 1;
            continue;
          }
          allowed.push({ id, concept });
        }
        // Cascade so dropped boxes don't perfectly overlap. Eén gecombineerde
        // undo-entry voor de hele bulk-drop (i.p.v. één per object).
        ws.placeExistingConceptsOnDiagram(
          workspaceId,
          branchId,
          allowed.map((a, i) => ({
            conceptId: a.id,
            position: { x: position.x + i * 30, y: position.y + i * 30 },
          })),
        );
        if (diagram) {
          for (const a of allowed) {
            void maybeApplyViewKindStereotype(workspaceId, branchId, a.concept, diagram.viewKind);
          }
        }
        if (skipped > 0 && diagram) {
          useWorkspaceData.setState({
            lastError: t("canvas.dropSkipped", { n: skipped, viewKind: diagram.viewKind }),
          });
        }
        return;
      }
      const existingId = event.dataTransfer.getData("application/ae-existing-concept-id");
      if (existingId) {
        const concept = ws.store.getConcept(existingId);
        if (
          diagram &&
          concept &&
          !isElementTypeAllowedOnViewKind(concept.typeId, diagram.viewKind)
        ) {
          useWorkspaceData.setState({
            lastError: t("canvas.typeNotAllowed", {
              type: concept.typeId,
              viewKind: diagram.viewKind,
            }),
          });
          return;
        }
        const placedId = ws.placeExistingConceptOnDiagram(
          workspaceId,
          branchId,
          existingId,
          position,
        );
        if (placedId) {
          handlePositionChangeForNesting(placedId, position, null, workspaceId, branchId);
        }
        if (diagram && concept) {
          void maybeApplyViewKindStereotype(workspaceId, branchId, concept, diagram.viewKind);
        }
        return;
      }

      // Drag-uit-Relations-panel: een bestaande relatie op dit diagram
      // tevoorschijn halen (mits beide endpoints hier al staan). Het
      // tekenen zelf gebeurt automatisch zodra de viewState-marker is
      // gezet — Canvas.computedEdges pikt 'm op via de visibility-filter.
      //
      // Feature A (2026-05-26) — instance-aware drop. Wanneer de drop
      // PRECIES op een specifieke React Flow node landt: pin de relatie's
      // endpoint dat bij dat concept hoort aan die instance, en kies
      // voor het andere endpoint de dichtstbijzijnde instance van het
      // andere concept op dit diagram. Zonder drop-op-instance: oude
      // canonical-pad (eerste instance per concept).
      const existingRelationId = event.dataTransfer.getData("application/ae-existing-relation-id");
      if (existingRelationId) {
        const rel = ws.store.getRelation(existingRelationId);
        const placeOpts: { sourceInstanceId?: string; targetInstanceId?: string } = {};
        if (rel && diagram) {
          // Vind het React Flow node-element onder de drop-cursor via
          // event.target → closest('.react-flow__node'). data-id-attribute
          // levert de instance-key in viewState.
          const targetEl =
            event.target instanceof HTMLElement
              ? event.target.closest<HTMLElement>(".react-flow__node")
              : null;
          const dropInstanceId = targetEl?.getAttribute("data-id") ?? null;
          if (dropInstanceId && diagram.viewState[dropInstanceId]) {
            const dropVe = diagram.viewState[dropInstanceId];
            const dropConceptId = dropVe?.ref ?? dropInstanceId;
            // Match: is de drop-instance de source of target van de relatie?
            const isSource = dropConceptId === rel.sourceConceptId;
            const isTarget = dropConceptId === rel.targetConceptId;
            if (isSource || isTarget) {
              const otherConceptId = isSource ? rel.targetConceptId : rel.sourceConceptId;
              // Verzamel alle instances van het andere concept op dit
              // diagram met positie + bereken Euclidische afstand tot
              // de drop-positie. Pak de dichtstbijzijnde.
              let nearestId: string | null = null;
              let nearestDist = Number.POSITIVE_INFINITY;
              for (const [key, ve] of Object.entries(diagram.viewState)) {
                if (!ve.position) continue;
                if (ve.hidden === true) continue;
                if ((ve.ref ?? key) !== otherConceptId) continue;
                const cx = ve.position.x + 70; // halve default-node-breedte
                const cy = ve.position.y + 25;
                const dx = cx - (flow?.x ?? 0);
                const dy = cy - (flow?.y ?? 0);
                const d = dx * dx + dy * dy;
                if (d < nearestDist) {
                  nearestDist = d;
                  nearestId = key;
                }
              }
              if (isSource) {
                placeOpts.sourceInstanceId = dropInstanceId;
                if (nearestId) placeOpts.targetInstanceId = nearestId;
              } else {
                placeOpts.targetInstanceId = dropInstanceId;
                if (nearestId) placeOpts.sourceInstanceId = nearestId;
              }
            }
          }
        }
        const result = ws.placeExistingRelationOnDiagram(
          workspaceId,
          branchId,
          existingRelationId,
          placeOpts,
        );
        if (result === "missing-endpoint") {
          if (rel) {
            const src = ws.store.getConcept(rel.sourceConceptId);
            const tgt = ws.store.getConcept(rel.targetConceptId);
            const srcName = src ? resolveBilingual(src.name, lang) || src.id : "?";
            const tgtName = tgt ? resolveBilingual(tgt.name, lang) || tgt.id : "?";
            useWorkspaceData.setState({
              lastError: t("canvas.relationNeedsBothEnds", { source: srcName, target: tgtName }),
            });
          }
        }
        // result === "already" of "placed" → geen toast, gewoon door.
        // Bij "placed" pikt de canvas-render het automatisch op.
        return;
      }

      const typeId = event.dataTransfer.getData("application/ae-type-id");
      const droppedName = readDroppedTypeName(event.dataTransfer);
      // Virtual palette groups (e.g. C4) attach a JSON payload of
      // attribute defaults — applied at create-time so e.g. dropping a
      // "Software System (C4)" entry creates an ApplicationComponent
      // with `c4_level=system` pre-set.
      const defaultAttrsRaw = event.dataTransfer.getData("application/ae-default-attributes");
      let defaultAttrs: Record<string, unknown> | undefined;
      if (defaultAttrsRaw) {
        try {
          defaultAttrs = JSON.parse(defaultAttrsRaw) as Record<string, unknown>;
        } catch {
          // Ignore malformed payload; the create just proceeds without defaults.
        }
      }
      // Sharp + andere stereotype-aware palette-groepen geven via een
      // tweede MIME ook een lijst stereotype-ids mee. Die worden bij
      // create-time meteen op het concept gezet (zelfde server-side
      // validator-pad als wanneer je 'm later via de tag-UI zou zetten).
      const defaultStereoRaw = event.dataTransfer.getData("application/ae-default-stereotypes");
      let defaultStereotypes: string[] | undefined;
      if (defaultStereoRaw) {
        try {
          const parsed = JSON.parse(defaultStereoRaw);
          if (Array.isArray(parsed) && parsed.every((s): s is string => typeof s === "string")) {
            defaultStereotypes = parsed;
          }
        } catch {
          // Ignore malformed payload.
        }
      }
      if (!typeId) return;
      if (diagram && !isElementTypeAllowedOnViewKind(typeId, diagram.viewKind)) {
        useWorkspaceData.setState({
          lastError: t("canvas.typeNotAllowed", { type: typeId, viewKind: diagram.viewKind }),
        });
        return;
      }
      // Use the registry's bilingual label as initial name so each language gets
      // its own native term (e.g. nl: "Bedrijfsproces", en: "Business Process").
      const reg = getRegistry();
      const elementType = reg.getElementType(typeId);
      // Drawing-primitieven krijgen GEEN initial label — een vrije
      // rechthoek hoort leeg te starten zodat de gebruiker er zelf wat
      // intypt (of niets, en de vorm puur als annotatie gebruikt).
      // `drawing.DiagramDescriptionBox` krijgt bewust GEEN tekst mee: 'ie
      // toont de beschrijving van het diagram bij élke render (zie
      // `conceptDisplayLabel`). Kopiëren bij het neerzetten was het oude
      // gedrag en gaf een vakje dat leeg bleef wanneer de beschrijving er op
      // dat moment nog niet was — en dat is het normale geval.
      const isDrawingType = typeId.startsWith("drawing.");
      // The palette item's name (both languages) gets priority over the
      // type's own label so virtual palette entries with their own
      // `label_override` (e.g. "Software System") show up as the concept's
      // initial name.
      const initialName: { nl: string; en: string } = isDrawingType
        ? { nl: "", en: "" }
        : droppedName
          ? droppedName
          : elementType?.label
            ? { nl: elementType.label.nl ?? "", en: elementType.label.en ?? "" }
            : { nl: "Nieuw concept", en: "New concept" };
      void ws
        .addConceptOnCanvas(
          workspaceId,
          branchId,
          typeId,
          initialName,
          position,
          defaultAttrs,
          defaultStereotypes,
        )
        .then((created) => {
          if (!created) return;
          // Sub-stereotype-keuze bij «Container» (M8): een generieke Container
          // is nu aangemaakt; toon een dialoog met de sub-stereotypes die op
          // dit base-type passen. De keuze patcht het concept (zie modal).
          if (defaultStereotypes?.includes("c4.Container")) {
            const subs = subStereotypesForBase("c4.Container", typeId);
            if (subs.length > 0) {
              const base = getRegistry().getStereotype("c4.Container");
              setSubStereoPrompt({
                conceptId: created.id,
                baseLabel: base ? resolveBilingual(base.label, lang) : "Container",
                options: subs,
              });
            }
          }
          // View-kind auto-stereotype-regel toepassen op het nieuwe concept
          // (boven op een eventuele palette-entry-default_stereotypes), bv.
          // een ApplicationComponent gedropt op de Risk-and-Security-view
          // krijgt automatisch ook «risk.Asset».
          if (diagram) {
            void maybeApplyViewKindStereotype(workspaceId, branchId, created, diagram.viewKind);
          }
          // Walk the active diagram's viewState to find the smallest
          // visible node whose absolute bounds contain the drop point.
          // We use viewState directly (not localNodesRef) because the
          // drop+API roundtrip leaves a brief window where the React
          // mirror may lag. This avoids the race that was making the
          // post-drop nest prompt occasionally fail to fire.
          const wsLatest = useWorkspaceData.getState();
          const diagramId = wsLatest.activeDiagramId;
          if (!diagramId) return;
          const liveDiagram = wsLatest.store.getDiagram(diagramId);
          if (!liveDiagram) return;
          // Drop-centre = drop top-left + half default size (matches the
          // default 140×60 used elsewhere when the new node is unmeasured).
          const dropCenter = { x: position.x + 70, y: position.y + 30 };

          const absOf = (key: string): { x: number; y: number } | null => {
            const ve = liveDiagram.viewState[key];
            if (!ve?.position) return null;
            let x = ve.position.x;
            let y = ve.position.y;
            let cur = ve;
            while (cur.containerId) {
              const parent: typeof cur | undefined = liveDiagram.viewState[cur.containerId];
              if (!parent?.position) break;
              x += parent.position.x;
              y += parent.position.y;
              cur = parent;
            }
            return { x, y };
          };

          // Op detailed-concept-view worden sharp.Attribute / sharp.Identifier
          // gerenderd als compact compartment-rij in hun parent-Concept,
          // niet als zelfstandige React-Flow-node. Hun viewState heeft
          // echter nog steeds een absolute positie + default-size, dus
          // zonder deze skip zou een nieuwe drop in Medewerker vaak een
          // bestaande child-rij als "smallest container" kiezen → nest
          // mislukt of nestelt onder de verkeerde rij.
          const isDetailedConceptView = liveDiagram.viewKind === "sharp.detailed-concept";
          let candidate: { id: string; area: number; abs: { x: number; y: number } } | null = null;
          for (const [key, ve] of Object.entries(liveDiagram.viewState)) {
            if (key === created.id) continue;
            if (!ve.position) continue;
            if (ve.hidden === true) continue;
            // Only concept-instance entries have a non-conceptRef key;
            // relation entries don't have a position so they're already
            // skipped by the check above.
            // Defense-in-depth: skip orphan view_state-entries waarvan
            // het onderliggende concept hard-verwijderd is. Anders zou
            // auto-nest een phantom-parent kunnen kiezen → React Flow
            // krijgt een onbestaande parentId en de child springt naar
            // canvas-midden. Hard-delete-pad zou deze al moeten
            // opruimen; dit is de tweede lijn.
            const candidateConceptId = ve.ref ?? key;
            const candidateConcept = wsLatest.store.getConcept(candidateConceptId);
            if (!candidateConcept) continue;
            if (
              isDetailedConceptView &&
              (candidateConcept.typeId === "sharp.Attribute" ||
                candidateConcept.typeId === "sharp.Identifier")
            ) {
              continue;
            }
            const abs = absOf(key);
            if (!abs) continue;
            const size = readNodeSize(key) ?? { width: 140, height: 60 };
            if (
              dropCenter.x >= abs.x &&
              dropCenter.x <= abs.x + size.width &&
              dropCenter.y >= abs.y &&
              dropCenter.y <= abs.y + size.height
            ) {
              const area = size.width * size.height;
              if (!candidate || area < candidate.area) {
                candidate = { id: key, area, abs };
              }
            }
          }

          if (!candidate) return; // dropped on empty pane → no nest

          // Relative position so the child sits where the user dropped it
          // (anchored to the parent's top-left).
          const relative = {
            x: position.x - candidate.abs.x,
            y: position.y - candidate.abs.y,
          };
          const result = wsLatest.nestOnDiagram(
            workspaceId,
            branchId,
            created.id,
            candidate.id,
            relative,
          );
          // A fresh palette concept can't have any pre-existing relation,
          // so hiddenCount is expected to be 0 here. Guard anyway: if for
          // some reason the matrix has already produced a hidden line
          // (e.g. via undo replay), don't double-prompt.
          if (result.hiddenCount > 0) return;

          const reg = getRegistry();
          const parentVe = liveDiagram.viewState[candidate.id];
          const parentConceptId = parentVe?.ref ?? candidate.id;
          const parentConcept = wsLatest.store.getConcept(parentConceptId);
          const childConcept = wsLatest.store.getConcept(created.id);
          if (!childConcept || !parentConcept) return;
          // Drawing-primitieven krijgen geen relatie-prompt bij nesten;
          // ze zijn puur visueel — een rechthoek rond een ApplicationComponent
          // betekent niet dat er een ArchiMate-relatie tussen moet komen.
          if (
            childConcept.typeId.startsWith("drawing.") ||
            parentConcept.typeId.startsWith("drawing.")
          ) {
            return;
          }
          // Sharp Attribute / Identifier zijn structurele onderdelen
          // van een Concept (containment via containerId is genoeg).
          // Een extra ArchiMate-relatie ertussen is semantisch onnodig
          // en zou alleen visuele ruis op het detailed-concept-diagram
          // geven — dus geen relation-prompt.
          if (
            childConcept.typeId === "sharp.Attribute" ||
            childConcept.typeId === "sharp.Identifier"
          ) {
            return;
          }
          // C4-views: visuele containment, geen relation-prompt
          // (Component-in-Container, Container-in-Boundary, etc.).
          if (liveDiagram.viewKind?.startsWith("c4.")) {
            return;
          }
          // Sharp's procesniveaus — zelfde regel als bij het slepen.
          if (
            sharpNiveauNesting(
              workspaceId,
              branchId,
              childConcept,
              parentConcept,
              created.id,
              parentConceptId,
              t,
            )
          ) {
            return;
          }
          let validTypes = validRelationTypesBetween(childConcept.typeId, parentConcept.typeId);
          const vk = reg.getViewKind(liveDiagram.viewKind);
          const allow = vk?.included_relation_types;
          if (allow && allow.length > 0) {
            // Tekenrelaties staan boven de view-kind-restrictie, net als de
            // tekenelementen zelf: ook op een procesmap moet je een vrije lijn
            // kunnen trekken.
            const allowSet = new Set([...allow, ...alwaysAllowedRelationTypeIds()]);
            validTypes = validTypes.filter((id) => allowSet.has(id));
          }
          void useUi
            .getState()
            .promptNestRelationType(created.id, parentConceptId, validTypes)
            .then(async ({ chosen }) => {
              if (!chosen) return;
              const relation = await useWorkspaceData
                .getState()
                .connectConcepts(workspaceId, branchId, created.id, parentConceptId, chosen);
              if (relation) {
                useWorkspaceData
                  .getState()
                  .hideRelationOnDiagram(workspaceId, branchId, relation.id);
              }
            });
        });
    },
    [workspaceId, branchId, handlePositionChangeForNesting, t],
  );

  // Canvas-mode (BMC Pakket B): wanneer de actieve view-kind een
  // `layout_mode = "canvas"` heeft (bv. bmc.canvas met
  // bmc.canvas.v1-template), schakelen we de hele React-Flow-render
  // uit ten gunste van een vaste-grid-renderer. De wrapper-onDrop, de
  // RF-edges en pan/zoom zijn voor canvas-mode allemaal niet relevant —
  // CanvasGridLayout regelt drop-validatie per slot zelf en heeft geen
  // edges. Free-form viewpoints (bmc.realization, etc.) blijven gewoon
  // op het normale React-Flow-pad. Hooks hierboven blijven onverkort
  // draaien zodat de rules-of-hooks-volgorde stabiel is.
  const isCanvasMode = useMemo(() => {
    void revision;
    if (!activeDiagramId) return false;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return false;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "canvas";
  }, [revision, activeDiagramId]);

  // Heat-map-mode (analytics-pack): zelfde pattern als isCanvasMode —
  // view-kind heeft `layout_mode = "heat-map"`, dispatch naar
  // `HeatMapCanvas` i.p.v. React Flow.
  const heatMapDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "heat-map" ? diagram : null;
  }, [revision, activeDiagramId]);

  // Gap-diagram (roadmap-tijdreizen.md stap 3d): berekend element-niveau-
  // diff-render-pad. Zelfde dispatch-patroon als heat-map.
  const gapDiffDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "gap-diff" ? diagram : null;
  }, [revision, activeDiagramId]);

  const plateauDiffDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "plateau-diff" ? diagram : null;
  }, [revision, activeDiagramId]);

  // Roadmap-diagram (roadmap-tijdreizen.md stap 3c): lanes-per-plateau +
  // tijd-slider. Zelfde dispatch-patroon als de andere berekende paden.
  const roadmapDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "roadmap" ? diagram : null;
  }, [revision, activeDiagramId]);

  // Roadmap-matrix (roadmap-tijdreizen.md §6.4): evolutie-matrix per plateau.
  // Zelfde dispatch-patroon als de andere berekende paden.
  const roadmapMatrixDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "roadmap-matrix" ? diagram : null;
  }, [revision, activeDiagramId]);

  // Glossary-view (Sharp Attribuuttypes) — eigen tabel-render-pad.
  const glossaryDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "glossary" ? diagram : null;
  }, [revision, activeDiagramId]);

  // Banen-diagram (Sharp Workflow Modeling) — eigen render-pad; de plaatsing
  // volgt uit de Assignment, niet uit coordinaten.
  const swimlaneDiagram = useMemo(() => {
    void revision;
    if (!activeDiagramId) return null;
    const store = useWorkspaceData.getState().store;
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) return null;
    const vk = getRegistry().getViewKind(diagram.viewKind);
    return vk?.layout_mode === "swimlane" ? diagram : null;
  }, [revision, activeDiagramId]);

  // M6.5-W3b-fix (2026-05-14): de twee useEffect's hieronder moeten
  // VOOR de isCanvasMode-early-return staan zodat de hook-volgorde
  // stabiel blijft tussen renders waar Canvas vroeg returns
  // (canvas-mode views = bmc.canvas) en niet (free-form views).
  // "Rendered fewer hooks than expected"-crash anders.

  // Escape op canvas: deselect ALTIJD, ongeacht scope. Een muis-klik
  // op een node kan scope naar attribute-panel hebben gewisseld; toch
  // moet Escape de selectie kunnen wissen. Modals (Vraag Claude, Help,
  // ScopePicker, Settings) handelen hun eigen Escape in capture-fase
  // en sluiten zichzelf eerst — hun rendering verdwijnt vóór deze
  // handler verder gaat met deselect, dus geen conflict.
  useEffect(() => {
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (inEditableField(e.target)) return;
      const selectedNodes = localNodesRef.current.filter((n) => n.selected);
      const selectedEdges = localEdgesRef.current.filter((edge) => edge.selected);
      const ui = useUi.getState();
      const hasSelection =
        selectedNodes.length > 0 ||
        selectedEdges.length > 0 ||
        ui.selectedConceptId !== null ||
        ui.selectedRelationId !== null;
      if (!hasSelection) return;
      ui.selectConcept(null);
      ui.selectRelation(null);
      setLocalNodes((curr) => curr.map((n) => (n.selected ? { ...n, selected: false } : n)));
      setLocalEdges((curr) =>
        curr.map((edge) => (edge.selected ? { ...edge, selected: false } : edge)),
      );
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  // Alt+Arrow Left/Right — wrap-resize voor geselecteerd edge-label.
  // Scope-onafhankelijk net als Escape: een muis-klik op een edge
  // (waarvan het label in een EdgeLabelRenderer-portal buiten de
  // data-scope="canvas"-wrapper rendert) kan scope op een ander paneel
  // hebben gezet. De selectie zelf (ui.selectedRelationId) is wat
  // telt, niet de scope.
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const inEditableField = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      if (target.closest(".cm-editor")) return true;
      return false;
    };
    const onAltArrow = (e: KeyboardEvent) => {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (useSession.getState().readOnly) return;
      // Mac Option+Arrow kan soms een ander e.key produceren (woord-
      // navigatie-emulatie); e.code is altijd "ArrowLeft"/"ArrowRight"
      // ongeacht modifier. Match op beide om robuust te zijn.
      const isLeft = e.key === "ArrowLeft" || e.code === "ArrowLeft";
      const isRight = e.key === "ArrowRight" || e.code === "ArrowRight";
      if (!isLeft && !isRight) return;
      if (inEditableField(e.target)) return;
      // Probeer eerst de UI-selectie, anders fallback op React Flow's
      // edge.selected-flag (mogelijk uit sync na een muis-actie).
      let relId = useUi.getState().selectedRelationId;
      if (!relId) {
        const selectedEdge = localEdgesRef.current.find((edge) => edge.selected);
        if (selectedEdge) relId = selectedEdge.id;
      }
      if (!relId) return;
      const ws2 = useWorkspaceData.getState();
      const diagramId = ws2.activeDiagramId;
      if (!diagramId) return;
      const diagram = ws2.store.getDiagram(diagramId);
      if (!diagram) return;
      const currentMax = diagram.viewState[relId]?.labelMaxWidth;
      const STEP = 20;
      e.preventDefault();
      e.stopPropagation();
      if (typeof currentMax !== "number") {
        if (isLeft) {
          ws2.setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, relId, 200);
        }
        return;
      }
      const delta = isLeft ? -STEP : STEP;
      const next = Math.max(40, currentMax + delta);
      ws2.setRelationLabelMaxWidthOnCanvas(workspaceId, branchId, relId, next);
    };
    window.addEventListener("keydown", onAltArrow, { capture: true });
    return () => window.removeEventListener("keydown", onAltArrow, { capture: true });
  }, [workspaceId, branchId]);

  // Canvas-mode (BMC) early-return — verplaatst hierheen vanaf vóór
  // de Escape/AltArrow-useEffects zodat de hooks-volgorde stabiel is
  // tussen canvas-mode en free-form renders.
  if (isCanvasMode && workspaceId && branchId) {
    return <CanvasGridLayout workspaceId={workspaceId} branchId={branchId} />;
  }

  // Heat-map-mode early-return — analytics.heat-map / analytics.relation-heat-map
  // en soortgelijke views met layout_mode = "heat-map".
  if (heatMapDiagram) {
    if (heatMapDiagram.viewKind === "analytics.relation-heat-map") {
      return <RelationHeatMapCanvas diagram={heatMapDiagram} />;
    }
    return <HeatMapCanvasV2 diagram={heatMapDiagram} />;
  }

  if (gapDiffDiagram) {
    return <GapDiffCanvas diagram={gapDiffDiagram} />;
  }

  if (plateauDiffDiagram) {
    return <PlateauDiffCanvas diagram={plateauDiffDiagram} />;
  }

  if (roadmapDiagram) {
    return <RoadmapCanvas diagram={roadmapDiagram} />;
  }

  if (roadmapMatrixDiagram) {
    return <RoadmapMatrixCanvas diagram={roadmapMatrixDiagram} />;
  }

  if (glossaryDiagram) {
    return <GlossaryCanvas />;
  }

  if (swimlaneDiagram) {
    return <SwimlaneCanvas diagram={swimlaneDiagram} />;
  }

  // Geen diagram geopend: toon een duidelijk grijs vlak (i.p.v. een leeg
  // wit-dotted canvas dat lijkt op een bewerkbaar diagram) zodat zichtbaar
  // is dat er niets actiefs is.
  if (!activeDiagramId) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-200 text-slate-400 text-sm">
        {t("canvas.noDiagramOpen")}
      </div>
    );
  }

  // Cursor verschijnt pas bij de eerste echte toetsenbord-actie (Arrow/
  // Tab/Enter) — die init zit in de keydown-handler hierboven. Geen
  // auto-init bij scope-change of localNodes-wijzigingen, anders zou
  // de cursor-cel ongevraagd verschijnen na een muis-selectie + Escape.

  // Cursor-cel-node injecteren in ReactFlow's node-array zodra we in
  // canvas-scope zitten met een actieve cursor. Verdwijnt zodra er een
  // selectie is — Gerald 2026-05-13: de selectie-glow toont al genoeg
  // visuele state, een tweede cursor erbovenop is dubbel.
  // Cel-grootte = effectiveCellSize (= CELL_SIZE / zoom) zodat 't
  // constant ~CELL_SIZE screen-px is. ZIndex 15000 (boven edges/labels/
  // collapsed containers).
  const hasSelection = selectedConceptId !== null || selectedRelationId !== null;
  const nodesWithCursor: Node[] = (() => {
    if (!canvasCursor || canvasScope !== "canvas" || hasSelection) return localNodes;
    const cursorNode: Node = {
      id: CURSOR_CELL_NODE_ID,
      type: "cursor-cell",
      position: { x: canvasCursor.x, y: canvasCursor.y },
      data: { size: effectiveCellSize },
      width: effectiveCellSize,
      height: effectiveCellSize,
      selectable: false,
      draggable: false,
      focusable: false,
      zIndex: 15000,
    };
    return [...localNodes, cursorNode];
  })();

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationeel canvas-oppervlak; interactie is pointer-gebaseerd, canvas-keyboard-nav zit op React-Flow-niveau
    <div
      ref={wrapperRef}
      className="relative h-full w-full"
      data-scope="canvas"
      // Verborgen tot React Flow gemount + gefit is (anti-flikker bij branch-
      // wissel-remount). visibility behoudt de layout-meting zodat fitView werkt.
      style={{ visibility: canvasReady ? undefined : "hidden" }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDrop={readOnly ? undefined : onDrop}
      onContextMenu={readOnly ? undefined : onCanvasContextMenu}
      onMouseDownCapture={onCanvasMouseDown}
      onMouseUp={onCanvasMouseUp}
    >
      <EdgeMarkers />
      {/*
        `selectedRelationIds` is derived from React Flow's per-edge
        `.selected` flag — every edge the user has clicked / box-
        selected ends up in the set. The toolbar uses this for bulk
        actions (line colour, label background, routing). For
        single-edge-only actions (e.g. orthogonalize, attribute
        panel) the existing `selectedRelationId` from the UI store
        keeps pointing at the most-recently-clicked edge.
      */}
      <RibbonPortal>
        <CanvasToolbar
          workspaceId={workspaceId}
          branchId={branchId}
          nodes={localNodes}
          selectedRelationId={selectedRelationId}
          // Twee lijsten, want de werkbalk doet twee soorten dingen.
          // `selectedRelationIds` = de domein-relaties, voor bewerkingen die
          // over de relatie als geheel gaan (samenvoegen, type wisselen).
          // `selectedEdgeKeys` = de VOORKOMENS die de modelleur aanwees, voor
          // alles wat in de viewState van dít voorkomen leeft: lijnkleur,
          // lijnstijl, routing en de opmaak van het label. Zonder die tweede
          // lijst raakte een opmaakwijziging élk voorkomen van de relatie —
          // op een diagram waar dezelfde reeks meerdere keren staat dus ook
          // de rijen waar de modelleur niet aan zat.
          selectedRelationIds={localEdges
            .filter((e) => e.selected)
            .map((e) => {
              const rid = (e.data as { relationId?: unknown } | undefined)?.relationId;
              return typeof rid === "string" ? rid : e.id;
            })}
          selectedEdgeKeys={localEdges.filter((e) => e.selected).map((e) => e.id)}
        />
      </RibbonPortal>

      <OverlayRulesPanel workspaceId={workspaceId} branchId={branchId} />

      <SimplifyPanel workspaceId={workspaceId} branchId={branchId} />

      {derived.hiddenTotalOnDiagram > 0 && (
        <div
          className="pointer-events-auto absolute top-2 right-2 z-40 flex items-center gap-1.5 rounded-md border border-slate-300 bg-white/90 px-2 py-1 font-medium text-slate-700 text-xs shadow-sm backdrop-blur-sm"
          title={t("canvas.hiddenTotal.tooltip")}
        >
          <span aria-hidden>👁</span>
          <span>{t("canvas.hiddenTotal.label", { count: derived.hiddenTotalOnDiagram })}</span>
        </div>
      )}
      {selectionBox && wrapperRectRef.current && (
        <div
          className="pointer-events-none absolute z-50 border border-blue-400 bg-blue-400/10"
          style={{
            left: selectionBox.x1 - wrapperRectRef.current.left,
            top: selectionBox.y1 - wrapperRectRef.current.top,
            width: selectionBox.x2 - selectionBox.x1,
            height: selectionBox.y2 - selectionBox.y1,
          }}
        />
      )}
      <ReactFlow
        nodes={nodesWithCursor}
        edges={localEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        onInit={(instance: ReactFlowInstance) => {
          rfInstance.current = instance;
          setViewportZoom(instance.getViewport().zoom);
          // Restore de laatst-onthouden viewport voor dit diagram (na de
          // fitView-prop, die al is toegepast op dit punt) — zie
          // `readPersistedViewport` hierboven voor de reden (fitView kan op
          // een cold load op een nog niet volledig gehydrateerde node-set
          // fitten en dat wordt anders nooit gecorrigeerd).
          if (workspaceId && activeDiagramId) {
            const persisted = readPersistedViewport(workspaceId, activeDiagramId);
            if (persisted) {
              instance.setViewport(persisted, { duration: 0 });
              setViewportZoom(persisted.zoom);
            }
          }
          // Onthul het canvas pas nadat de fitView-prop is toegepast (na 2
          // frames), zodat een (re)mount bij branch-wissel niet flikkert.
          requestAnimationFrame(() => requestAnimationFrame(() => setCanvasReady(true)));
        }}
        onMove={(_event: MouseEvent | TouchEvent | null, viewport: Viewport) => {
          // Houd zoom-state in sync zodat de cursor-cel z'n effectieve
          // grootte mee schaalt (constant ~80 screen-px ongeacht zoom).
          // Gecoalesceerd naar ~1×/frame (zie scheduleViewportZoom) tegen de
          // React-19 edge-desync bij snel scroll-wiel-zoomen. #46.
          scheduleViewportZoom(viewport.zoom);
          scheduleViewportPersist(viewport);
        }}
        onNodesChange={onNodesChange}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        // Alleen kijken: verbinden, opnieuw verbinden en contextmenu's vallen weg.
        {...(readOnly ? {} : { onConnect, onReconnect, onEdgeContextMenu, onNodeContextMenu })}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        edgesReconnectable={!readOnly}
        onEdgesChange={onEdgesChange}
        onSelectionChange={onSelectionChange}
        // GEEN `onEdgeClick`-prop — `RoutedEdge.onLineMouseDown` is de
        // enige eigenaar van edge-click-handling (zie comment in body).
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        // Dubbelklik op een node of edge opent het AttributePanel —
        // single-click selecteert alleen (zonder paneel-open) zodat 't
        // niet permanent in de weg zit boven de canvas.
        onNodeDoubleClick={() => useUi.getState().setAttributePanelOpen(true)}
        onEdgeDoubleClick={() => useUi.getState().setAttributePanelOpen(true)}
        deleteKeyCode={null}
        // Multi-selection modifiers — accept Cmd, Ctrl, AND Alt/Option
        // for NODE multi-select. Edge multi-select runs through our
        // own onEdgeClick logic (RF's edge-selection-on-click was
        // unreliable with modifiers, even with this prop set).
        multiSelectionKeyCode={["Meta", "Control", "Alt"]}
        selectionKeyCode={["Shift"]}
        // RF mag GEEN eigen edge-selection-state bijhouden. Onze clicks
        // op de edge gaan via RoutedEdge's `onLineMouseDown` (de wide
        // invisible overlay vangt mousedown vóór RF's handler kan firen),
        // die useUi.selectedRelationIds direct muteert. Als we RF óók
        // edge-focus laten tracken, raakt RF's interne state los van
        // useUi en firet 'ie op mouseup een correctie-`onSelectionChange
        // {edges:0}` die onze selectie weer wegveegt → "flash"-bug.
        edgesFocusable={false}
        // Selectie mag niet de z-stack veranderen — RF default
        // (`elevateNodesOnSelect: true`) tilt geselecteerde nodes met
        // +1000 op zodat ze altijd zichtbaar zijn. Dat overrulet onze
        // user-zOrder-toolbar onvoorspelbaar (een node met zOrder=-1 die
        // ook geselecteerd is komt op zIndex 999, nog altijd boven
        // unselected zIndex 0). Selectie-feedback komt al via de blauwe
        // handles + outline; geen z-elevation nodig.
        elevateNodesOnSelect={false}
        // Left-mouse drag is handled by our custom rubber-band (onWrapperMouseDown).
        // Panning uses middle / right mouse button only.
        panOnDrag={[1, 2]}
        // Links slepen op een lege plek trekt een selectiekader. Zonder dit
        // deed het niets en moest je Shift erbij weten te houden.
        selectionOnDrag
        fitView
        // Allow zooming out much further than the default 50% so a
        // sprawling diagram can be brought fully in view; max 2× still
        // matches the default. The Controls' fit-view button (icoon
        // links onderaan) past de viewport aan op alle elementen.
        minZoom={0.05}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <EdgeGeometryRefresher nodeIdsKey={nodeIdsKey} />
      </ReactFlow>

      {routingMenu && (
        <RelationContextMenu
          stand={routingMenu}
          workspaceId={workspaceId}
          branchId={branchId}
          onClose={() => setRoutingMenu(null)}
          onSamenvoegen={mergeSelectedRelations}
        />
      )}

      {nodeContextMenu && (
        // Hetzelfde menu als op het banen-canvas — zie `ConceptContextMenu`.
        // De twee regels die alleen hier gelden gaan als `extra` mee.
        <ConceptContextMenu
          stand={{
            x: nodeContextMenu.x,
            y: nodeContextMenu.y,
            conceptId:
              derived.instanceToConcept.get(nodeContextMenu.nodeId) ?? nodeContextMenu.nodeId,
          }}
          workspaceId={workspaceId}
          branchId={branchId}
          onClose={() => setNodeContextMenu(null)}
          extra={
            <>
              {(() => {
                const cid =
                  derived.instanceToConcept.get(nodeContextMenu.nodeId) ?? nodeContextMenu.nodeId;
                const c = useWorkspaceData.getState().store.getConcept(cid);
                if (!c?.stereotypes?.includes("sharp.Concept")) return null;
                return (
                  <button
                    type="button"
                    className="block w-full px-3 py-1 text-left text-slate-700 hover:bg-slate-100"
                    onClick={() => {
                      setNodeContextMenu(null);
                      setChangeTypeConceptId(cid);
                    }}
                  >
                    {t("canvas.contextMenu.changeArchimateType")}
                  </button>
                );
              })()}
              {new Set(selectedConceptIds).size >= 2 && (
                <button
                  type="button"
                  className="block w-full px-3 py-1 text-left text-slate-700 hover:bg-slate-100"
                  onClick={mergeSelectedConcepts}
                >
                  {t("canvas.contextMenu.merge")} ({new Set(selectedConceptIds).size})
                </button>
              )}
            </>
          }
        />
      )}

      {canvasMenu && (
        <CanvasContextMenu
          stand={canvasMenu}
          onClose={() => setCanvasMenu(null)}
          onAllesSelecteren={() =>
            rfInstance.current?.setNodes((nds) => nds.map((n) => ({ ...n, selected: true })))
          }
          onInzoomen={() => rfInstance.current?.zoomIn({ duration: 200 })}
          onUitzoomen={() => rfInstance.current?.zoomOut({ duration: 200 })}
          onTerug={() => rfInstance.current?.zoomTo(1, { duration: 200 })}
          onPassend={() => rfInstance.current?.fitView({ duration: 200 })}
        />
      )}

      {changeTypeConceptId && workspaceId && branchId && (
        <ChangeArchimateTypeDialog
          workspaceId={workspaceId}
          branchId={branchId}
          conceptId={changeTypeConceptId}
          onClose={() => setChangeTypeConceptId(null)}
        />
      )}

      {subStereoPrompt && workspaceId && branchId && (
        <SubStereotypeChoiceModal
          baseLabel={subStereoPrompt.baseLabel}
          options={subStereoPrompt.options}
          lang={lang}
          onCancel={() => setSubStereoPrompt(null)}
          onChoose={(subId) => {
            // Generiek (null) → alleen c4.Container. Gekozen sub → erbij, naast
            // c4.Container: dezelfde vorm als het voorbeeld en de MCP
            // (`c4.Container` + `c4.Database`); de renderer laat de sub winnen.
            // De andere stereotypes blijven staan, ook een die de view-kind
            // net automatisch toevoegde.
            if (subId) {
              const current =
                useWorkspaceData.getState().store.getConcept(subStereoPrompt.conceptId)
                  ?.stereotypes ?? [];
              const next = current.includes("c4.Container")
                ? current
                : ["c4.Container", ...current];
              void useWorkspaceData
                .getState()
                .patchConcept(workspaceId, branchId, subStereoPrompt.conceptId, {
                  stereotypes: next.includes(subId) ? next : [...next, subId],
                });
            }
            setSubStereoPrompt(null);
          }}
        />
      )}

      {lastError && (
        <div className="absolute top-4 right-4 max-w-md rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900 text-sm shadow">
          <div className="flex items-start gap-2">
            <span>{lastError}</span>
            <button
              type="button"
              className="text-rose-700 hover:underline"
              onClick={() => useWorkspaceData.getState().clearError()}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Nesting helpers ------------------------------------------------------

/**
 * Walk every node and resolve its absolute position. React Flow stores a
 * nested node's `position` as relative to its parent, so absolute = parent
 * absolute + child relative (recursively).
 */
function computeAbsolutePositions(nodes: Node[]): Map<string, { x: number; y: number }> {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  const result = new Map<string, { x: number; y: number }>();
  const resolve = (n: Node): { x: number; y: number } => {
    const cached = result.get(n.id);
    if (cached) return cached;
    if (!n.parentId) {
      result.set(n.id, n.position);
      return n.position;
    }
    const parent = byId.get(n.parentId);
    if (!parent) {
      result.set(n.id, n.position);
      return n.position;
    }
    const pa = resolve(parent);
    const abs = { x: pa.x + n.position.x, y: pa.y + n.position.y };
    result.set(n.id, abs);
    return abs;
  };
  for (const n of nodes) resolve(n);
  return result;
}

/** True when `candidateAncestor` is in `nodeId`'s parent chain. */
function isDescendantOf(candidateAncestor: string, nodeId: string, nodes: Node[]): boolean {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  let cursor = byId.get(candidateAncestor);
  while (cursor?.parentId) {
    if (cursor.parentId === nodeId) return true;
    cursor = byId.get(cursor.parentId);
  }
  return false;
}

/** Read the rendered DOM size of a React Flow node, or null if not measured yet. */
function readNodeSize(nodeId: string): { width: number; height: number } | null {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(
    `.react-flow__node[data-id="${cssEscape(nodeId)}"]`,
  ) as HTMLElement | null;
  if (!el) return null;
  return { width: el.offsetWidth, height: el.offsetHeight };
}

function cssEscape(s: string): string {
  // Minimal escape for the attribute selector — node ids are UUIDs so we
  // don't need full CSS.escape; this just avoids breaking on hyphens.
  return s.replace(/(["'\\])/g, "\\$1");
}
